import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, Input, Output, ViewEncapsulation, ViewChild, Pipe, HostListener, Directive, Injectable, NgModule } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1$1 from '@angular/material/slider';
import { MatSliderModule } from '@angular/material/slider';
import * as i2 from '@angular/router';
import { RouterModule } from '@angular/router';
import { Subject, BehaviorSubject, Subscription, Observable, of, EMPTY, fromEvent } from 'rxjs';
import { trigger, transition, style, animate, state } from '@angular/animations';
import * as i1$2 from '@angular/platform-browser';
import * as i2$1 from '@angular/common/http';
import { HttpEventType } from '@angular/common/http';
import { mergeMap, map, tap, concatMap, filter, catchError, takeUntil, delay } from 'rxjs/operators';
import { v4 } from 'uuid';

class ContentCardButtonComponent {
    constructor() {
        this.isActive = false;
        this.elementId = '';
        this.buttonPadding = 'py-6';
        this.onItemSelectedEvent = new EventEmitter();
    }
    onItemSelected() {
        this.onItemSelectedEvent.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ContentCardButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: ContentCardButtonComponent, isStandalone: false, selector: "content-card-button", inputs: { isActive: "isActive", elementId: "elementId", buttonPadding: "buttonPadding" }, outputs: { onItemSelectedEvent: "onItemSelectedEvent" }, ngImport: i0, template: "<button\n  (click)=\"onItemSelected()\"\n  [ngClass]=\"isActive ? 'border-secondary-500 shadow-input' : 'border-neutral-200'\"\n  class=\"w-full {{\n    buttonPadding\n  }} bg-light border rounded-md hover:shadow-input transition-all\"\n  type=\"button\"\n  [id]=\"elementId\">\n  <ng-content> </ng-content>\n</button>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ContentCardButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'content-card-button', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<button\n  (click)=\"onItemSelected()\"\n  [ngClass]=\"isActive ? 'border-secondary-500 shadow-input' : 'border-neutral-200'\"\n  class=\"w-full {{\n    buttonPadding\n  }} bg-light border rounded-md hover:shadow-input transition-all\"\n  type=\"button\"\n  [id]=\"elementId\">\n  <ng-content> </ng-content>\n</button>\n" }]
        }], propDecorators: { isActive: [{
                type: Input
            }], elementId: [{
                type: Input
            }], buttonPadding: [{
                type: Input
            }], onItemSelectedEvent: [{
                type: Output
            }] } });

class CustomTooltipComponent {
    set shouldShow(shouldShow) {
        if (shouldShow) {
            this.isVisible = true;
        }
        else if (this.delayClose) {
            setTimeout(() => {
                this.isVisible = false;
                this.changeDetector.detectChanges();
            }, 300);
        }
        else {
            this.isVisible = false;
        }
        this.changeDetector.detectChanges();
    }
    constructor(changeDetector) {
        this.changeDetector = changeDetector;
        this.x = 0;
        this.y = 0;
        this.delayClose = true;
        this.isVisible = false;
        this.isMouseOver = false;
    }
    handleMouseEnter() {
        if (this.isVisible) {
            this.isMouseOver = true;
        }
    }
    handleMouseLeave() {
        this.isMouseOver = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: CustomTooltipComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: CustomTooltipComponent, isStandalone: false, selector: "custom-tooltip", inputs: { x: "x", y: "y", delayClose: "delayClose", shouldShow: "shouldShow" }, ngImport: i0, template: "<div\r\n  *ngIf=\"isVisible || isMouseOver\"\r\n  class=\"fixed z-10\"\r\n  [style.left.px]=\"x\"\r\n  [style.top.px]=\"y\"\r\n  (mouseenter)=\"handleMouseEnter()\"\r\n  (mouseleave)=\"handleMouseLeave()\">\r\n  <ng-content></ng-content>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: CustomTooltipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'custom-tooltip', standalone: false, template: "<div\r\n  *ngIf=\"isVisible || isMouseOver\"\r\n  class=\"fixed z-10\"\r\n  [style.left.px]=\"x\"\r\n  [style.top.px]=\"y\"\r\n  (mouseenter)=\"handleMouseEnter()\"\r\n  (mouseleave)=\"handleMouseLeave()\">\r\n  <ng-content></ng-content>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { x: [{
                type: Input
            }], y: [{
                type: Input
            }], delayClose: [{
                type: Input
            }], shouldShow: [{
                type: Input
            }] } });

class GoogleAutocompleteComponent {
    constructor() {
        this.value = '';
        this.placeholder = 'Enter a location';
        this.inputClasses = '';
        this.defaultCountryCode = 'gb';
        this.inputId = 'google-autocomplete-input';
        this.onAddressChangeEvent = new EventEmitter();
        this.onInputBlurEvent = new EventEmitter();
        this.onDestroy$ = new Subject();
    }
    ngOnDestroy() {
        this.onDestroy$.next();
    }
    ngAfterViewInit() {
        if (google) {
            this.setAutocomplete();
        }
    }
    ngOnChanges(changes) {
        if (this.addressText && changes.defaultCountryCode) {
            this.setAutocomplete();
        }
    }
    onInputBlur($event) {
        const value = $event.target.value;
        this.onInputBlurEvent.emit(value);
    }
    setAutocomplete() {
        const autocomplete = new google.maps.places.Autocomplete(this.addressText.nativeElement, {
            componentRestrictions: { country: this.defaultCountryCode },
            types: ['postal_town', 'locality', 'sublocality', 'postal_code'],
        });
        google.maps.event.addListener(autocomplete, 'place_changed', () => {
            const place = autocomplete.getPlace();
            const result = {
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng(),
                searchedText: this.addressText.nativeElement.value,
            };
            this.onAddressChangeEvent.emit(result);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: GoogleAutocompleteComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: GoogleAutocompleteComponent, isStandalone: false, selector: "google-autocomplete", inputs: { value: "value", placeholder: "placeholder", inputClasses: "inputClasses", defaultCountryCode: "defaultCountryCode", inputId: "inputId" }, outputs: { onAddressChangeEvent: "onAddressChangeEvent", onInputBlurEvent: "onInputBlurEvent" }, viewQueries: [{ propertyName: "addressText", first: true, predicate: ["addressText"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<input\n  [value]=\"value\"\n  [ngClass]=\"inputClasses ? inputClasses : 'w-full'\"\n  [placeholder]=\"placeholder\"\n  (blur)=\"onInputBlur($event)\"\n  type=\"search\"\n  [id]=\"inputId\"\n  #addressText />\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: GoogleAutocompleteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'google-autocomplete', encapsulation: ViewEncapsulation.None, standalone: false, template: "<input\n  [value]=\"value\"\n  [ngClass]=\"inputClasses ? inputClasses : 'w-full'\"\n  [placeholder]=\"placeholder\"\n  (blur)=\"onInputBlur($event)\"\n  type=\"search\"\n  [id]=\"inputId\"\n  #addressText />\n" }]
        }], propDecorators: { value: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], inputClasses: [{
                type: Input
            }], defaultCountryCode: [{
                type: Input
            }], inputId: [{
                type: Input
            }], onAddressChangeEvent: [{
                type: Output
            }], onInputBlurEvent: [{
                type: Output
            }], addressText: [{
                type: ViewChild,
                args: ['addressText']
            }] } });

const slideDown = trigger('slideDown', [
    transition(':enter', [
        style({ transform: 'translateY(-100%)' }),
        animate('.3s ease-out', style({ transform: 'translateY(0)' })),
    ]),
    transition(':leave', [
        style({ transform: 'translateY(0)', position: 'absolute' }),
        animate('.3s ease-in', style({ transform: 'translateY(-100%)', position: 'absolute' })),
    ]),
]);
const slideFromRight = trigger('slideFromRight', [
    transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('.3s ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
    ]),
    transition(':leave', [
        style({ transform: 'translateX(0)', opacity: 1 }),
        animate('.3s ease-in', style({ transform: 'translateX(100%)', opacity: 0 })),
    ]),
]);
const slideUp = trigger('slideUp', [
    transition(':enter', [
        style({ transform: 'translateY(100%)' }),
        animate('.3s ease-out', style({ transform: 'translateY(0)' })),
    ]),
    transition(':leave', [
        style({ transform: 'translateY(0)', position: 'absolute' }),
        animate('.3s ease-in', style({ transform: 'translateY(100%)', position: 'absolute' })),
    ]),
]);
const fade = trigger('fade', [
    transition(':enter', [
        style({ opacity: '0' }),
        animate('.075s ease-out', style({ opacity: '1' })),
    ]),
    transition(':leave', [
        style({ opacity: '1' }),
        animate('.075s ease-in', style({ opacity: '0' })),
    ]),
]);
const fadeOut = trigger('fadeOut', [
    transition(':leave', [
        style({ opacity: '1' }),
        animate('{{duration}} ease-out', style({ opacity: '0' })),
    ], { params: { duration: '.075s' } }),
]);
const transformToSize = trigger('transformToSize', [
    transition(':enter', [
        style({ transform: 'scale(0) translateY(-50%)' }),
        animate('.075s ease-out', style({ transform: 'scale(1) translateY(-50%)' })),
    ]),
    transition(':leave', [
        style({ transform: 'scale(1) translateY(-50%)' }),
        animate('.075s ease-in', style({ transform: 'scale(0) translateY(-50%)' })),
    ]),
]);
const openClose = trigger('openClose', [
    transition(':enter', [
        style({ transform: 'scale(1, 0)', 'transform-origin': 'top' }),
        animate('.3s ease-out', style({ transform: 'scale(1, 1)', 'transform-origin': 'top' })),
    ]),
    transition(':leave', [
        style({ transform: 'scale(1, 1)', 'transform-origin': 'top' }),
        animate('.3s ease-out', style({ transform: 'scale(1, 0)', 'transform-origin': 'top' })),
    ]),
]);
const slideX = trigger('slideX', [
    transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('.3s ease-out', style({ transform: 'translateX(0)' })),
    ]),
    transition(':leave', [
        style({ transform: 'translateX(0)' }),
        animate('.3s ease-in', style({ transform: 'translateX(100%)' })),
    ]),
]);
const slideY = trigger('slideY', [
    transition(':enter', [
        style({ transform: 'translateY(-100%)' }),
        animate('.3s ease-out', style({ transform: 'translateY(0)' })),
    ]),
    transition(':leave', [
        style({ transform: 'translateY(0)' }),
        animate('.3s ease-in', style({ transform: 'translateY(100%)' })),
    ]),
]);
const carouselFadeAnimation = trigger('carouselFadeAnimation', [
    transition('void => *', [
        style({ opacity: 0 }),
        animate('500ms cubic-bezier(0.785, 0.135, 0.15, 0.86)', style({ opacity: 1 })),
    ]),
    transition('* => void', [
        animate('500ms cubic-bezier(0.785, 0.135, 0.15, 0.86)', style({ opacity: 0 })),
    ]),
]);
const expandAndRetract = trigger('expandAndRetract', [
    state('true', style({
        height: '*',
    })),
    state('false', style({
        overflow: 'hidden',
        height: '0px',
    })),
    transition('true => false', animate('150ms ease-out')),
    transition('false => true', animate('150ms ease-out')),
]);
const slideLeftAndFade = trigger('slideLeftAndFade', [
    transition(':enter', [
        style({ transform: 'translateX(50%)', opacity: 0 }),
        animate('.300s ease-out', style({ transform: 'translateX(0)', opacity: 1 })),
    ]),
    transition(':leave', [
        style({ opacity: 1 }),
        animate('.075s ease-out', style({ opacity: 0 })),
    ]),
]);

var animations = /*#__PURE__*/Object.freeze({
    __proto__: null,
    carouselFadeAnimation: carouselFadeAnimation,
    expandAndRetract: expandAndRetract,
    fade: fade,
    fadeOut: fadeOut,
    openClose: openClose,
    slideDown: slideDown,
    slideFromRight: slideFromRight,
    slideLeftAndFade: slideLeftAndFade,
    slideUp: slideUp,
    slideX: slideX,
    slideY: slideY,
    transformToSize: transformToSize
});

// Use this, not the one in library-core
var Themes;
(function (Themes) {
    Themes["warning"] = "warning";
    Themes["error"] = "error";
    Themes["success"] = "success";
    Themes["warningAlt"] = "warning-alt";
    Themes["errorAlt"] = "error-alt";
    Themes["successAlt"] = "success-alt";
    Themes["info"] = "info";
    Themes["infoBlue"] = "info-blue";
    Themes["infoAlt"] = "info-alt";
})(Themes || (Themes = {}));

class ModalApplicantsElementsComponent {
    constructor() {
        this.isOpen = false;
        this.theme = Themes.info;
        this.iconPath = '';
        this.closeButtonPath = '';
        this.modalTitle = '';
        this.cancelText = '';
        this.callToActionText = '';
        this.shouldDisableCallToAction = false;
        this.callToActionIconCss = '';
        this.loading = false;
        this.cancelEvent = new EventEmitter();
        this.callToActionEvent = new EventEmitter();
        this.closeEvent = new EventEmitter();
    }
    cancel() {
        this.cancelEvent.emit();
    }
    close() {
        this.closeEvent.emit();
    }
    callToAction() {
        this.callToActionEvent.emit();
    }
    getIconColours() {
        switch (this.theme) {
            case Themes.error:
                return 'text-error-400 bg-error-100';
            case Themes.info:
                return 'text-info-200 bg-info-100';
            case Themes.warning:
                return 'text-warning-500 bg-warning-100';
            case Themes.success:
                return 'text-success-500 bg-success-100';
            default:
                throw Error(`Theme ${this.theme} has not been implemented in modal.component for colours`);
        }
    }
    getButtonColours() {
        switch (this.theme) {
            case Themes.error:
                return 'error';
            case Themes.info:
                return 'primary';
            case Themes.warning:
                return 'primary';
            case Themes.success:
                return 'primary';
            default:
                throw Error(`Theme ${this.theme} has not been implemented in modal.component for buttons`);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ModalApplicantsElementsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: ModalApplicantsElementsComponent, isStandalone: false, selector: "modal-applicants-elements", inputs: { isOpen: "isOpen", theme: "theme", iconPath: "iconPath", closeButtonPath: "closeButtonPath", modalTitle: "modalTitle", cancelText: "cancelText", callToActionText: "callToActionText", shouldDisableCallToAction: "shouldDisableCallToAction", callToActionIconCss: "callToActionIconCss", loading: "loading" }, outputs: { cancelEvent: "cancelEvent", callToActionEvent: "callToActionEvent", closeEvent: "closeEvent" }, ngImport: i0, template: "<div class=\"flex items-center justify-center\">\r\n  <div\r\n    *ngIf=\"isOpen\"\r\n    [@fade]\r\n    class=\"fixed z-MODAL top-0 left-0 p-8 w-full h-full bg-overlay\"></div>\r\n  <div\r\n    *ngIf=\"isOpen\"\r\n    class=\"fixed z-MODAL absolute-center-y tablet:pr-APPLICANTSELEMENTSMODAL\"\r\n    [@transformToSize]>\r\n    <div\r\n      aria-labelledby=\"modal-title\"\r\n      aria-modal=\"true\"\r\n      role=\"dialog\"\r\n      class=\"w-375 tablet:w-556\">\r\n      <div class=\"rounded shadow bg-light\">\r\n        <div\r\n          [ngClass]=\"\r\n            iconPath.length > 0 ? 'px-7 pt-7 tablet:p-3' : 'pr-7 tablet:pt-6 tablet:pr-6'\r\n          \">\r\n          <div class=\"flex tablet:items-start\">\r\n            <div\r\n              *ngIf=\"iconPath.length > 0\"\r\n              class=\"hidden tablet:block p-4 rounded-full mt-7 ml-7\"\r\n              [ngClass]=\"getIconColours()\">\r\n              <img class=\"w-34\" [src]=\"iconPath\" />\r\n            </div>\r\n\r\n            <div class=\"w-full text-left tablet:mt-0 tablet:ml-7 tablet:text-left\">\r\n              <div class=\"pt-6 mt-1\">\r\n                <h6\r\n                  *ngIf=\"modalTitle.length > 0\"\r\n                  class=\"paragraph-2 font-medium w-356\"\r\n                  id=\"modal-title text-dark\">\r\n                  {{ modalTitle }}\r\n                </h6>\r\n              </div>\r\n\r\n              <div class=\"mt-4\">\r\n                <p class=\"text-neutral-500 paragraph-1\">\r\n                  <ng-content></ng-content>\r\n                </p>\r\n              </div>\r\n            </div>\r\n\r\n            <div class=\"mt-5 mr-4 pr-1 justify-end opacity-30\">\r\n              <img\r\n                *ngIf=\"closeButtonPath.length > 0\"\r\n                (click)=\"close()\"\r\n                [src]=\"closeButtonPath\"\r\n                class=\"transform rotate-45 w-34 cursor-pointer\" />\r\n            </div>\r\n          </div>\r\n        </div>\r\n        <div class=\"mt-4 px-7 pb-7 flex justify-end\">\r\n          <button (click)=\"cancel()\" type=\"button\" class=\"cancel p-6 h-44\">\r\n            {{ cancelText }}\r\n          </button>\r\n          <button\r\n            *ngIf=\"callToActionText\"\r\n            (click)=\"callToAction()\"\r\n            [ngClass]=\"getButtonColours()\"\r\n            [disabled]=\"shouldDisableCallToAction\"\r\n            type=\"button\"\r\n            class=\"px-5 py-4 ml-3\">\r\n            <i *ngIf=\"callToActionIconCss.length > 0\" [ngClass]=\"callToActionIconCss\"></i>\r\n            <span [ngClass]=\"callToActionIconCss.length > 0 ? 'ml-1' : ''\">\r\n              {{ callToActionText }}\r\n            </span>\r\n            <div *ngIf=\"loading\" class=\"spinner size-5 ml-1\"></div>\r\n          </button>\r\n        </div>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [transformToSize, fade], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ModalApplicantsElementsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'modal-applicants-elements', animations: [transformToSize, fade], changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<div class=\"flex items-center justify-center\">\r\n  <div\r\n    *ngIf=\"isOpen\"\r\n    [@fade]\r\n    class=\"fixed z-MODAL top-0 left-0 p-8 w-full h-full bg-overlay\"></div>\r\n  <div\r\n    *ngIf=\"isOpen\"\r\n    class=\"fixed z-MODAL absolute-center-y tablet:pr-APPLICANTSELEMENTSMODAL\"\r\n    [@transformToSize]>\r\n    <div\r\n      aria-labelledby=\"modal-title\"\r\n      aria-modal=\"true\"\r\n      role=\"dialog\"\r\n      class=\"w-375 tablet:w-556\">\r\n      <div class=\"rounded shadow bg-light\">\r\n        <div\r\n          [ngClass]=\"\r\n            iconPath.length > 0 ? 'px-7 pt-7 tablet:p-3' : 'pr-7 tablet:pt-6 tablet:pr-6'\r\n          \">\r\n          <div class=\"flex tablet:items-start\">\r\n            <div\r\n              *ngIf=\"iconPath.length > 0\"\r\n              class=\"hidden tablet:block p-4 rounded-full mt-7 ml-7\"\r\n              [ngClass]=\"getIconColours()\">\r\n              <img class=\"w-34\" [src]=\"iconPath\" />\r\n            </div>\r\n\r\n            <div class=\"w-full text-left tablet:mt-0 tablet:ml-7 tablet:text-left\">\r\n              <div class=\"pt-6 mt-1\">\r\n                <h6\r\n                  *ngIf=\"modalTitle.length > 0\"\r\n                  class=\"paragraph-2 font-medium w-356\"\r\n                  id=\"modal-title text-dark\">\r\n                  {{ modalTitle }}\r\n                </h6>\r\n              </div>\r\n\r\n              <div class=\"mt-4\">\r\n                <p class=\"text-neutral-500 paragraph-1\">\r\n                  <ng-content></ng-content>\r\n                </p>\r\n              </div>\r\n            </div>\r\n\r\n            <div class=\"mt-5 mr-4 pr-1 justify-end opacity-30\">\r\n              <img\r\n                *ngIf=\"closeButtonPath.length > 0\"\r\n                (click)=\"close()\"\r\n                [src]=\"closeButtonPath\"\r\n                class=\"transform rotate-45 w-34 cursor-pointer\" />\r\n            </div>\r\n          </div>\r\n        </div>\r\n        <div class=\"mt-4 px-7 pb-7 flex justify-end\">\r\n          <button (click)=\"cancel()\" type=\"button\" class=\"cancel p-6 h-44\">\r\n            {{ cancelText }}\r\n          </button>\r\n          <button\r\n            *ngIf=\"callToActionText\"\r\n            (click)=\"callToAction()\"\r\n            [ngClass]=\"getButtonColours()\"\r\n            [disabled]=\"shouldDisableCallToAction\"\r\n            type=\"button\"\r\n            class=\"px-5 py-4 ml-3\">\r\n            <i *ngIf=\"callToActionIconCss.length > 0\" [ngClass]=\"callToActionIconCss\"></i>\r\n            <span [ngClass]=\"callToActionIconCss.length > 0 ? 'ml-1' : ''\">\r\n              {{ callToActionText }}\r\n            </span>\r\n            <div *ngIf=\"loading\" class=\"spinner size-5 ml-1\"></div>\r\n          </button>\r\n        </div>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n" }]
        }], propDecorators: { isOpen: [{
                type: Input
            }], theme: [{
                type: Input
            }], iconPath: [{
                type: Input
            }], closeButtonPath: [{
                type: Input
            }], modalTitle: [{
                type: Input
            }], cancelText: [{
                type: Input
            }], callToActionText: [{
                type: Input
            }], shouldDisableCallToAction: [{
                type: Input
            }], callToActionIconCss: [{
                type: Input
            }], loading: [{
                type: Input
            }], cancelEvent: [{
                type: Output
            }], callToActionEvent: [{
                type: Output
            }], closeEvent: [{
                type: Output
            }] } });

class ModalLargeComponent {
    constructor() {
        this.isOpen = false;
        this.widthClasses = 'w-90p max-w-CONTENT';
        this.heightClasses = 'h-80vh';
        this.positionClasses = 'fixed absolute-center-y';
        this.isOverflowYHidden = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ModalLargeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: ModalLargeComponent, isStandalone: false, selector: "modal-large", inputs: { isOpen: "isOpen", widthClasses: "widthClasses", heightClasses: "heightClasses", positionClasses: "positionClasses", isOverflowYHidden: "isOverflowYHidden" }, ngImport: i0, template: "<div class=\"hidden tablet:flex items-center justify-center\">\n  <div\n    *ngIf=\"isOpen\"\n    [@fade]\n    class=\"fixed z-MODAL top-0 left-0 w-full h-full bg-overlay\"></div>\n  <div\n    *ngIf=\"isOpen\"\n    class=\"{{ positionClasses }} {{ widthClasses }} z-MODAL\"\n    [@transformToSize]>\n    <div aria-labelledby=\"modal-title\" role=\"dialog\" aria-modal=\"true\" class=\"w-full\">\n      <div\n        [ngClass]=\"[isOverflowYHidden ? 'overflow-y-hidden' : '']\"\n        class=\"{{ heightClasses }} rounded shadow-400 bg-light\">\n        <ng-content> </ng-content>\n      </div>\n    </div>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [transformToSize, fade], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ModalLargeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'modal-large', animations: [transformToSize, fade], changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<div class=\"hidden tablet:flex items-center justify-center\">\n  <div\n    *ngIf=\"isOpen\"\n    [@fade]\n    class=\"fixed z-MODAL top-0 left-0 w-full h-full bg-overlay\"></div>\n  <div\n    *ngIf=\"isOpen\"\n    class=\"{{ positionClasses }} {{ widthClasses }} z-MODAL\"\n    [@transformToSize]>\n    <div aria-labelledby=\"modal-title\" role=\"dialog\" aria-modal=\"true\" class=\"w-full\">\n      <div\n        [ngClass]=\"[isOverflowYHidden ? 'overflow-y-hidden' : '']\"\n        class=\"{{ heightClasses }} rounded shadow-400 bg-light\">\n        <ng-content> </ng-content>\n      </div>\n    </div>\n  </div>\n</div>\n" }]
        }], propDecorators: { isOpen: [{
                type: Input
            }], widthClasses: [{
                type: Input
            }], heightClasses: [{
                type: Input
            }], positionClasses: [{
                type: Input
            }], isOverflowYHidden: [{
                type: Input
            }] } });

var NotificationBannerTheme;
(function (NotificationBannerTheme) {
    NotificationBannerTheme[NotificationBannerTheme["INFO_LIGHT"] = 0] = "INFO_LIGHT";
    NotificationBannerTheme[NotificationBannerTheme["INFO_DARK"] = 1] = "INFO_DARK";
    NotificationBannerTheme[NotificationBannerTheme["SUCCESS_LIGHT"] = 2] = "SUCCESS_LIGHT";
    NotificationBannerTheme[NotificationBannerTheme["SUCCESS_DARK"] = 3] = "SUCCESS_DARK";
    NotificationBannerTheme[NotificationBannerTheme["WARNING_LIGHT"] = 4] = "WARNING_LIGHT";
    NotificationBannerTheme[NotificationBannerTheme["WARNING_DARK"] = 5] = "WARNING_DARK";
    NotificationBannerTheme[NotificationBannerTheme["ERROR_LIGHT"] = 6] = "ERROR_LIGHT";
    NotificationBannerTheme[NotificationBannerTheme["ERROR_DARK"] = 7] = "ERROR_DARK";
})(NotificationBannerTheme || (NotificationBannerTheme = {}));

class NotificationBannerBackgroundPipe {
    transform(theme) {
        switch (theme) {
            case NotificationBannerTheme.INFO_LIGHT:
                return 'bg-info-75';
            case NotificationBannerTheme.INFO_DARK:
                return 'bg-info-200';
            case NotificationBannerTheme.SUCCESS_LIGHT:
                return 'bg-success-50';
            case NotificationBannerTheme.SUCCESS_DARK:
                return 'bg-success-400';
            case NotificationBannerTheme.WARNING_LIGHT:
                return 'bg-warning-50';
            case NotificationBannerTheme.WARNING_DARK:
                return 'bg-warning-500';
            case NotificationBannerTheme.ERROR_LIGHT:
                return 'bg-error-50';
            case NotificationBannerTheme.ERROR_DARK:
                return 'bg-error-500';
            default:
                return 'bg-info-75';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerBackgroundPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerBackgroundPipe, isStandalone: false, name: "notificationBannerBackground" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerBackgroundPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'notificationBannerBackground',
                    pure: true,
                    standalone: false
                }]
        }] });
class NotificationBannerTextPipe {
    transform(theme) {
        switch (theme) {
            case NotificationBannerTheme.INFO_LIGHT:
                return 'text-dark';
            case NotificationBannerTheme.INFO_DARK:
                return 'text-white';
            case NotificationBannerTheme.SUCCESS_LIGHT:
                return 'text-dark';
            case NotificationBannerTheme.SUCCESS_DARK:
                return 'text-dark';
            case NotificationBannerTheme.WARNING_LIGHT:
                return 'text-dark';
            case NotificationBannerTheme.WARNING_DARK:
                return 'text-dark';
            case NotificationBannerTheme.ERROR_LIGHT:
                return 'text-dark';
            case NotificationBannerTheme.ERROR_DARK:
                return 'text-white';
            default:
                return 'text-white';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerTextPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerTextPipe, isStandalone: false, name: "notificationBannerText" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerTextPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'notificationBannerText',
                    pure: true,
                    standalone: false
                }]
        }] });
class NotificationBannerIconPipe {
    transform(theme) {
        switch (theme) {
            case NotificationBannerTheme.INFO_LIGHT:
                return 'fak fa-talos-information';
            case NotificationBannerTheme.INFO_DARK:
                return 'fak fa-talos-information';
            case NotificationBannerTheme.SUCCESS_LIGHT:
                return 'fak fa-check-border';
            case NotificationBannerTheme.SUCCESS_DARK:
                return 'fak fa-check-border';
            case NotificationBannerTheme.WARNING_LIGHT:
                return 'fak fa-alert';
            case NotificationBannerTheme.WARNING_DARK:
                return 'fak fa-alert';
            case NotificationBannerTheme.ERROR_LIGHT:
                return 'fak fa-talos-exclamation';
            case NotificationBannerTheme.ERROR_DARK:
                return 'fak fa-talos-exclamation';
            default:
                return 'fak fa-talos-information';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerIconPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerIconPipe, isStandalone: false, name: "notificationBannerIcon" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerIconPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'notificationBannerIcon',
                    pure: true,
                    standalone: false
                }]
        }] });
class NotificationBannerIconColourPipe {
    transform(theme) {
        switch (theme) {
            case NotificationBannerTheme.INFO_LIGHT:
                return 'text-info-200 hover:text-info-200';
            case NotificationBannerTheme.INFO_DARK:
                return 'text-primary-200 hover:text-primary-200';
            case NotificationBannerTheme.SUCCESS_LIGHT:
                return 'text-success-400 hover:text-success-400';
            case NotificationBannerTheme.SUCCESS_DARK:
                return 'text-success-200 hover:text-success-200';
            case NotificationBannerTheme.WARNING_LIGHT:
                return 'text-warning-500 hover:text-warning-500';
            case NotificationBannerTheme.WARNING_DARK:
                return 'text-warning-100 hover:text-warning-100';
            case NotificationBannerTheme.ERROR_LIGHT:
                return 'text-error-400 hover:text-error-400';
            case NotificationBannerTheme.ERROR_DARK:
                return 'text-error-200 hover:text-error-200';
            default:
                return 'text-info-200 hover:text-info-200';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerIconColourPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerIconColourPipe, isStandalone: false, name: "notificationBannerIconColour" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerIconColourPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'notificationBannerIconColour',
                    pure: true,
                    standalone: false
                }]
        }] });
class NotificationBannerBorderPipe {
    transform(theme) {
        switch (theme) {
            case NotificationBannerTheme.INFO_LIGHT:
                return 'border-b border-info-200';
            case NotificationBannerTheme.SUCCESS_LIGHT:
                return 'border-b border-success-400';
            case NotificationBannerTheme.WARNING_LIGHT:
                return 'border-b border-warning-500';
            case NotificationBannerTheme.ERROR_LIGHT:
                return 'border-b border-error-400';
            default:
                return '';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerBorderPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerBorderPipe, isStandalone: false, name: "notificationBannerBorder" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerBorderPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'notificationBannerBorder',
                    pure: true,
                    standalone: false
                }]
        }] });

class NotificationBannerComponent {
    constructor() {
        this.notificationBanner = { shouldShow: true, text: '' };
        this.theme = NotificationBannerTheme.INFO_LIGHT;
        this.closeEvent = new EventEmitter();
    }
    close() {
        this.closeEvent.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: NotificationBannerComponent, isStandalone: false, selector: "notification-banner", inputs: { notificationBanner: "notificationBanner", theme: "theme" }, outputs: { closeEvent: "closeEvent" }, ngImport: i0, template: "<ng-container *ngIf=\"notificationBanner.shouldShow\">\n  <div\n    [ngClass]=\"[theme | notificationBannerBackground, theme | notificationBannerBorder]\"\n    class=\"w-full py-5 fixed top-0 z-50 px-8 flex justify-between items-center shadow-btnIcon\">\n    <div [ngClass]=\"theme | notificationBannerText\" class=\"flex items-center\">\n      <i\n        [ngClass]=\"[theme | notificationBannerIcon, theme | notificationBannerIconColour]\"\n        class=\"mr-5 text-7\"></i>\n      <p [ngClass]=\"theme | notificationBannerText\" class=\"text-3\">\n        {{ notificationBanner.text }}\n      </p>\n    </div>\n    <i\n      [ngClass]=\"theme | notificationBannerIconColour\"\n      class=\"fak fa-talos-cross text-primary-300 text-1 hover:none cursor-pointer\"\n      (click)=\"close()\"></i>\n  </div>\n</ng-container>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: NotificationBannerBackgroundPipe, name: "notificationBannerBackground" }, { kind: "pipe", type: NotificationBannerTextPipe, name: "notificationBannerText" }, { kind: "pipe", type: NotificationBannerIconPipe, name: "notificationBannerIcon" }, { kind: "pipe", type: NotificationBannerIconColourPipe, name: "notificationBannerIconColour" }, { kind: "pipe", type: NotificationBannerBorderPipe, name: "notificationBannerBorder" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: NotificationBannerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'notification-banner', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<ng-container *ngIf=\"notificationBanner.shouldShow\">\n  <div\n    [ngClass]=\"[theme | notificationBannerBackground, theme | notificationBannerBorder]\"\n    class=\"w-full py-5 fixed top-0 z-50 px-8 flex justify-between items-center shadow-btnIcon\">\n    <div [ngClass]=\"theme | notificationBannerText\" class=\"flex items-center\">\n      <i\n        [ngClass]=\"[theme | notificationBannerIcon, theme | notificationBannerIconColour]\"\n        class=\"mr-5 text-7\"></i>\n      <p [ngClass]=\"theme | notificationBannerText\" class=\"text-3\">\n        {{ notificationBanner.text }}\n      </p>\n    </div>\n    <i\n      [ngClass]=\"theme | notificationBannerIconColour\"\n      class=\"fak fa-talos-cross text-primary-300 text-1 hover:none cursor-pointer\"\n      (click)=\"close()\"></i>\n  </div>\n</ng-container>\n" }]
        }], propDecorators: { notificationBanner: [{
                type: Input
            }], theme: [{
                type: Input
            }], closeEvent: [{
                type: Output
            }] } });

class IsPreviousButtonDisabledPipe {
    transform(currentPage) {
        return currentPage <= 1;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: IsPreviousButtonDisabledPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: IsPreviousButtonDisabledPipe, isStandalone: false, name: "isPreviousButtonDisabled" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: IsPreviousButtonDisabledPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'isPreviousButtonDisabled',
                    pure: true,
                    standalone: false
                }]
        }] });
class IsNextButtonDisabledPipe {
    transform(numberOfResults, currentPage, currentPageSize) {
        const numberOfItemsToDisplay = currentPage * currentPageSize;
        const currentPageEndRange = numberOfItemsToDisplay > numberOfResults ? numberOfResults : numberOfItemsToDisplay;
        return numberOfResults === currentPageEndRange;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: IsNextButtonDisabledPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: IsNextButtonDisabledPipe, isStandalone: false, name: "isNextButtonDisabled" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: IsNextButtonDisabledPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'isNextButtonDisabled',
                    pure: true,
                    standalone: false
                }]
        }] });
class PaginatePipe {
    transform(items, pageSize, page) {
        const minimumIndexToTake = pageSize * (page - 1);
        const maximumIndexToTake = pageSize * page - 1;
        return items.filter((_, index) => index >= minimumIndexToTake && index <= maximumIndexToTake);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: PaginatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: PaginatePipe, isStandalone: false, name: "paginate" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: PaginatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'paginate',
                    pure: true,
                    standalone: false
                }]
        }] });

class PaginationComponent {
    constructor() {
        this.pageSizes = [];
        this.currentPageSize = 1;
        this.currentPage = 1;
        this.numberOfResults = 0;
        // Legacy outputs
        this.pageSizeChangeEvent = new EventEmitter();
        this.previousPageEvent = new EventEmitter();
        this.nextPageEvent = new EventEmitter();
        this.firstPageEvent = new EventEmitter();
        this.lastPageEvent = new EventEmitter();
        this.onPageSizeChangedEvent = new EventEmitter();
        this.onPageNumberChangedEvent = new EventEmitter();
        this.shouldShowPageSizeDropdown = false;
    }
    getPageInformation() {
        const resultFrom = this.numberOfResults > 0 ? (this.currentPage - 1) * this.currentPageSize + 1 : 0;
        const resultsTo = this.getCurrentPageEndRange();
        return `${resultFrom}-${resultsTo} of ${this.numberOfResults}`;
    }
    shouldDisablePrevious() {
        return this.currentPage <= 1;
    }
    shouldDisableNext() {
        return this.numberOfResults === this.getCurrentPageEndRange();
    }
    pageSizeChange($event, pageSize) {
        $event.stopPropagation();
        this.pageSizeChangeEvent.emit(pageSize);
        this.onPageSizeChangedEvent.emit(pageSize);
    }
    toggleShouldShowPageSizeDropdown($event) {
        $event.stopPropagation();
        this.shouldShowPageSizeDropdown = !this.shouldShowPageSizeDropdown;
    }
    previousPage() {
        this.previousPageEvent.emit();
        this.onPageNumberChangedEvent.emit(this.currentPage - 1);
    }
    nextPage() {
        this.nextPageEvent.emit();
        this.onPageNumberChangedEvent.emit(this.currentPage + 1);
    }
    firstPage() {
        this.firstPageEvent.emit();
        this.onPageNumberChangedEvent.emit(1);
    }
    lastPage() {
        this.lastPageEvent.emit();
        const finalPageNummber = this.numberOfResults % this.currentPageSize === 0
            ? this.numberOfResults / this.currentPageSize
            : Math.floor(this.numberOfResults / this.currentPageSize + 1);
        this.onPageNumberChangedEvent.emit(finalPageNummber);
    }
    getCurrentPageEndRange() {
        const currentPageEndRange = this.currentPage * this.currentPageSize;
        return currentPageEndRange > this.numberOfResults
            ? this.numberOfResults
            : currentPageEndRange;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: PaginationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: PaginationComponent, isStandalone: false, selector: "pagination", inputs: { pageSizes: "pageSizes", currentPageSize: "currentPageSize", currentPage: "currentPage", numberOfResults: "numberOfResults" }, outputs: { pageSizeChangeEvent: "pageSizeChangeEvent", previousPageEvent: "previousPageEvent", nextPageEvent: "nextPageEvent", firstPageEvent: "firstPageEvent", lastPageEvent: "lastPageEvent", onPageSizeChangedEvent: "onPageSizeChangedEvent", onPageNumberChangedEvent: "onPageNumberChangedEvent" }, ngImport: i0, template: "<div class=\"flex mobile:flex-col laptop:flex-row laptop:justify-between\">\n  <div class=\"mb-7 flex justify-center laptop:justify-start laptop:mb-none items-center\">\n    <span class=\"paragraph-1 text-neutral-500 font-primary\"> Show rows per page </span>\n\n    <button\n      (click)=\"toggleShouldShowPageSizeDropdown($event)\"\n      class=\"relative flex items-center ml-6 py-4 px-5 border border-neutral-100 bg-light rounded\"\n      type=\"button\">\n      <span class=\"leading-17\">\n        {{ currentPageSize }}\n      </span>\n      <i class=\"far fa-chevron-down text-1 ml-7\"></i>\n\n      <div\n        *ngIf=\"shouldShowPageSizeDropdown\"\n        (click)=\"toggleShouldShowPageSizeDropdown($event)\"\n        class=\"overlay\"></div>\n\n      <div *ngIf=\"shouldShowPageSizeDropdown\" class=\"dropdown top-9 right-none\">\n        <button\n          *ngFor=\"let pageSize of pageSizes; let i = index\"\n          (click)=\"pageSizeChange($event, pageSize)\"\n          [id]=\"'pageSize' + i\"\n          class=\"dropdown-button\"\n          type=\"button\">\n          {{ pageSize }}\n        </button>\n      </div>\n    </button>\n  </div>\n\n  <div\n    class=\"flex flex-col laptop:flex-row justify-center laptop:justify-end items-center\">\n    <div>\n      <span class=\"text-neutral-500 mx-4 font-primary\">\n        {{ getPageInformation() }}\n      </span>\n    </div>\n    <div class=\"flex my-2 justify-center items-center laptop:justify-start\">\n      <button\n        (click)=\"firstPage()\"\n        [disabled]=\"currentPage | isPreviousButtonDisabled\"\n        [ngClass]=\"shouldDisablePrevious() ? 'text-neutral-300' : ''\"\n        id=\"first-page\"\n        type=\"button\"\n        class=\"py-4 px-5\"\n        aria-label=\"Open first page\">\n        <i class=\"fak fa-talos-first-page text-6\"></i>\n      </button>\n      <div class=\"py-4 px-5 text-neutral-300\">\n        <i class=\"fa-regular fa-pipe\"></i>\n      </div>\n\n      <button\n        (click)=\"previousPage()\"\n        [disabled]=\"currentPage | isPreviousButtonDisabled\"\n        [ngClass]=\"shouldDisablePrevious() ? 'text-neutral-300' : ''\"\n        id=\"previous-page\"\n        type=\"button\"\n        class=\"py-4 px-5\">\n        <i class=\"far fa-chevron-left\"></i>\n        <span class=\"pl-1 font-primary\"> Prev </span>\n      </button>\n\n      <button\n        (click)=\"nextPage()\"\n        [disabled]=\"\n          numberOfResults | isNextButtonDisabled : currentPage : currentPageSize\n        \"\n        [ngClass]=\"\n          (numberOfResults | isNextButtonDisabled : currentPage : currentPageSize)\n            ? 'text-neutral-300'\n            : ''\n        \"\n        id=\"next-page\"\n        type=\"button\"\n        class=\"py-4 px-5\">\n        <span class=\"pr-1 font-primary\"> Next </span>\n        <i class=\"far fa-chevron-right\"></i>\n      </button>\n      <div class=\"py-4 px-5 text-neutral-300\">\n        <i class=\"fa-regular fa-pipe\"></i>\n      </div>\n      <button\n        (click)=\"lastPage()\"\n        [disabled]=\"\n          numberOfResults | isNextButtonDisabled : currentPage : currentPageSize\n        \"\n        [ngClass]=\"\n          (numberOfResults | isNextButtonDisabled : currentPage : currentPageSize)\n            ? 'text-neutral-300'\n            : ''\n        \"\n        id=\"last-page\"\n        type=\"button\"\n        class=\"\"\n        class=\"py-4 px-5\"\n        aria-label=\"Open last page\">\n        <i class=\"fak fa-talos-last-page text-6\"></i>\n      </button>\n    </div>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: IsPreviousButtonDisabledPipe, name: "isPreviousButtonDisabled" }, { kind: "pipe", type: IsNextButtonDisabledPipe, name: "isNextButtonDisabled" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: PaginationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'pagination', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<div class=\"flex mobile:flex-col laptop:flex-row laptop:justify-between\">\n  <div class=\"mb-7 flex justify-center laptop:justify-start laptop:mb-none items-center\">\n    <span class=\"paragraph-1 text-neutral-500 font-primary\"> Show rows per page </span>\n\n    <button\n      (click)=\"toggleShouldShowPageSizeDropdown($event)\"\n      class=\"relative flex items-center ml-6 py-4 px-5 border border-neutral-100 bg-light rounded\"\n      type=\"button\">\n      <span class=\"leading-17\">\n        {{ currentPageSize }}\n      </span>\n      <i class=\"far fa-chevron-down text-1 ml-7\"></i>\n\n      <div\n        *ngIf=\"shouldShowPageSizeDropdown\"\n        (click)=\"toggleShouldShowPageSizeDropdown($event)\"\n        class=\"overlay\"></div>\n\n      <div *ngIf=\"shouldShowPageSizeDropdown\" class=\"dropdown top-9 right-none\">\n        <button\n          *ngFor=\"let pageSize of pageSizes; let i = index\"\n          (click)=\"pageSizeChange($event, pageSize)\"\n          [id]=\"'pageSize' + i\"\n          class=\"dropdown-button\"\n          type=\"button\">\n          {{ pageSize }}\n        </button>\n      </div>\n    </button>\n  </div>\n\n  <div\n    class=\"flex flex-col laptop:flex-row justify-center laptop:justify-end items-center\">\n    <div>\n      <span class=\"text-neutral-500 mx-4 font-primary\">\n        {{ getPageInformation() }}\n      </span>\n    </div>\n    <div class=\"flex my-2 justify-center items-center laptop:justify-start\">\n      <button\n        (click)=\"firstPage()\"\n        [disabled]=\"currentPage | isPreviousButtonDisabled\"\n        [ngClass]=\"shouldDisablePrevious() ? 'text-neutral-300' : ''\"\n        id=\"first-page\"\n        type=\"button\"\n        class=\"py-4 px-5\"\n        aria-label=\"Open first page\">\n        <i class=\"fak fa-talos-first-page text-6\"></i>\n      </button>\n      <div class=\"py-4 px-5 text-neutral-300\">\n        <i class=\"fa-regular fa-pipe\"></i>\n      </div>\n\n      <button\n        (click)=\"previousPage()\"\n        [disabled]=\"currentPage | isPreviousButtonDisabled\"\n        [ngClass]=\"shouldDisablePrevious() ? 'text-neutral-300' : ''\"\n        id=\"previous-page\"\n        type=\"button\"\n        class=\"py-4 px-5\">\n        <i class=\"far fa-chevron-left\"></i>\n        <span class=\"pl-1 font-primary\"> Prev </span>\n      </button>\n\n      <button\n        (click)=\"nextPage()\"\n        [disabled]=\"\n          numberOfResults | isNextButtonDisabled : currentPage : currentPageSize\n        \"\n        [ngClass]=\"\n          (numberOfResults | isNextButtonDisabled : currentPage : currentPageSize)\n            ? 'text-neutral-300'\n            : ''\n        \"\n        id=\"next-page\"\n        type=\"button\"\n        class=\"py-4 px-5\">\n        <span class=\"pr-1 font-primary\"> Next </span>\n        <i class=\"far fa-chevron-right\"></i>\n      </button>\n      <div class=\"py-4 px-5 text-neutral-300\">\n        <i class=\"fa-regular fa-pipe\"></i>\n      </div>\n      <button\n        (click)=\"lastPage()\"\n        [disabled]=\"\n          numberOfResults | isNextButtonDisabled : currentPage : currentPageSize\n        \"\n        [ngClass]=\"\n          (numberOfResults | isNextButtonDisabled : currentPage : currentPageSize)\n            ? 'text-neutral-300'\n            : ''\n        \"\n        id=\"last-page\"\n        type=\"button\"\n        class=\"\"\n        class=\"py-4 px-5\"\n        aria-label=\"Open last page\">\n        <i class=\"fak fa-talos-last-page text-6\"></i>\n      </button>\n    </div>\n  </div>\n</div>\n" }]
        }], propDecorators: { pageSizes: [{
                type: Input
            }], currentPageSize: [{
                type: Input
            }], currentPage: [{
                type: Input
            }], numberOfResults: [{
                type: Input
            }], pageSizeChangeEvent: [{
                type: Output
            }], previousPageEvent: [{
                type: Output
            }], nextPageEvent: [{
                type: Output
            }], firstPageEvent: [{
                type: Output
            }], lastPageEvent: [{
                type: Output
            }], onPageSizeChangedEvent: [{
                type: Output
            }], onPageNumberChangedEvent: [{
                type: Output
            }] } });

class ProgressBarComponent {
    constructor() {
        this.percentageComplete = 0;
        this.isSmall = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ProgressBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: ProgressBarComponent, isStandalone: false, selector: "progress-bar", inputs: { percentageComplete: "percentageComplete", isSmall: "isSmall" }, ngImport: i0, template: "<div\n  [ngClass]=\"isSmall ? 'h-PROGRESSSMALL' : 'h-PROGRESS'\"\n  class=\"bg-neutral-75 w-full rounded\">\n  <div\n    [style]=\"'width:' + percentageComplete + '%'\"\n    class=\"bg-secondary-300 h-PROGRESS transition-all rounded\"></div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ProgressBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'progress-bar', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<div\n  [ngClass]=\"isSmall ? 'h-PROGRESSSMALL' : 'h-PROGRESS'\"\n  class=\"bg-neutral-75 w-full rounded\">\n  <div\n    [style]=\"'width:' + percentageComplete + '%'\"\n    class=\"bg-secondary-300 h-PROGRESS transition-all rounded\"></div>\n</div>\n" }]
        }], ctorParameters: () => [], propDecorators: { percentageComplete: [{
                type: Input
            }], isSmall: [{
                type: Input
            }] } });

class SkipToContentButtonComponent {
    onEnter(event) {
        this.clickToSkipToContentEvent.emit();
    }
    constructor(el) {
        this.el = el;
        this.clickToSkipToContentEvent = new EventEmitter();
    }
    ngAfterViewInit() {
        setTimeout(() => {
            this.el.nativeElement.querySelector('#skipToContentButton').focus();
        }, 0);
    }
    onClickToSkipToContentButton() {
        this.clickToSkipToContentEvent.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: SkipToContentButtonComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: SkipToContentButtonComponent, isStandalone: false, selector: "skip-to-content-button", outputs: { clickToSkipToContentEvent: "clickToSkipToContentEvent" }, host: { listeners: { "document:keydown.enter": "onEnter($event)" } }, ngImport: i0, template: "<button\n  id=\"skipToContentButton\"\n  (click)=\"onClickToSkipToContentButton()\"\n  type=\"button\"\n  class=\"focus:shadow-skipToContent focus:outline-none absolute top-1 left-1 py-[18px] px-6 w-fit h-fit bg-error-500 hover:bg-error-400 active:bg-error-600 text-light text-3 leading-16 z-10 rounded-md\">\n  Skip to Main Content\n</button>\n" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: SkipToContentButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'skip-to-content-button', standalone: false, template: "<button\n  id=\"skipToContentButton\"\n  (click)=\"onClickToSkipToContentButton()\"\n  type=\"button\"\n  class=\"focus:shadow-skipToContent focus:outline-none absolute top-1 left-1 py-[18px] px-6 w-fit h-fit bg-error-500 hover:bg-error-400 active:bg-error-600 text-light text-3 leading-16 z-10 rounded-md\">\n  Skip to Main Content\n</button>\n" }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { clickToSkipToContentEvent: [{
                type: Output
            }], onEnter: [{
                type: HostListener,
                args: ['document:keydown.enter', ['$event']]
            }] } });

class SliderComponent {
    constructor() {
        this.minValue = 0;
        this.maxValue = 100;
        this.value = 0;
        this.orientation = '';
        this.sliderDragEvent = new EventEmitter();
    }
    onSliderMove(matSliderChange) {
        this.sliderDragEvent.emit(matSliderChange ?? 0);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: SliderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: SliderComponent, isStandalone: false, selector: "slider", inputs: { minValue: "minValue", maxValue: "maxValue", value: "value", orientation: "orientation" }, outputs: { sliderDragEvent: "sliderDragEvent" }, ngImport: i0, template: "<mat-slider\r\n  [min]=\"minValue\"\r\n  [max]=\"maxValue\">\r\n    <input matSliderThumb \r\n      [value]=\"value === undefined ? 0 : value\"\r\n      (valueChange)=\"onSliderMove($event)\">\r\n</mat-slider>\r\n", styles: [":root{--mdc-slider-inactive-track-shape: 4px;--mdc-slider-active-track-shape: 4px;--mdc-slider-active-track-height: 8px;--mdc-slider-inactive-track-height: 8px;--mdc-slider-inactive-track-color: #dfe0e3;--mdc-slider-active-track-color: #14bdbd;--mdc-slider-handle-shape: 50%;--mdc-slider-handle-width: 16px;--mdc-slider-handle-height: 16px;--mdc-slider-handle-color: #14bdbd;--mdc-slider-hover-handle-color: #14bdbd;--mdc-slider-focus-handle-color: #14bdbd}.mat-mdc-slider.mdc-slider{cursor:default;padding:0;margin:0 24px;width:calc(100% - 48px)}.mat-mdc-slider.mdc-slider .mdc-slider__thumb-knob{transition:width .2s ease,height .2s ease}.mat-mdc-slider.mdc-slider:active .mdc-slider__thumb-knob{height:22px;width:22px}input[type=range].mdc-slider__input{cursor:grab}input[type=range].mdc-slider__input:active{cursor:grabbing}\n"], dependencies: [{ kind: "component", type: i1$1.MatSlider, selector: "mat-slider", inputs: ["disabled", "discrete", "showTickMarks", "min", "color", "disableRipple", "max", "step", "displayWith"], exportAs: ["matSlider"] }, { kind: "directive", type: i1$1.MatSliderThumb, selector: "input[matSliderThumb]", inputs: ["value"], outputs: ["valueChange", "dragStart", "dragEnd"], exportAs: ["matSliderThumb"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: SliderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'slider', encapsulation: ViewEncapsulation.None, standalone: false, template: "<mat-slider\r\n  [min]=\"minValue\"\r\n  [max]=\"maxValue\">\r\n    <input matSliderThumb \r\n      [value]=\"value === undefined ? 0 : value\"\r\n      (valueChange)=\"onSliderMove($event)\">\r\n</mat-slider>\r\n", styles: [":root{--mdc-slider-inactive-track-shape: 4px;--mdc-slider-active-track-shape: 4px;--mdc-slider-active-track-height: 8px;--mdc-slider-inactive-track-height: 8px;--mdc-slider-inactive-track-color: #dfe0e3;--mdc-slider-active-track-color: #14bdbd;--mdc-slider-handle-shape: 50%;--mdc-slider-handle-width: 16px;--mdc-slider-handle-height: 16px;--mdc-slider-handle-color: #14bdbd;--mdc-slider-hover-handle-color: #14bdbd;--mdc-slider-focus-handle-color: #14bdbd}.mat-mdc-slider.mdc-slider{cursor:default;padding:0;margin:0 24px;width:calc(100% - 48px)}.mat-mdc-slider.mdc-slider .mdc-slider__thumb-knob{transition:width .2s ease,height .2s ease}.mat-mdc-slider.mdc-slider:active .mdc-slider__thumb-knob{height:22px;width:22px}input[type=range].mdc-slider__input{cursor:grab}input[type=range].mdc-slider__input:active{cursor:grabbing}\n"] }]
        }], propDecorators: { minValue: [{
                type: Input
            }], maxValue: [{
                type: Input
            }], value: [{
                type: Input
            }], orientation: [{
                type: Input
            }], sliderDragEvent: [{
                type: Output
            }] } });

class TalosTouchEventsListenerDirective {
    constructor() {
        this.onSwipeLeft = new EventEmitter();
        this.onSwipeRight = new EventEmitter();
        this.onSwipeUp = new EventEmitter();
        this.onSwipeDown = new EventEmitter();
        this.defaultTouch = { x: 0, y: 0, time: 0 };
    }
    handleTouch(event) {
        let touch = event.touches[0] || event.changedTouches[0];
        if (event.type === 'touchstart') {
            this.defaultTouch.x = touch.pageX;
            this.defaultTouch.y = touch.pageY;
            this.defaultTouch.time = event.timeStamp;
        }
        else if (event.type === 'touchend') {
            let deltaX = touch.pageX - this.defaultTouch.x;
            let deltaY = touch.pageY - this.defaultTouch.y;
            let deltaTime = event.timeStamp - this.defaultTouch.time;
            // simulte a swipe -> less than 500 ms and more than 40 px
            if (deltaTime < 500) {
                if (Math.abs(deltaX) > 40) {
                    if (deltaX > 0) {
                        this.onSwipeRight.emit();
                    }
                    else {
                        this.onSwipeLeft.emit();
                    }
                }
                if (Math.abs(deltaY) > 40) {
                    if (deltaY > 0) {
                        this.onSwipeDown.emit();
                    }
                    else {
                        this.onSwipeDown.emit();
                    }
                }
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosTouchEventsListenerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.6", type: TalosTouchEventsListenerDirective, isStandalone: false, selector: "[listenTouchEvents]", outputs: { onSwipeLeft: "onSwipeLeft", onSwipeRight: "onSwipeRight", onSwipeUp: "onSwipeUp", onSwipeDown: "onSwipeDown" }, host: { listeners: { "touchstart": "handleTouch($event)", "touchend": "handleTouch($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosTouchEventsListenerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[listenTouchEvents]',
                    standalone: false
                }]
        }], propDecorators: { onSwipeLeft: [{
                type: Output
            }], onSwipeRight: [{
                type: Output
            }], onSwipeUp: [{
                type: Output
            }], onSwipeDown: [{
                type: Output
            }], handleTouch: [{
                type: HostListener,
                args: ['touchstart', ['$event']]
            }, {
                type: HostListener,
                args: ['touchend', ['$event']]
            }] } });

class TalosCarouselComponent {
    constructor() {
        this.images = [];
        this.carouselPointer = 0;
    }
    ngOnChanges() {
        this.carouselPointer = 0;
    }
    toNextImage() {
        this.carouselPointer =
            this.carouselPointer === this.images.length - 1 ? 0 : this.carouselPointer + 1;
    }
    toPreviousImage() {
        this.carouselPointer =
            this.carouselPointer !== 0 ? this.carouselPointer - 1 : this.images.length - 1;
    }
    getOtherImage(index) {
        this.carouselPointer = index;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosCarouselComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosCarouselComponent, isStandalone: false, selector: "talos-carousel", inputs: { images: "images" }, usesOnChanges: true, ngImport: i0, template: "<ng-container *ngIf=\"images\">\n  <div class=\"relative w-full h-full min-h-400 overflow-hidden\">\n    <ng-container *ngFor=\"let img of images; let index = index\">\n      <div\n        *ngIf=\"index === carouselPointer\"\n        @carouselFadeAnimation\n        [title]=\"'Image ' + img.altText\"\n        [ngStyle]=\"{ 'background-image': 'url(' + img.url + ')' }\"\n        class=\"absolute bg-cover bg-center w-full h-full max-h-inherit min-h-400 mx-auto bg-black\"\n        listenTouchEvents\n        (onSwipeLeft)=\"toNextImage()\"\n        (onSwipeRight)=\"toPreviousImage()\"></div>\n    </ng-container>\n    <button\n      *ngIf=\"images.length > 1\"\n      (click)=\"toPreviousImage()\"\n      [id]=\"'carousel-left-' + images[carouselPointer].id\"\n      aria-label=\"To previous image\"\n      type=\"button\"\n      class=\"absolute top-50pc left-26px w-44 h-44 bg-light opacity-80 rounded-full\">\n      <i class=\"fak fa-arrow-left text-31\"></i>\n    </button>\n    <button\n      *ngIf=\"images.length > 1\"\n      (click)=\"toNextImage()\"\n      [id]=\"'carousel-right-' + images[carouselPointer].id\"\n      aria-label=\"To next image\"\n      type=\"button\"\n      class=\"absolute top-50pc right-26px w-44 h-44 bg-light opacity-80 rounded-full\">\n      <i class=\"fak fa-arrow-right text-31\"></i>\n    </button>\n  </div>\n\n  <div *ngIf=\"images.length > 1\" class=\"w-fit -mt-10 mb-22px mx-auto\">\n    <button\n      *ngFor=\"let image of images; let i = index\"\n      (click)=\"getOtherImage(i)\"\n      [id]=\"'carousel-circle-' + image.id\"\n      aria-label=\"Select image\"\n      type=\"button\"\n      [ngClass]=\"carouselPointer === i ? 'opacity-100' : 'opacity-50'\"\n      class=\"w-20 h-20 ml-2 bg-light rounded-full\"></button>\n  </div>\n</ng-container>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: TalosTouchEventsListenerDirective, selector: "[listenTouchEvents]", outputs: ["onSwipeLeft", "onSwipeRight", "onSwipeUp", "onSwipeDown"] }], animations: [carouselFadeAnimation], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosCarouselComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-carousel', changeDetection: ChangeDetectionStrategy.OnPush, animations: [carouselFadeAnimation], standalone: false, template: "<ng-container *ngIf=\"images\">\n  <div class=\"relative w-full h-full min-h-400 overflow-hidden\">\n    <ng-container *ngFor=\"let img of images; let index = index\">\n      <div\n        *ngIf=\"index === carouselPointer\"\n        @carouselFadeAnimation\n        [title]=\"'Image ' + img.altText\"\n        [ngStyle]=\"{ 'background-image': 'url(' + img.url + ')' }\"\n        class=\"absolute bg-cover bg-center w-full h-full max-h-inherit min-h-400 mx-auto bg-black\"\n        listenTouchEvents\n        (onSwipeLeft)=\"toNextImage()\"\n        (onSwipeRight)=\"toPreviousImage()\"></div>\n    </ng-container>\n    <button\n      *ngIf=\"images.length > 1\"\n      (click)=\"toPreviousImage()\"\n      [id]=\"'carousel-left-' + images[carouselPointer].id\"\n      aria-label=\"To previous image\"\n      type=\"button\"\n      class=\"absolute top-50pc left-26px w-44 h-44 bg-light opacity-80 rounded-full\">\n      <i class=\"fak fa-arrow-left text-31\"></i>\n    </button>\n    <button\n      *ngIf=\"images.length > 1\"\n      (click)=\"toNextImage()\"\n      [id]=\"'carousel-right-' + images[carouselPointer].id\"\n      aria-label=\"To next image\"\n      type=\"button\"\n      class=\"absolute top-50pc right-26px w-44 h-44 bg-light opacity-80 rounded-full\">\n      <i class=\"fak fa-arrow-right text-31\"></i>\n    </button>\n  </div>\n\n  <div *ngIf=\"images.length > 1\" class=\"w-fit -mt-10 mb-22px mx-auto\">\n    <button\n      *ngFor=\"let image of images; let i = index\"\n      (click)=\"getOtherImage(i)\"\n      [id]=\"'carousel-circle-' + image.id\"\n      aria-label=\"Select image\"\n      type=\"button\"\n      [ngClass]=\"carouselPointer === i ? 'opacity-100' : 'opacity-50'\"\n      class=\"w-20 h-20 ml-2 bg-light rounded-full\"></button>\n  </div>\n</ng-container>\n" }]
        }], propDecorators: { images: [{
                type: Input
            }] } });

class ClickOutsideTalosSelectDirective {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.clickOutside = new EventEmitter();
    }
    onClick(event, targetElement) {
        if (!targetElement) {
            return;
        }
        const clickedInside = this.elementRef.nativeElement?.contains(targetElement) ||
            this.elementRef.nativeElement?.previousSibling.contains(targetElement);
        if (!clickedInside) {
            this.clickOutside.emit(event);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ClickOutsideTalosSelectDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.6", type: ClickOutsideTalosSelectDirective, isStandalone: false, selector: "[clickOutside]", outputs: { clickOutside: "clickOutside" }, host: { listeners: { "document:click": "onClick($event,$event.target)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ClickOutsideTalosSelectDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[clickOutside]',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { clickOutside: [{
                type: Output
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event', '$event.target']]
            }] } });

class TalosDropdownMenuComponent {
    constructor() {
        this.icon = 'fa-ellipsis-vertical';
        this.isDisabled = false;
        this.isMenuOpen = false;
        this.buttonStyles = 'z-SIDENAV px-5';
        this.iconSize = '';
        this.menuPosition = '-right-3 top-10';
        this.onToggleEvent = new EventEmitter();
    }
    toggleIsOpen($event) {
        $event.stopPropagation();
        this.isMenuOpen = !this.isMenuOpen;
        this.onToggleEvent.emit();
    }
    clickOutside($event) {
        this.isMenuOpen = false;
        this.onToggleEvent.emit(this.isMenuOpen);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosDropdownMenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosDropdownMenuComponent, isStandalone: false, selector: "talos-dropdown-menu", inputs: { icon: "icon", isDisabled: "isDisabled", isMenuOpen: "isMenuOpen", buttonStyles: "buttonStyles", iconSize: "iconSize", menuPosition: "menuPosition" }, outputs: { onToggleEvent: "onToggleEvent" }, ngImport: i0, template: "<div class=\"relative w-fit group\">\n  <button\n    [id]=\"'dropdown-menu'\"\n    class=\"px-3 hover:text-neutral-700 {{ buttonStyles }}\"\n    aria-label=\"Open dropdown menu\"\n    [disabled]=\"isDisabled\"\n    (click)=\"toggleIsOpen($event)\"\n    type=\"button\">\n    <i class=\"fa-regular {{ icon }} {{ iconSize }} text-4 align-middle\"></i>\n  </button>\n\n  <div\n    *ngIf=\"isMenuOpen\"\n    (clickOutside)=\"clickOutside($event)\"\n    class=\"dropdown {{ menuPosition }} top-10 overflow-visible z-MODAL\">\n    <ng-content></ng-content>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: ClickOutsideTalosSelectDirective, selector: "[clickOutside]", outputs: ["clickOutside"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosDropdownMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-dropdown-menu', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<div class=\"relative w-fit group\">\n  <button\n    [id]=\"'dropdown-menu'\"\n    class=\"px-3 hover:text-neutral-700 {{ buttonStyles }}\"\n    aria-label=\"Open dropdown menu\"\n    [disabled]=\"isDisabled\"\n    (click)=\"toggleIsOpen($event)\"\n    type=\"button\">\n    <i class=\"fa-regular {{ icon }} {{ iconSize }} text-4 align-middle\"></i>\n  </button>\n\n  <div\n    *ngIf=\"isMenuOpen\"\n    (clickOutside)=\"clickOutside($event)\"\n    class=\"dropdown {{ menuPosition }} top-10 overflow-visible z-MODAL\">\n    <ng-content></ng-content>\n  </div>\n</div>\n" }]
        }], ctorParameters: () => [], propDecorators: { icon: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], isMenuOpen: [{
                type: Input
            }], buttonStyles: [{
                type: Input
            }], iconSize: [{
                type: Input
            }], menuPosition: [{
                type: Input
            }], onToggleEvent: [{
                type: Output
            }] } });

class ShouldTruncateNamePipe {
    transform(title) {
        return title.length >= 25 ? `${title.substring(0, 25)}...` : title;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ShouldTruncateNamePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: ShouldTruncateNamePipe, isStandalone: false, name: "shouldTruncateName" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ShouldTruncateNamePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'shouldTruncateName',
                    pure: true,
                    standalone: false
                }]
        }] });
class ExpandedButtonBorderStylePipe {
    transform(isFullBorder, isTopBorder, isOpen) {
        let borderStyles = '';
        if (isTopBorder) {
            borderStyles = 'border-t border-neutral-100';
        }
        if (isFullBorder) {
            borderStyles = isOpen
                ? 'border border-neutral-100 rounded hover:shadow-input hover:border-secondary-500'
                : 'border border-neutral-100 rounded hover:shadow-input';
        }
        return borderStyles;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ExpandedButtonBorderStylePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: ExpandedButtonBorderStylePipe, isStandalone: false, name: "expandedButtonBorderStyle" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ExpandedButtonBorderStylePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'expandedButtonBorderStyle',
                    pure: true,
                    standalone: false
                }]
        }] });

class TalosExpandedButtonComponent {
    constructor() {
        this.isTopBorder = true;
        this.isFullBorder = false;
        this.isDisabled = false;
        this.isOpen = false;
        this.isIconOnLeft = false;
        this.hasDropdownMenu = false;
        this.tooltipText = '';
        this.onToggleEvent = new EventEmitter();
    }
    toggleIsOpen($event) {
        $event.stopPropagation();
        this.isOpen = !this.isOpen;
        this.onToggleEvent.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosExpandedButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosExpandedButtonComponent, isStandalone: false, selector: "talos-expanded-button", inputs: { buttonTitle: "buttonTitle", isTopBorder: "isTopBorder", isFullBorder: "isFullBorder", isDisabled: "isDisabled", isOpen: "isOpen", isIconOnLeft: "isIconOnLeft", hasDropdownMenu: "hasDropdownMenu", tooltipText: "tooltipText" }, outputs: { onToggleEvent: "onToggleEvent" }, ngImport: i0, template: "<div [ngClass]=\"isFullBorder | expandedButtonBorderStyle : isTopBorder : isOpen\">\r\n  <button\r\n    (click)=\"toggleIsOpen($event)\"\r\n    [disabled]=\"isDisabled\"\r\n    class=\"flex items-center w-full h-56 pr-3\"\r\n    [ngClass]=\"!isIconOnLeft || hasDropdownMenu ? 'justify-between' : 'justify-start'\"\r\n    id=\"talos-expanded-button\"\r\n    type=\"button\">\r\n    <div>\r\n      <ng-content select=\"[expandedButtonDragAndDrop]\"></ng-content>\r\n\r\n      <i\r\n        *ngIf=\"isIconOnLeft\"\r\n        [ngClass]=\"[\r\n          isOpen && !isDisabled ? 'rotate-180' : '',\r\n          isDisabled ? 'opacity-50' : ''\r\n        ]\"\r\n        class=\"far fa-chevron-down ml-5 text-3 text-neutral-600 transition-all align-middle\"></i>\r\n\r\n      <span\r\n        [ngClass]=\"{ 'opacity-50': isDisabled, 'pl-2': isIconOnLeft }\"\r\n        class=\"paragraph-1 text-neutral-600\">\r\n        {{ buttonTitle | shouldTruncateName }}\r\n      </span>\r\n\r\n      <ng-container *ngIf=\"tooltipText\">\r\n        <i\r\n          class=\"fak fa-talos-information ml-1 text-4 text-neutral-500 tooltip-subject\"\r\n          [ngClass]=\"{ 'opacity-50': isDisabled }\">\r\n        </i>\r\n        <div class=\"tooltip points-bottom-left left-6 -top-13 w-295\">\r\n          <div class=\"tooltip-content\">\r\n            <p class=\"paragraph-1\">\r\n              {{ tooltipText }}\r\n            </p>\r\n          </div>\r\n        </div>\r\n      </ng-container>\r\n    </div>\r\n\r\n    <div class=\"flex items-center\">\r\n      <ng-content select=\"[expandedButtonToggle]\"></ng-content>\r\n\r\n      <i\r\n        *ngIf=\"!isIconOnLeft\"\r\n        [ngClass]=\"[\r\n          isOpen && !isDisabled ? 'rotate-180' : '',\r\n          isDisabled ? 'opacity-50' : ''\r\n        ]\"\r\n        class=\"far fa-chevron-down ml-5 text-3 text-neutral-600 transition-all align-middle\"></i>\r\n\r\n      <ng-content select=\"[expandedButtonDropdownMenu]\"></ng-content>\r\n    </div>\r\n  </button>\r\n  <div [@expandAndRetract]=\"isOpen.toString()\" [ngClass]=\"isFullBorder ? 'px-7' : ''\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: ShouldTruncateNamePipe, name: "shouldTruncateName" }, { kind: "pipe", type: ExpandedButtonBorderStylePipe, name: "expandedButtonBorderStyle" }], animations: [expandAndRetract], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosExpandedButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-expanded-button', changeDetection: ChangeDetectionStrategy.OnPush, animations: [expandAndRetract], standalone: false, template: "<div [ngClass]=\"isFullBorder | expandedButtonBorderStyle : isTopBorder : isOpen\">\r\n  <button\r\n    (click)=\"toggleIsOpen($event)\"\r\n    [disabled]=\"isDisabled\"\r\n    class=\"flex items-center w-full h-56 pr-3\"\r\n    [ngClass]=\"!isIconOnLeft || hasDropdownMenu ? 'justify-between' : 'justify-start'\"\r\n    id=\"talos-expanded-button\"\r\n    type=\"button\">\r\n    <div>\r\n      <ng-content select=\"[expandedButtonDragAndDrop]\"></ng-content>\r\n\r\n      <i\r\n        *ngIf=\"isIconOnLeft\"\r\n        [ngClass]=\"[\r\n          isOpen && !isDisabled ? 'rotate-180' : '',\r\n          isDisabled ? 'opacity-50' : ''\r\n        ]\"\r\n        class=\"far fa-chevron-down ml-5 text-3 text-neutral-600 transition-all align-middle\"></i>\r\n\r\n      <span\r\n        [ngClass]=\"{ 'opacity-50': isDisabled, 'pl-2': isIconOnLeft }\"\r\n        class=\"paragraph-1 text-neutral-600\">\r\n        {{ buttonTitle | shouldTruncateName }}\r\n      </span>\r\n\r\n      <ng-container *ngIf=\"tooltipText\">\r\n        <i\r\n          class=\"fak fa-talos-information ml-1 text-4 text-neutral-500 tooltip-subject\"\r\n          [ngClass]=\"{ 'opacity-50': isDisabled }\">\r\n        </i>\r\n        <div class=\"tooltip points-bottom-left left-6 -top-13 w-295\">\r\n          <div class=\"tooltip-content\">\r\n            <p class=\"paragraph-1\">\r\n              {{ tooltipText }}\r\n            </p>\r\n          </div>\r\n        </div>\r\n      </ng-container>\r\n    </div>\r\n\r\n    <div class=\"flex items-center\">\r\n      <ng-content select=\"[expandedButtonToggle]\"></ng-content>\r\n\r\n      <i\r\n        *ngIf=\"!isIconOnLeft\"\r\n        [ngClass]=\"[\r\n          isOpen && !isDisabled ? 'rotate-180' : '',\r\n          isDisabled ? 'opacity-50' : ''\r\n        ]\"\r\n        class=\"far fa-chevron-down ml-5 text-3 text-neutral-600 transition-all align-middle\"></i>\r\n\r\n      <ng-content select=\"[expandedButtonDropdownMenu]\"></ng-content>\r\n    </div>\r\n  </button>\r\n  <div [@expandAndRetract]=\"isOpen.toString()\" [ngClass]=\"isFullBorder ? 'px-7' : ''\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n</div>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { buttonTitle: [{
                type: Input
            }], isTopBorder: [{
                type: Input
            }], isFullBorder: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], isOpen: [{
                type: Input
            }], isIconOnLeft: [{
                type: Input
            }], hasDropdownMenu: [{
                type: Input
            }], tooltipText: [{
                type: Input
            }], onToggleEvent: [{
                type: Output
            }] } });

class TalosInfoMessageComponent {
    constructor() {
        this.shouldShowInfoMessage = false;
        this.link = '';
        this.text = '';
        this.linkText = '';
        this.canBeClosed = true;
        this.wrapperStyles = 'p-7';
        this.icon = 'fak fa-help';
        this.iconClass = 'text-7 text-info-200';
        this.messageClass = 'text-info-200';
        this.onHideInfoMessageEvent = new EventEmitter();
    }
    hideStylesMessage() {
        this.onHideInfoMessageEvent.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosInfoMessageComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosInfoMessageComponent, isStandalone: false, selector: "talos-info-message", inputs: { shouldShowInfoMessage: "shouldShowInfoMessage", link: "link", text: "text", linkText: "linkText", canBeClosed: "canBeClosed", wrapperStyles: "wrapperStyles", icon: "icon", iconClass: "iconClass", messageClass: "messageClass" }, outputs: { onHideInfoMessageEvent: "onHideInfoMessageEvent" }, ngImport: i0, template: "<div\r\n     *ngIf=\"shouldShowInfoMessage\"\r\n     class=\"relative bg-info-100 rounded {{ wrapperStyles }}\">\r\n  <button\r\n          *ngIf=\"canBeClosed\"\r\n          (click)=\"hideStylesMessage()\"\r\n          class=\"absolute top-2 right-2\">\r\n    <i class=\"fak fa-talos-cross text-primary-200 text-1\"></i>\r\n  </button>\r\n\r\n  <div class=\"flex\">\r\n    <i class=\"{{ icon }} {{iconClass}}\"></i>\r\n    <div class=\"ml-3\">\r\n      <p [ngClass]=\"messageClass\">\r\n        {{text}}\r\n      </p>\r\n      <ng-container *ngIf=\"link.length > 0 && linkText.length > 0\">\r\n        <a\r\n           [routerLink]=\"[link]\"\r\n           class=\"flex items-center mt-1 group text-primary-600 font-medium hover:text-primary-600\">\r\n          <span>{{ linkText }}</span>\r\n          <i\r\n             class=\"fak fa-talos-arrow-right font-semibold ml-1 group-hover:ml-2 transition-all\"></i>\r\n        </a>\r\n      </ng-container>\r\n    </div>\r\n  </div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosInfoMessageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-info-message', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<div\r\n     *ngIf=\"shouldShowInfoMessage\"\r\n     class=\"relative bg-info-100 rounded {{ wrapperStyles }}\">\r\n  <button\r\n          *ngIf=\"canBeClosed\"\r\n          (click)=\"hideStylesMessage()\"\r\n          class=\"absolute top-2 right-2\">\r\n    <i class=\"fak fa-talos-cross text-primary-200 text-1\"></i>\r\n  </button>\r\n\r\n  <div class=\"flex\">\r\n    <i class=\"{{ icon }} {{iconClass}}\"></i>\r\n    <div class=\"ml-3\">\r\n      <p [ngClass]=\"messageClass\">\r\n        {{text}}\r\n      </p>\r\n      <ng-container *ngIf=\"link.length > 0 && linkText.length > 0\">\r\n        <a\r\n           [routerLink]=\"[link]\"\r\n           class=\"flex items-center mt-1 group text-primary-600 font-medium hover:text-primary-600\">\r\n          <span>{{ linkText }}</span>\r\n          <i\r\n             class=\"fak fa-talos-arrow-right font-semibold ml-1 group-hover:ml-2 transition-all\"></i>\r\n        </a>\r\n      </ng-container>\r\n    </div>\r\n  </div>\r\n</div>\r\n" }]
        }], propDecorators: { shouldShowInfoMessage: [{
                type: Input
            }], link: [{
                type: Input
            }], text: [{
                type: Input
            }], linkText: [{
                type: Input
            }], canBeClosed: [{
                type: Input
            }], wrapperStyles: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconClass: [{
                type: Input
            }], messageClass: [{
                type: Input
            }], onHideInfoMessageEvent: [{
                type: Output
            }] } });

function getTagColours(tagColour) {
    switch (tagColour) {
        case 'Red':
            return {
                textColour: 'text-error-500',
                backgroundColour: 'bg-error-50',
            };
        case 'Amber':
            return {
                textColour: 'text-warning-750',
                backgroundColour: 'bg-warning-50',
            };
        case 'Green':
            return {
                textColour: 'text-success-800',
                backgroundColour: 'bg-success-100',
            };
        case 'Grey':
            return {
                textColour: 'text-primary-500',
                backgroundColour: 'bg-primary-50',
            };
        default:
            return {
                textColour: 'text-primary-500',
                backgroundColour: 'bg-primary-50',
            };
    }
}
class GetTagTextColourPipe {
    transform(tagColour) {
        return getTagColours(tagColour).textColour;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: GetTagTextColourPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: GetTagTextColourPipe, isStandalone: false, name: "getTagTextColour" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: GetTagTextColourPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'getTagTextColour',
                    pure: true,
                    standalone: false
                }]
        }] });
class GetTagBackgroundPipe {
    transform(tagColour) {
        return getTagColours(tagColour).backgroundColour;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: GetTagBackgroundPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: GetTagBackgroundPipe, isStandalone: false, name: "getTagBackground" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: GetTagBackgroundPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'getTagBackground',
                    pure: true,
                    standalone: false
                }]
        }] });

class TalosNotificationTagComponent {
    constructor() {
        this.tag = {};
        this.index = 0;
        this.canDelete = true;
        this.styles = 'mr-4 mt-4';
        this.onDeleteTagClickEvent = new EventEmitter();
    }
    onDeleteTagClick(tagId, index) {
        this.onDeleteTagClickEvent.emit({ tagId, index });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosNotificationTagComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosNotificationTagComponent, isStandalone: false, selector: "talos-notification-tag", inputs: { tag: "tag", index: "index", canDelete: "canDelete", styles: "styles" }, outputs: { onDeleteTagClickEvent: "onDeleteTagClickEvent" }, ngImport: i0, template: "<ng-container *ngIf=\"tag.colour | getTagTextColour as textColour\">\n  <div\n    class=\"w-fit flex items-center px-2 py-1 rounded {{\n      tag.colour | getTagBackground\n    }} {{ styles }}\">\n    <i\n      *ngIf=\"tag.hasIcon\"\n      class=\"fa-kit fa-talos-info-tag text-4 mr-1 {{ textColour }}\"></i>\n    <p class=\"text-3 leading-18 {{ textColour }}\">\n      {{ tag.text }}\n    </p>\n    <button\n      *ngIf=\"canDelete\"\n      [id]=\"'delete-tag-' + tag.id\"\n      aria-label=\"Delete tag\"\n      type=\"button\"\n      class=\"h-16 w-16 mb-[2px]\"\n      (click)=\"onDeleteTagClick(tag.id, index)\">\n      <i class=\"fa-kit fa-talos-small-cross text-4 {{ textColour }}\"></i>\n    </button>\n  </div>\n</ng-container>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: GetTagTextColourPipe, name: "getTagTextColour" }, { kind: "pipe", type: GetTagBackgroundPipe, name: "getTagBackground" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosNotificationTagComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-notification-tag', standalone: false, template: "<ng-container *ngIf=\"tag.colour | getTagTextColour as textColour\">\n  <div\n    class=\"w-fit flex items-center px-2 py-1 rounded {{\n      tag.colour | getTagBackground\n    }} {{ styles }}\">\n    <i\n      *ngIf=\"tag.hasIcon\"\n      class=\"fa-kit fa-talos-info-tag text-4 mr-1 {{ textColour }}\"></i>\n    <p class=\"text-3 leading-18 {{ textColour }}\">\n      {{ tag.text }}\n    </p>\n    <button\n      *ngIf=\"canDelete\"\n      [id]=\"'delete-tag-' + tag.id\"\n      aria-label=\"Delete tag\"\n      type=\"button\"\n      class=\"h-16 w-16 mb-[2px]\"\n      (click)=\"onDeleteTagClick(tag.id, index)\">\n      <i class=\"fa-kit fa-talos-small-cross text-4 {{ textColour }}\"></i>\n    </button>\n  </div>\n</ng-container>\n" }]
        }], propDecorators: { tag: [{
                type: Input
            }], index: [{
                type: Input
            }], canDelete: [{
                type: Input
            }], styles: [{
                type: Input
            }], onDeleteTagClickEvent: [{
                type: Output
            }] } });

class AsPipe {
    transform(value, _type) {
        return value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: AsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: AsPipe, isStandalone: false, name: "as" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: AsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'as',
                    pure: true,
                    standalone: false
                }]
        }] });
class SanitizeHtmlPipe {
    constructor(sanitiser) {
        this.sanitiser = sanitiser;
    }
    transform(stringToSanitise) {
        return this.sanitiser.bypassSecurityTrustHtml(stringToSanitise);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: SanitizeHtmlPipe, deps: [{ token: i1$2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: SanitizeHtmlPipe, isStandalone: false, name: "sanitiseHtml" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: SanitizeHtmlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'sanitiseHtml',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: i1$2.DomSanitizer }] });
class BackgroundImageUrlPipe {
    transform(imageUrl) {
        return `url(${imageUrl})`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: BackgroundImageUrlPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: BackgroundImageUrlPipe, isStandalone: false, name: "backgroundImageUrl" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: BackgroundImageUrlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'backgroundImageUrl',
                    pure: true,
                    standalone: false
                }]
        }] });
class IsErrorStatePipe {
    transform(value, minLength, maxLength, isRequired, isDirty) {
        return ((isDirty && isRequired && value.length < minLength) ||
            (isDirty && maxLength > 0 && value.length > maxLength));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: IsErrorStatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: IsErrorStatePipe, isStandalone: false, name: "isErrorState" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: IsErrorStatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'isErrorState',
                    pure: true,
                    standalone: false
                }]
        }] });
class ErrorStateTextPipe {
    transform(value, minLength, maxLength, isRequired, isDirty, customErrorMessage) {
        if (isDirty && isRequired && value.length < minLength) {
            return customErrorMessage ?? 'This field is required. Please enter a value.';
        }
        if (isDirty && maxLength > 0 && value.length > maxLength) {
            return 'The value exceeds the maximum number of characters.';
        }
        return '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ErrorStateTextPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: ErrorStateTextPipe, isStandalone: false, name: "errorStateText" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ErrorStateTextPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'errorStateText',
                    pure: true,
                    standalone: false
                }]
        }] });
class IsEmailErrorStatePipe {
    transform(value, isDirty) {
        return (isDirty &&
            !new RegExp('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,4}$').test(value));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: IsEmailErrorStatePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: IsEmailErrorStatePipe, isStandalone: false, name: "isEmailErrorState" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: IsEmailErrorStatePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'isEmailErrorState',
                    pure: true,
                    standalone: false
                }]
        }] });
class IfEmptyThenPipe {
    transform(value, defaultValue) {
        return value === '' ? defaultValue : value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: IfEmptyThenPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: IfEmptyThenPipe, isStandalone: false, name: "ifEmptyThen" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: IfEmptyThenPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'ifEmptyThen',
                    pure: true,
                    standalone: false
                }]
        }] });
class HasPermissionPipe {
    transform(permissionsIHave, permissionINeed) {
        return permissionsIHave.includes(permissionINeed);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasPermissionPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: HasPermissionPipe, isStandalone: false, name: "hasPermission" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasPermissionPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'hasPermission',
                    pure: true,
                    standalone: false
                }]
        }] });
class HasPermissionsPipe {
    transform(permissionsIHave, permissionsINeed) {
        return permissionsINeed.every(permissionINeed => permissionsIHave.includes(permissionINeed));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasPermissionsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: HasPermissionsPipe, isStandalone: false, name: "hasPermissions" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasPermissionsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'hasPermissions',
                    pure: true,
                    standalone: false
                }]
        }] });
class HasEitherPermissionsPipe {
    transform(permissionsIHave, permissionsINeedOneOf) {
        if (permissionsINeedOneOf.length == 0) {
            return true;
        }
        return permissionsIHave.some(permission => permissionsINeedOneOf.includes(permission));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasEitherPermissionsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: HasEitherPermissionsPipe, isStandalone: false, name: "hasEitherPermissions" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasEitherPermissionsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'hasEitherPermissions',
                    pure: true,
                    standalone: false
                }]
        }] });
class HighlightSearchPipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(value, searchTerm) {
        if (!searchTerm) {
            return value;
        }
        const lowerCaseValue = value.toLowerCase();
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        const highlightIndexes = [];
        let currentIndex = lowerCaseValue.indexOf(lowerCaseSearchTerm);
        while (currentIndex !== -1) {
            highlightIndexes.push(currentIndex);
            currentIndex = lowerCaseValue.indexOf(lowerCaseSearchTerm, currentIndex + 1);
        }
        let highlightedValue = '';
        let previousIndex = 0;
        for (const index of highlightIndexes) {
            highlightedValue += value.substring(previousIndex, index);
            highlightedValue += `<mark>${value.substr(index, searchTerm.length)}</mark>`;
            previousIndex = index + searchTerm.length;
        }
        highlightedValue += value.substring(previousIndex);
        const safeHtml = this.sanitizer.bypassSecurityTrustHtml(highlightedValue);
        return safeHtml;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HighlightSearchPipe, deps: [{ token: i1$2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: HighlightSearchPipe, isStandalone: false, name: "highlightSearch" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HighlightSearchPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'highlightSearch',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: i1$2.DomSanitizer }] });
class IsObjectEmptyPipe {
    transform(obj) {
        return Object.keys(obj).length === 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: IsObjectEmptyPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: IsObjectEmptyPipe, isStandalone: false, name: "isObjectEmpty" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: IsObjectEmptyPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'isObjectEmpty',
                    standalone: false
                }]
        }] });
class HasFeatureFlagPipe {
    transform(flagsIHave, flagINeed) {
        if (flagsIHave) {
            return flagsIHave.includes(flagINeed);
        }
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasFeatureFlagPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: HasFeatureFlagPipe, isStandalone: false, name: "hasFeatureFlag" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasFeatureFlagPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'hasFeatureFlag',
                    pure: true,
                    standalone: false
                }]
        }] });
class HasFeatureFlagsPipe {
    transform(flagsIHave, flagsINeed) {
        return flagsINeed.every(flagINeed => flagsIHave.includes(flagINeed));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasFeatureFlagsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: HasFeatureFlagsPipe, isStandalone: false, name: "hasFeatureFlags" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasFeatureFlagsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'hasFeatureFlags',
                    pure: true,
                    standalone: false
                }]
        }] });
class HasModulePipe {
    transform(modulesIHave, moduleINeed) {
        return modulesIHave.includes(moduleINeed);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasModulePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: HasModulePipe, isStandalone: false, name: "hasModule" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasModulePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'hasModule',
                    pure: true,
                    standalone: false
                }]
        }] });
class HasModulesPipe {
    transform(modulesIHave, modulesINeed) {
        return modulesINeed.every(moduleINeed => modulesIHave.includes(moduleINeed));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasModulesPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: HasModulesPipe, isStandalone: false, name: "hasModules" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasModulesPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'hasModules',
                    pure: true,
                    standalone: false
                }]
        }] });
class HasModuleSettingPipe {
    transform(moduleSettingsIHave, moduleSettingINeed) {
        return moduleSettingsIHave.includes(moduleSettingINeed);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasModuleSettingPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: HasModuleSettingPipe, isStandalone: false, name: "hasModuleSetting" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasModuleSettingPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'hasModuleSetting',
                    pure: true,
                    standalone: false
                }]
        }] });
class HasModuleSettingsPipe {
    transform(moduleSettingsIHave, moduleSettingsINeed) {
        return moduleSettingsINeed.every(moduleSettingINeed => moduleSettingsIHave.includes(moduleSettingINeed));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasModuleSettingsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: HasModuleSettingsPipe, isStandalone: false, name: "hasModuleSettings" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: HasModuleSettingsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'hasModuleSettings',
                    pure: true,
                    standalone: false
                }]
        }] });

class TalosInputCheckboxComponent {
    constructor() {
        this.checked = false;
        this.inputName = '';
        this.inputLabel = '';
        this.isChild = 'false';
        this.width = 'w-full';
        this.icon = '';
        this.iconStyles = '';
        this.uncheckedIcon = 'fa-kit fa-talos-checkbox-alt-default';
        this.checkedIcon = 'fa-kit fa-talos-checkbox-alt-selected';
        this.newIcon = false;
        this.avatar = {};
        this.initials = '';
        this.tag = {};
        this.toggleEntityEvent = new EventEmitter();
    }
    toggleEntity($event) {
        $event.stopPropagation();
        this.toggleEntityEvent.emit(this.entity);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosInputCheckboxComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosInputCheckboxComponent, isStandalone: false, selector: "talos-input-checkbox", inputs: { checked: "checked", entity: "entity", inputName: "inputName", inputLabel: "inputLabel", isChild: "isChild", width: "width", icon: "icon", iconStyles: "iconStyles", uncheckedIcon: "uncheckedIcon", checkedIcon: "checkedIcon", newIcon: "newIcon", avatar: "avatar", initials: "initials", tag: "tag" }, outputs: { toggleEntityEvent: "toggleEntityEvent" }, ngImport: i0, template: "<div class=\"p-0 {{ width }} group\">\r\n  <div class=\"w-full flex items-center py-3\" (click)=\"toggleEntity($event)\">\r\n    <i\r\n      *ngIf=\"newIcon; else inputCheckbox\"\r\n      id=\"{{ inputName }}\"\r\n      class=\"text-6\"\r\n      [ngClass]=\"\r\n        checked\r\n          ? [checkedIcon, 'text-secondary-500']\r\n          : [uncheckedIcon, 'hover:text-neutral-500', 'text-neutral-400']\r\n      \">\r\n    </i>\r\n\r\n    <ng-template #inputCheckbox>\r\n      <input\r\n        *ngIf=\"!newIcon\"\r\n        [checked]=\"checked\"\r\n        id=\"{{ inputName }}\"\r\n        name=\"{{ inputName }}\"\r\n        type=\"checkbox\"\r\n        class=\"large\" />\r\n    </ng-template>\r\n\r\n    <ng-container *ngIf=\"tag | isObjectEmpty; else tagLayout\">\r\n      <ng-template #inputCheckbox>\r\n        <ng-container *ngIf=\"icon\">\r\n          <img class=\"ml-4 {{ iconStyles }}\" src=\"{{ icon }}\" alt=\"checkbox icon\" />\r\n        </ng-container>\r\n      </ng-template>\r\n\r\n      <div\r\n        *ngIf=\"avatar.textColour\"\r\n        [style.color]=\"avatar.textColour\"\r\n        [style.background]=\"avatar.backgroundColour\"\r\n        class=\"flex justify-center items-center font-medium w-[32px] h-[32px] rounded-full ml-4 text-3\">\r\n        {{ initials }}\r\n      </div>\r\n      <label\r\n        class=\"paragraph-1 block mt-0.5 ml-4 cursor-pointer z-10 text-ellipsis overflow-hidden whitespace-nowrap\"\r\n        for=\"{{ inputName }}\">\r\n        {{ inputLabel }}\r\n      </label>\r\n    </ng-container>\r\n\r\n    <ng-template #tagLayout>\r\n      <talos-notification-tag\r\n        [tag]=\"tag\"\r\n        [canDelete]=\"false\"\r\n        styles=\"ml-4\"></talos-notification-tag>\r\n    </ng-template>\r\n  </div>\r\n  <div class=\"grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: TalosNotificationTagComponent, selector: "talos-notification-tag", inputs: ["tag", "index", "canDelete", "styles"], outputs: ["onDeleteTagClickEvent"] }, { kind: "pipe", type: IsObjectEmptyPipe, name: "isObjectEmpty" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosInputCheckboxComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-input-checkbox', standalone: false, template: "<div class=\"p-0 {{ width }} group\">\r\n  <div class=\"w-full flex items-center py-3\" (click)=\"toggleEntity($event)\">\r\n    <i\r\n      *ngIf=\"newIcon; else inputCheckbox\"\r\n      id=\"{{ inputName }}\"\r\n      class=\"text-6\"\r\n      [ngClass]=\"\r\n        checked\r\n          ? [checkedIcon, 'text-secondary-500']\r\n          : [uncheckedIcon, 'hover:text-neutral-500', 'text-neutral-400']\r\n      \">\r\n    </i>\r\n\r\n    <ng-template #inputCheckbox>\r\n      <input\r\n        *ngIf=\"!newIcon\"\r\n        [checked]=\"checked\"\r\n        id=\"{{ inputName }}\"\r\n        name=\"{{ inputName }}\"\r\n        type=\"checkbox\"\r\n        class=\"large\" />\r\n    </ng-template>\r\n\r\n    <ng-container *ngIf=\"tag | isObjectEmpty; else tagLayout\">\r\n      <ng-template #inputCheckbox>\r\n        <ng-container *ngIf=\"icon\">\r\n          <img class=\"ml-4 {{ iconStyles }}\" src=\"{{ icon }}\" alt=\"checkbox icon\" />\r\n        </ng-container>\r\n      </ng-template>\r\n\r\n      <div\r\n        *ngIf=\"avatar.textColour\"\r\n        [style.color]=\"avatar.textColour\"\r\n        [style.background]=\"avatar.backgroundColour\"\r\n        class=\"flex justify-center items-center font-medium w-[32px] h-[32px] rounded-full ml-4 text-3\">\r\n        {{ initials }}\r\n      </div>\r\n      <label\r\n        class=\"paragraph-1 block mt-0.5 ml-4 cursor-pointer z-10 text-ellipsis overflow-hidden whitespace-nowrap\"\r\n        for=\"{{ inputName }}\">\r\n        {{ inputLabel }}\r\n      </label>\r\n    </ng-container>\r\n\r\n    <ng-template #tagLayout>\r\n      <talos-notification-tag\r\n        [tag]=\"tag\"\r\n        [canDelete]=\"false\"\r\n        styles=\"ml-4\"></talos-notification-tag>\r\n    </ng-template>\r\n  </div>\r\n  <div class=\"grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3\">\r\n    <ng-content></ng-content>\r\n  </div>\r\n</div>\r\n" }]
        }], ctorParameters: () => [], propDecorators: { checked: [{
                type: Input
            }], entity: [{
                type: Input
            }], inputName: [{
                type: Input
            }], inputLabel: [{
                type: Input
            }], isChild: [{
                type: Input
            }], width: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconStyles: [{
                type: Input
            }], uncheckedIcon: [{
                type: Input
            }], checkedIcon: [{
                type: Input
            }], newIcon: [{
                type: Input
            }], avatar: [{
                type: Input
            }], initials: [{
                type: Input
            }], tag: [{
                type: Input
            }], toggleEntityEvent: [{
                type: Output
            }] } });

class TalosInputEmailComponent {
    constructor(isEmailErrorStatePipe) {
        this.isEmailErrorStatePipe = isEmailErrorStatePipe;
        this.value = '';
        this.isRequired = false;
        this.placeholder = '';
        this.name = '';
        this.id = '';
        this.onInputChangeEvent = new EventEmitter();
        this.onInvalidEmailEvent = new EventEmitter();
        this.isDirty = false;
    }
    onInputChanged($event) {
        const value = $event.target.value;
        this.isDirty = true;
        const isEmailInvalid = this.isEmailErrorStatePipe.transform(value, this.isDirty);
        this.onInvalidEmailEvent.emit(isEmailInvalid);
        this.onInputChangeEvent.emit(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosInputEmailComponent, deps: [{ token: IsEmailErrorStatePipe }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosInputEmailComponent, isStandalone: false, selector: "talos-input-email", inputs: { value: "value", isRequired: "isRequired", placeholder: "placeholder", name: "name", id: "id" }, outputs: { onInputChangeEvent: "onInputChangeEvent", onInvalidEmailEvent: "onInvalidEmailEvent" }, ngImport: i0, template: "<div class=\"relative\">\n  <input\n    [value]=\"value\"\n    [ngClass]=\"(value | isEmailErrorState : isDirty) ? 'error' : ''\"\n    (input)=\"onInputChanged($event)\"\n    [placeholder]=\"placeholder\"\n    [name]=\"name\"\n    [id]=\"id\"\n    class=\"w-full\"\n    type=\"email\" />\n  <i\n    *ngIf=\"value | isEmailErrorState : isDirty\"\n    class=\"absolute absolute-center-y right-4 pl-2 py-2 fak fa-talos-warning-triangle bg-light text-4 text-error-500\"></i>\n</div>\n\n<p *ngIf=\"value | isEmailErrorState : isDirty\" class=\"mt-1 caption-1 text-error-500\">\n  This is not a valid email address. Please try again.\n</p>\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: IsEmailErrorStatePipe, name: "isEmailErrorState" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosInputEmailComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-input-email', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<div class=\"relative\">\n  <input\n    [value]=\"value\"\n    [ngClass]=\"(value | isEmailErrorState : isDirty) ? 'error' : ''\"\n    (input)=\"onInputChanged($event)\"\n    [placeholder]=\"placeholder\"\n    [name]=\"name\"\n    [id]=\"id\"\n    class=\"w-full\"\n    type=\"email\" />\n  <i\n    *ngIf=\"value | isEmailErrorState : isDirty\"\n    class=\"absolute absolute-center-y right-4 pl-2 py-2 fak fa-talos-warning-triangle bg-light text-4 text-error-500\"></i>\n</div>\n\n<p *ngIf=\"value | isEmailErrorState : isDirty\" class=\"mt-1 caption-1 text-error-500\">\n  This is not a valid email address. Please try again.\n</p>\n" }]
        }], ctorParameters: () => [{ type: IsEmailErrorStatePipe }], propDecorators: { value: [{
                type: Input
            }], isRequired: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], name: [{
                type: Input
            }], id: [{
                type: Input
            }], onInputChangeEvent: [{
                type: Output
            }], onInvalidEmailEvent: [{
                type: Output
            }] } });

class TalosInputSearchComponent {
    constructor() {
        this.value = '';
        this.minLength = 0;
        this.maxLength = 0;
        this.isRequired = false;
        this.placeholder = '';
        this.name = '';
        this.id = '';
        this.showCharacterLimitCount = false;
        this.inputClasses = 'h-44';
        this.onInputChangeEvent = new EventEmitter();
        this.isDirty = false;
    }
    onInputChanged($event) {
        const value = $event.target.value;
        this.isDirty = true;
        this.onInputChangeEvent.emit(value);
    }
    clearValue() {
        this.isDirty = true;
        this.onInputChangeEvent.emit('');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosInputSearchComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosInputSearchComponent, isStandalone: false, selector: "talos-input-search", inputs: { value: "value", minLength: "minLength", maxLength: "maxLength", isRequired: "isRequired", placeholder: "placeholder", name: "name", id: "id", showCharacterLimitCount: "showCharacterLimitCount", inputClasses: "inputClasses" }, outputs: { onInputChangeEvent: "onInputChangeEvent" }, ngImport: i0, template: "<div class=\"relative\">\n  <i class=\"fak fa-talos-search absolute left-4 absolute-center-y text-neutral-400\"></i>\n  <input\n    [value]=\"value\"\n    [ngClass]=\"[\n      inputClasses,\n      (value | isErrorState : minLength : maxLength : isRequired : isDirty) ? 'error' : ''\n    ]\"\n    (input)=\"onInputChanged($event)\"\n    [placeholder]=\"placeholder\"\n    [name]=\"name\"\n    [id]=\"id\"\n    class=\"w-full pl-8\"\n    type=\"search\" />\n  <!-- <button *ngIf=\"!(value | isErrorState: minLength: maxLength: isRequired: isDirty)\"\n          (click)=\"clearValue()\"\n          [id]=\"'clear-' + id\"\n          class=\"absolute absolute-center-y right-4  bg-neutral-400\"\n          type=\"button\">\n    <i class=\"fak fa-talos-cancel text-4 text-light\"></i>\n  </button> -->\n  <i\n    *ngIf=\"value | isErrorState : minLength : maxLength : isRequired : isDirty\"\n    class=\"absolute absolute-centre-y right-4 fak fa-talos-warning-triangle text-4 text-error-500\"></i>\n</div>\n\n<p\n  *ngIf=\"showCharacterLimitCount\"\n  [ngClass]=\"\n    (value | isErrorState : minLength : maxLength : isRequired : isDirty)\n      ? 'text-error-500'\n      : 'text-neutral-500'\n  \"\n  class=\"mt-1 caption-1 text-right\">\n  {{ value.length }} / {{ maxLength }}\n</p>\n", styles: ["talos-input-search input[type=search]{border:1px solid theme(\"colors.neutral.200\");border-radius:theme(\"borderRadius.DEFAULT\");padding:theme(\"spacing.2\") theme(\"spacing.4\")}talos-input-search input[type=search]:focus{border:1px solid theme(\"colors.secondary.500\");box-shadow:theme(\"boxShadow.input\")}talos-input-search input[type=search].error{border-color:theme(\"colors.error.500\");box-shadow:none}talos-input-search input[type=search]:disabled{background-color:theme(\"colors.neutral.50\");border-color:theme(\"colors.neutral.100\");color:theme(\"colors.neutral.400\")}talos-input-search input[type=search]::placeholder{color:theme(\"colors.neutral.400\")}talos-input-search input[type=search].input-icon-left,talos-input-search input[type=search].input-icons{padding-left:theme(\"spacing.8\")}talos-input-search input[type=search].icon-right,talos-input-search input[type=search].input-icons{padding-right:theme(\"spacing.8\")}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: IsErrorStatePipe, name: "isErrorState" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosInputSearchComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-input-search', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, standalone: false, template: "<div class=\"relative\">\n  <i class=\"fak fa-talos-search absolute left-4 absolute-center-y text-neutral-400\"></i>\n  <input\n    [value]=\"value\"\n    [ngClass]=\"[\n      inputClasses,\n      (value | isErrorState : minLength : maxLength : isRequired : isDirty) ? 'error' : ''\n    ]\"\n    (input)=\"onInputChanged($event)\"\n    [placeholder]=\"placeholder\"\n    [name]=\"name\"\n    [id]=\"id\"\n    class=\"w-full pl-8\"\n    type=\"search\" />\n  <!-- <button *ngIf=\"!(value | isErrorState: minLength: maxLength: isRequired: isDirty)\"\n          (click)=\"clearValue()\"\n          [id]=\"'clear-' + id\"\n          class=\"absolute absolute-center-y right-4  bg-neutral-400\"\n          type=\"button\">\n    <i class=\"fak fa-talos-cancel text-4 text-light\"></i>\n  </button> -->\n  <i\n    *ngIf=\"value | isErrorState : minLength : maxLength : isRequired : isDirty\"\n    class=\"absolute absolute-centre-y right-4 fak fa-talos-warning-triangle text-4 text-error-500\"></i>\n</div>\n\n<p\n  *ngIf=\"showCharacterLimitCount\"\n  [ngClass]=\"\n    (value | isErrorState : minLength : maxLength : isRequired : isDirty)\n      ? 'text-error-500'\n      : 'text-neutral-500'\n  \"\n  class=\"mt-1 caption-1 text-right\">\n  {{ value.length }} / {{ maxLength }}\n</p>\n", styles: ["talos-input-search input[type=search]{border:1px solid theme(\"colors.neutral.200\");border-radius:theme(\"borderRadius.DEFAULT\");padding:theme(\"spacing.2\") theme(\"spacing.4\")}talos-input-search input[type=search]:focus{border:1px solid theme(\"colors.secondary.500\");box-shadow:theme(\"boxShadow.input\")}talos-input-search input[type=search].error{border-color:theme(\"colors.error.500\");box-shadow:none}talos-input-search input[type=search]:disabled{background-color:theme(\"colors.neutral.50\");border-color:theme(\"colors.neutral.100\");color:theme(\"colors.neutral.400\")}talos-input-search input[type=search]::placeholder{color:theme(\"colors.neutral.400\")}talos-input-search input[type=search].input-icon-left,talos-input-search input[type=search].input-icons{padding-left:theme(\"spacing.8\")}talos-input-search input[type=search].icon-right,talos-input-search input[type=search].input-icons{padding-right:theme(\"spacing.8\")}\n"] }]
        }], propDecorators: { value: [{
                type: Input
            }], minLength: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], isRequired: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], name: [{
                type: Input
            }], id: [{
                type: Input
            }], showCharacterLimitCount: [{
                type: Input
            }], inputClasses: [{
                type: Input
            }], onInputChangeEvent: [{
                type: Output
            }] } });

class ShouldShowInputErrorsPipe {
    transform(isDirty, isRequired, minLength, maxLength) {
        return isDirty && (isRequired || minLength > 0 || maxLength > 0);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ShouldShowInputErrorsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: ShouldShowInputErrorsPipe, isStandalone: false, name: "shouldShowInputErrors", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ShouldShowInputErrorsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'shouldShowInputErrors',
                    pure: false,
                    standalone: false
                }]
        }] });
class ShouldShowLeftIconPipe {
    transform(icon, iconLeft) {
        return icon.length > 0 && iconLeft;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ShouldShowLeftIconPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: ShouldShowLeftIconPipe, isStandalone: false, name: "shouldShowLeftIcon" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ShouldShowLeftIconPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'shouldShowLeftIcon',
                    pure: true,
                    standalone: false
                }]
        }] });
class ShouldShowRightIconPipe {
    transform(icon, iconLeft, value, minLength, maxLength, isRequired, isDirty) {
        return (icon.length > 0 &&
            !iconLeft &&
            !((isDirty && isRequired && value.length < minLength) ||
                (isDirty && maxLength > 0 && value.length > maxLength)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ShouldShowRightIconPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: ShouldShowRightIconPipe, isStandalone: false, name: "shouldShowRightIcon" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ShouldShowRightIconPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'shouldShowRightIcon',
                    pure: true,
                    standalone: false
                }]
        }] });

class TalosInputTextComponent {
    set value(value) {
        this._value = value;
        if (this.couldBeChangedOutside) {
            this.checkValidation(value);
        }
    }
    get value() {
        return this._value;
    }
    constructor(isErrorStatePipe) {
        this.isErrorStatePipe = isErrorStatePipe;
        this._value = '';
        this.minLength = 0;
        this.maxLength = 100;
        this.isRequired = false;
        this.placeholder = '';
        this.name = '';
        this.id = '';
        this.showCharacterLimitCount = false;
        this.disabled = false;
        this.shouldShowErrorText = true;
        this.icon = '';
        this.iconLeft = true;
        this.type = 'text';
        this.couldBeChangedOutside = false;
        this.hasValidationError = false;
        this.valueTextSize = '';
        this.ariaLabel = '';
        this.customErrorMessage = '';
        this.onInputChangeEvent = new EventEmitter();
        this.onInvalidTextInputEvent = new EventEmitter();
        this.isDirty = false;
    }
    onInputChanged($event) {
        const value = $event.target.value;
        this.checkValidation(value);
        this.onInputChangeEvent.emit(value);
    }
    checkValidation(value) {
        this.isDirty = true;
        const isTextInputInvalid = this.isErrorStatePipe.transform(value, this.minLength, this.maxLength, this.isRequired, this.isDirty);
        this.onInvalidTextInputEvent.emit(isTextInputInvalid);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosInputTextComponent, deps: [{ token: IsErrorStatePipe }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosInputTextComponent, isStandalone: false, selector: "talos-input-text", inputs: { value: "value", minLength: "minLength", maxLength: "maxLength", isRequired: "isRequired", placeholder: "placeholder", name: "name", id: "id", showCharacterLimitCount: "showCharacterLimitCount", disabled: "disabled", shouldShowErrorText: "shouldShowErrorText", icon: "icon", iconLeft: "iconLeft", type: "type", couldBeChangedOutside: "couldBeChangedOutside", hasValidationError: "hasValidationError", valueTextSize: "valueTextSize", ariaLabel: "ariaLabel", customErrorMessage: "customErrorMessage" }, outputs: { onInputChangeEvent: "onInputChangeEvent", onInvalidTextInputEvent: "onInvalidTextInputEvent" }, ngImport: i0, template: "<div class=\"relative\">\r\n  <input\r\n    [value]=\"value\"\r\n    [class]=\"valueTextSize\"\r\n    [ngClass]=\"{\r\n      error:\r\n        (value | isErrorState : minLength : maxLength : isRequired : isDirty) ||\r\n        hasValidationError,\r\n      'pl-8': icon | shouldShowLeftIcon : iconLeft,\r\n      'pr-8':\r\n        icon\r\n        | shouldShowRightIcon\r\n          : iconLeft\r\n          : value\r\n          : minLength\r\n          : maxLength\r\n          : isRequired\r\n          : isDirty\r\n    }\"\r\n    [placeholder]=\"placeholder\"\r\n    [name]=\"name\"\r\n    [id]=\"id\"\r\n    [disabled]=\"disabled\"\r\n    [minLength]=\"minLength\"\r\n    [maxLength]=\"maxLength\"\r\n    [type]=\"type\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    (input)=\"onInputChanged($event)\"\r\n    class=\"w-full hover:shadow-hover ring-0 placeholder:text-neutral-500\" />\r\n  <i\r\n    *ngIf=\"icon | shouldShowLeftIcon : iconLeft\"\r\n    [ngClass]=\"icon\"\r\n    class=\"absolute absolute-center-y left-4 text-neutral-400\"></i>\r\n  <i\r\n    *ngIf=\"\r\n      icon\r\n        | shouldShowRightIcon\r\n          : iconLeft\r\n          : value\r\n          : minLength\r\n          : maxLength\r\n          : isRequired\r\n          : isDirty\r\n    \"\r\n    [ngClass]=\"icon\"\r\n    class=\"absolute absolute-center-y right-4 text-neutral-400\"></i>\r\n  <i\r\n    *ngIf=\"\r\n      (value | isErrorState : minLength : maxLength : isRequired : isDirty) ||\r\n      hasValidationError\r\n    \"\r\n    class=\"absolute absolute-center-y right-4 pl-2 py-2 fak fa-talos-warning-triangle bg-light text-4 text-error-500\"></i>\r\n  <p\r\n    *ngIf=\"showCharacterLimitCount && !(isRequired && value.length === 0)\"\r\n    [ngClass]=\"\r\n      (value | isErrorState : minLength : maxLength : isRequired : isDirty) ||\r\n      hasValidationError\r\n        ? 'text-error-500'\r\n        : 'text-neutral-500'\r\n    \"\r\n    class=\"absolute right-0 top-10 caption-1 text-right whitespace-nowrap\">\r\n    {{ value.length }} / {{ maxLength }}\r\n  </p>\r\n</div>\r\n\r\n<div\r\n  *ngIf=\"\r\n    (isDirty | shouldShowInputErrors : isRequired : minLength : maxLength) &&\r\n    shouldShowErrorText\r\n  \"\r\n  class=\"flex\">\r\n  <p\r\n    *ngIf=\"value | errorStateText : minLength : maxLength : isRequired : isDirty: customErrorMessage as text\"\r\n    class=\"caption-1 text-error-500 mt-1\">\r\n    {{ text }}\r\n  </p>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: ShouldShowInputErrorsPipe, name: "shouldShowInputErrors" }, { kind: "pipe", type: ShouldShowLeftIconPipe, name: "shouldShowLeftIcon" }, { kind: "pipe", type: ShouldShowRightIconPipe, name: "shouldShowRightIcon" }, { kind: "pipe", type: IsErrorStatePipe, name: "isErrorState" }, { kind: "pipe", type: ErrorStateTextPipe, name: "errorStateText" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosInputTextComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-input-text', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<div class=\"relative\">\r\n  <input\r\n    [value]=\"value\"\r\n    [class]=\"valueTextSize\"\r\n    [ngClass]=\"{\r\n      error:\r\n        (value | isErrorState : minLength : maxLength : isRequired : isDirty) ||\r\n        hasValidationError,\r\n      'pl-8': icon | shouldShowLeftIcon : iconLeft,\r\n      'pr-8':\r\n        icon\r\n        | shouldShowRightIcon\r\n          : iconLeft\r\n          : value\r\n          : minLength\r\n          : maxLength\r\n          : isRequired\r\n          : isDirty\r\n    }\"\r\n    [placeholder]=\"placeholder\"\r\n    [name]=\"name\"\r\n    [id]=\"id\"\r\n    [disabled]=\"disabled\"\r\n    [minLength]=\"minLength\"\r\n    [maxLength]=\"maxLength\"\r\n    [type]=\"type\"\r\n    [attr.aria-label]=\"ariaLabel\"\r\n    (input)=\"onInputChanged($event)\"\r\n    class=\"w-full hover:shadow-hover ring-0 placeholder:text-neutral-500\" />\r\n  <i\r\n    *ngIf=\"icon | shouldShowLeftIcon : iconLeft\"\r\n    [ngClass]=\"icon\"\r\n    class=\"absolute absolute-center-y left-4 text-neutral-400\"></i>\r\n  <i\r\n    *ngIf=\"\r\n      icon\r\n        | shouldShowRightIcon\r\n          : iconLeft\r\n          : value\r\n          : minLength\r\n          : maxLength\r\n          : isRequired\r\n          : isDirty\r\n    \"\r\n    [ngClass]=\"icon\"\r\n    class=\"absolute absolute-center-y right-4 text-neutral-400\"></i>\r\n  <i\r\n    *ngIf=\"\r\n      (value | isErrorState : minLength : maxLength : isRequired : isDirty) ||\r\n      hasValidationError\r\n    \"\r\n    class=\"absolute absolute-center-y right-4 pl-2 py-2 fak fa-talos-warning-triangle bg-light text-4 text-error-500\"></i>\r\n  <p\r\n    *ngIf=\"showCharacterLimitCount && !(isRequired && value.length === 0)\"\r\n    [ngClass]=\"\r\n      (value | isErrorState : minLength : maxLength : isRequired : isDirty) ||\r\n      hasValidationError\r\n        ? 'text-error-500'\r\n        : 'text-neutral-500'\r\n    \"\r\n    class=\"absolute right-0 top-10 caption-1 text-right whitespace-nowrap\">\r\n    {{ value.length }} / {{ maxLength }}\r\n  </p>\r\n</div>\r\n\r\n<div\r\n  *ngIf=\"\r\n    (isDirty | shouldShowInputErrors : isRequired : minLength : maxLength) &&\r\n    shouldShowErrorText\r\n  \"\r\n  class=\"flex\">\r\n  <p\r\n    *ngIf=\"value | errorStateText : minLength : maxLength : isRequired : isDirty: customErrorMessage as text\"\r\n    class=\"caption-1 text-error-500 mt-1\">\r\n    {{ text }}\r\n  </p>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: IsErrorStatePipe }], propDecorators: { value: [{
                type: Input
            }], minLength: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], isRequired: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], name: [{
                type: Input
            }], id: [{
                type: Input
            }], showCharacterLimitCount: [{
                type: Input
            }], disabled: [{
                type: Input
            }], shouldShowErrorText: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconLeft: [{
                type: Input
            }], type: [{
                type: Input
            }], couldBeChangedOutside: [{
                type: Input
            }], hasValidationError: [{
                type: Input
            }], valueTextSize: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], customErrorMessage: [{
                type: Input
            }], onInputChangeEvent: [{
                type: Output
            }], onInvalidTextInputEvent: [{
                type: Output
            }] } });

class TalosInputToggleComponent {
    constructor() {
        this.isChecked = false;
        this.identifier = '';
        this.disabled = false;
        this.onToggleEvent = new EventEmitter();
    }
    onToggle($event) {
        $event.stopPropagation();
        this.onToggleEvent.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosInputToggleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosInputToggleComponent, isStandalone: false, selector: "talos-input-toggle", inputs: { isChecked: "isChecked", identifier: "identifier", disabled: "disabled" }, outputs: { onToggleEvent: "onToggleEvent" }, ngImport: i0, template: "<label class=\"switch block\">\n  <input\n    (click)=\"onToggle($event)\"\n    [checked]=\"isChecked\"\n    [disabled]=\"disabled\"\n    [name]=\"identifier\"\n    [id]=\"identifier\"\n    type=\"checkbox\" />\n  <span></span>\n</label>\n", changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosInputToggleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-input-toggle', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<label class=\"switch block\">\n  <input\n    (click)=\"onToggle($event)\"\n    [checked]=\"isChecked\"\n    [disabled]=\"disabled\"\n    [name]=\"identifier\"\n    [id]=\"identifier\"\n    type=\"checkbox\" />\n  <span></span>\n</label>\n" }]
        }], propDecorators: { isChecked: [{
                type: Input
            }], identifier: [{
                type: Input
            }], disabled: [{
                type: Input
            }], onToggleEvent: [{
                type: Output
            }] } });

class TalosLoadingComponent {
    constructor() {
        this.isLoading = true;
        this.isFullScreen = false;
        this.zIndex = 10;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosLoadingComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosLoadingComponent, isStandalone: false, selector: "talos-loading", inputs: { isLoading: "isLoading", isFullScreen: "isFullScreen", zIndex: "zIndex" }, ngImport: i0, template: "<div\n  *ngIf=\"isLoading\"\n  [@fade]\n  [ngClass]=\"isFullScreen ? 'fixed bg-opacity-80 bg-light' : 'absolute'\"\n  [style.zIndex]=\"zIndex\"\n  class=\"h-full w-full top-0 right-0\">\n  <div class=\"loading-balls items-center justify-center h-full\">\n    <div class=\"loading-ball mr-2\"></div>\n    <div class=\"loading-ball mr-2 ball2\"></div>\n    <div class=\"loading-ball ball3\"></div>\n  </div>\n</div>\n", styles: [".loading-balls{display:flex}.loading-balls .loading-ball{height:theme(\"fontSize.1\");width:theme(\"fontSize.1\");border-radius:theme(\"borderRadius.full\");background:theme(\"colors.primary.200\");animation:bounce 1.5s infinite ease-in-out}.loading-balls .loading-ball.ball2{animation-delay:.25s}.loading-balls .loading-ball.ball3{animation-delay:.5s}.loading-balls .loading-ball.ball4{animation-delay:.75s}@keyframes bounce{0%,15%{transform:translate(0)}50%{transform:translateY(-16px);background:theme(\"colors.primary.600\")}85%,to{transform:translate(0)}}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [fade], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosLoadingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-loading', animations: [fade], changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<div\n  *ngIf=\"isLoading\"\n  [@fade]\n  [ngClass]=\"isFullScreen ? 'fixed bg-opacity-80 bg-light' : 'absolute'\"\n  [style.zIndex]=\"zIndex\"\n  class=\"h-full w-full top-0 right-0\">\n  <div class=\"loading-balls items-center justify-center h-full\">\n    <div class=\"loading-ball mr-2\"></div>\n    <div class=\"loading-ball mr-2 ball2\"></div>\n    <div class=\"loading-ball ball3\"></div>\n  </div>\n</div>\n", styles: [".loading-balls{display:flex}.loading-balls .loading-ball{height:theme(\"fontSize.1\");width:theme(\"fontSize.1\");border-radius:theme(\"borderRadius.full\");background:theme(\"colors.primary.200\");animation:bounce 1.5s infinite ease-in-out}.loading-balls .loading-ball.ball2{animation-delay:.25s}.loading-balls .loading-ball.ball3{animation-delay:.5s}.loading-balls .loading-ball.ball4{animation-delay:.75s}@keyframes bounce{0%,15%{transform:translate(0)}50%{transform:translateY(-16px);background:theme(\"colors.primary.600\")}85%,to{transform:translate(0)}}\n"] }]
        }], propDecorators: { isLoading: [{
                type: Input
            }], isFullScreen: [{
                type: Input
            }], zIndex: [{
                type: Input
            }] } });

class TalosRadioComponent {
    constructor() {
        this.isSelected = false;
        this.text = '';
        this.id = '';
        this.name = '';
        this.tooltipText = '';
        this.onCheckEvent = new EventEmitter();
    }
    check() {
        this.onCheckEvent.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosRadioComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosRadioComponent, isStandalone: false, selector: "talos-radio", inputs: { isSelected: "isSelected", text: "text", id: "id", name: "name", tooltipText: "tooltipText" }, outputs: { onCheckEvent: "onCheckEvent" }, ngImport: i0, template: "<div (click)=\"check()\" class=\"relative cursor-pointer radio-button-parent\">\r\n  <label\r\n    class=\"absolute ml-2 mt-0 left-8 absolute-center-y leading-20 cursor-pointer z-1\">\r\n    {{ text }}\r\n    <ng-container *ngIf=\"tooltipText.length\">\r\n      <i class=\"fak fa-talos-information tooltip-subject text-neutral-500 text-3 ml-1\">\r\n        <div class=\"tooltip w-350 centre features-tooltip -mt-21\">\r\n          <div class=\"tooltip-content p-4\">\r\n            <p>\r\n              {{ tooltipText }}\r\n            </p>\r\n          </div>\r\n        </div>\r\n      </i>\r\n    </ng-container>\r\n  </label>\r\n  <input\r\n    [checked]=\"isSelected\"\r\n    [id]=\"id\"\r\n    [name]=\"name\"\r\n    type=\"radio\"\r\n    class=\"radio-button w-full h-44 rounded\" />\r\n</div>\r\n", styles: [".radio-button-parent label:hover+input[type=radio],.radio-button-parent:hover label:hover+input[type=radio]{border-color:theme(\"colors.secondary.50\");outline:none;box-shadow:theme(\"boxShadow.input\")}.radio-button-parent input[type=radio],.radio-button-parent:hover input[type=radio]{position:relative;background-color:theme(\"colors.light\");border:theme(\"borderWidth.1\") solid theme(\"colors.neutral.200\");border-radius:theme(\"borderRadius.sm\");cursor:pointer;transition:all theme(\"duration.150\") ease-out;margin:0}.radio-button-parent input[type=radio]:checked,.radio-button-parent:hover input[type=radio]:checked{background-image:none}.radio-button-parent input[type=radio]:after,.radio-button-parent:hover input[type=radio]:after{content:\"\";position:absolute;top:50%;transform:translateY(-50%);left:theme(\"spacing.3\");border:2px solid;border-color:theme(\"colors.neutral.400\");background:theme(\"colors.light\");height:20px;width:20px;border-radius:100%;transition:all theme(\"duration.75\") ease}.radio-button-parent input[type=radio]:checked:after,.radio-button-parent:hover input[type=radio]:checked:after{background-color:theme(\"colors.secondary.500\");box-shadow:inset 0 0 0 4px theme(\"colors.light\");border-color:theme(\"colors.secondary.500\")!important}.radio-button-parent input[type=radio]:focus,.radio-button-parent input[type=radio]:checked,.radio-button-parent input[type=radio]:checked:focus,.radio-button-parent input[type=radio]:checked:checked,.radio-button-parent input[type=radio]:checked:hover,.radio-button-parent:hover input[type=radio]:focus,.radio-button-parent:hover input[type=radio]:checked,.radio-button-parent:hover input[type=radio]:checked:focus,.radio-button-parent:hover input[type=radio]:checked:checked,.radio-button-parent:hover input[type=radio]:checked:hover{background-color:theme(\"colors.secondary.50\");border-color:theme(\"colors.secondary.500\");outline:none;box-shadow:theme(\"boxShadow.input\")}.radio-button-parent input[type=radio]:hover,.radio-button-parent:hover input[type=radio]:hover{border-color:theme(\"colors.secondary.50\");outline:none;box-shadow:theme(\"boxShadow.input\")}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosRadioComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-radio', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<div (click)=\"check()\" class=\"relative cursor-pointer radio-button-parent\">\r\n  <label\r\n    class=\"absolute ml-2 mt-0 left-8 absolute-center-y leading-20 cursor-pointer z-1\">\r\n    {{ text }}\r\n    <ng-container *ngIf=\"tooltipText.length\">\r\n      <i class=\"fak fa-talos-information tooltip-subject text-neutral-500 text-3 ml-1\">\r\n        <div class=\"tooltip w-350 centre features-tooltip -mt-21\">\r\n          <div class=\"tooltip-content p-4\">\r\n            <p>\r\n              {{ tooltipText }}\r\n            </p>\r\n          </div>\r\n        </div>\r\n      </i>\r\n    </ng-container>\r\n  </label>\r\n  <input\r\n    [checked]=\"isSelected\"\r\n    [id]=\"id\"\r\n    [name]=\"name\"\r\n    type=\"radio\"\r\n    class=\"radio-button w-full h-44 rounded\" />\r\n</div>\r\n", styles: [".radio-button-parent label:hover+input[type=radio],.radio-button-parent:hover label:hover+input[type=radio]{border-color:theme(\"colors.secondary.50\");outline:none;box-shadow:theme(\"boxShadow.input\")}.radio-button-parent input[type=radio],.radio-button-parent:hover input[type=radio]{position:relative;background-color:theme(\"colors.light\");border:theme(\"borderWidth.1\") solid theme(\"colors.neutral.200\");border-radius:theme(\"borderRadius.sm\");cursor:pointer;transition:all theme(\"duration.150\") ease-out;margin:0}.radio-button-parent input[type=radio]:checked,.radio-button-parent:hover input[type=radio]:checked{background-image:none}.radio-button-parent input[type=radio]:after,.radio-button-parent:hover input[type=radio]:after{content:\"\";position:absolute;top:50%;transform:translateY(-50%);left:theme(\"spacing.3\");border:2px solid;border-color:theme(\"colors.neutral.400\");background:theme(\"colors.light\");height:20px;width:20px;border-radius:100%;transition:all theme(\"duration.75\") ease}.radio-button-parent input[type=radio]:checked:after,.radio-button-parent:hover input[type=radio]:checked:after{background-color:theme(\"colors.secondary.500\");box-shadow:inset 0 0 0 4px theme(\"colors.light\");border-color:theme(\"colors.secondary.500\")!important}.radio-button-parent input[type=radio]:focus,.radio-button-parent input[type=radio]:checked,.radio-button-parent input[type=radio]:checked:focus,.radio-button-parent input[type=radio]:checked:checked,.radio-button-parent input[type=radio]:checked:hover,.radio-button-parent:hover input[type=radio]:focus,.radio-button-parent:hover input[type=radio]:checked,.radio-button-parent:hover input[type=radio]:checked:focus,.radio-button-parent:hover input[type=radio]:checked:checked,.radio-button-parent:hover input[type=radio]:checked:hover{background-color:theme(\"colors.secondary.50\");border-color:theme(\"colors.secondary.500\");outline:none;box-shadow:theme(\"boxShadow.input\")}.radio-button-parent input[type=radio]:hover,.radio-button-parent:hover input[type=radio]:hover{border-color:theme(\"colors.secondary.50\");outline:none;box-shadow:theme(\"boxShadow.input\")}\n"] }]
        }], propDecorators: { isSelected: [{
                type: Input
            }], text: [{
                type: Input
            }], id: [{
                type: Input
            }], name: [{
                type: Input
            }], tooltipText: [{
                type: Input
            }], onCheckEvent: [{
                type: Output
            }] } });

class TalosSearchSelectComponent {
    constructor() {
        this.htmlIdentifier = '';
        this.disabled = false;
        this.selectedHasIcon = false;
        this.selectDataArray = [];
        this.selectedMultiName = '';
        this.selectPlaceholder = '';
        this.iconStyles = '';
        this.textMaxLength = 25;
        this.selectItemEvent = new EventEmitter();
        this.isDropdownOpen = false;
        this.errorState = false;
        this.search = '';
        this.dropdownDataArray$ = new BehaviorSubject(null);
        this.selectedText = '';
        this.selectedItem$ = new BehaviorSubject(null);
    }
    ngOnInit() {
        this.dropdownDataArray$.next(this.selectDataArray);
        this.setSelectedItem(this.selectDataArray);
    }
    setSelectedItem(selectDataArray) {
        const selectedItems = selectDataArray.filter(item => item.isActive);
        if (selectedItems.length === 0) {
            this.selectedItem$.next(null);
            this.errorState = true;
            this.selectedText = this.selectPlaceholder;
        }
        else if (selectedItems.length === 1) {
            this.errorState = false;
            this.selectedItem$.next(selectedItems[0]);
        }
        else {
            this.errorState = false;
            this.selectedItem$.next(null);
            this.selectedText = `${selectedItems.length} ${this.selectedMultiName} Selected`;
        }
    }
    selectItem(selectedItem) {
        this.selectDataArray = this.selectDataArray.map(item => item.value === selectedItem.value
            ? { ...item, isActive: !item.isActive }
            : { ...item });
        this.setSelectedItem(this.selectDataArray);
        if (this.search) {
            this.setSearchItemsData();
        }
        else {
            this.dropdownDataArray$.next(this.selectDataArray);
        }
        this.selectItemEvent.emit(this.selectDataArray);
    }
    toggleDropdown($event) {
        this.isDropdownOpen = !this.isDropdownOpen;
    }
    clickOutside($event) {
        this.isDropdownOpen = false;
    }
    clearSearchTerm() {
        this.search = '';
        this.dropdownDataArray$.next(this.selectDataArray);
    }
    clearAllSelected() {
        this.selectDataArray = this.selectDataArray.map(item => {
            return { ...item, isActive: false };
        });
        this.dropdownDataArray$.next(this.selectDataArray);
        this.setSelectedItem(this.selectDataArray);
        this.selectItemEvent.emit(this.selectDataArray);
    }
    onSearchChanged($event) {
        this.search = $event.target.value;
        this.setSearchItemsData();
    }
    setSearchItemsData() {
        this.dropdownDataArray$.next(this.selectDataArray.filter(item => item.value.toLowerCase().includes(this.search.toLowerCase())));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosSearchSelectComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosSearchSelectComponent, isStandalone: false, selector: "talos-search-select", inputs: { htmlIdentifier: "htmlIdentifier", disabled: "disabled", selectedHasIcon: "selectedHasIcon", selectDataArray: "selectDataArray", selectedMultiName: "selectedMultiName", selectPlaceholder: "selectPlaceholder", iconStyles: "iconStyles", textMaxLength: "textMaxLength" }, outputs: { selectItemEvent: "selectItemEvent" }, ngImport: i0, template: "<button\r\n  (click)=\"toggleDropdown($event)\"\r\n  class=\"relative w-full hover:shadow-input p-4 dropdown-parent\"\r\n  [name]=\"htmlIdentifier\"\r\n  [id]=\"htmlIdentifier\"\r\n  [disabled]=\"disabled\"\r\n  [ngClass]=\"{\r\n    'cursor-not-allowed bg-neutral-50 text-neutral-400': disabled,\r\n    'border-error-500': errorState\r\n  }\"\r\n  type=\"button\">\r\n  <div class=\"flex justify-between pr-6\">\r\n    <ng-container\r\n      *ngIf=\"selectedItem$ | async as selectedItem; else selectedTextTemplate\">\r\n      <div class=\"flex\">\r\n        <img src=\"{{ selectedItem.icon }}\" class=\"{{ iconStyles }}\" alt=\"checkbox icon\" />\r\n        <span class=\"block mt-0.5 ml-4 text-ellipsis overflow-hidden whitespace-nowrap\">\r\n          {{ selectedItem.value }}\r\n        </span>\r\n      </div>\r\n    </ng-container>\r\n\r\n    <ng-template #selectedTextTemplate>\r\n      <div class=\"pr-4 text-ellipsis overflow-hidden whitespace-nowrap\">\r\n        <span [ngClass]=\"{ 'text-neutral-400': errorState }\">\r\n          {{ selectedText }}\r\n        </span>\r\n      </div>\r\n    </ng-template>\r\n    <i\r\n      *ngIf=\"errorState\"\r\n      class=\"fa-kit fa-alert absolute absolute-center-y right-9 text-3 text-error-500\"></i>\r\n    <i\r\n      *ngIf=\"!disabled\"\r\n      class=\"far fa-chevron-down absolute absolute-center-y right-5 text-1 text-neutral-500\"></i>\r\n  </div>\r\n  <p\r\n    *ngIf=\"errorState && !isDropdownOpen\"\r\n    class=\"absolute left-0 top-10 text-error-500 text-2\">\r\n    This field is required. Please enter a value.\r\n  </p>\r\n</button>\r\n\r\n<div *ngIf=\"isDropdownOpen\" class=\"dropdown w-[26rem] max-h-[250px] overflow-hidden z-50\">\r\n  <div class=\"relative flex items-center mb-2\">\r\n    <i class=\"fak fa-talos-search absolute left-4 absolute-center-y text-neutral-400\"></i>\r\n    <input\r\n      type=\"search\"\r\n      class=\"w-full block border h-44 p-4 rounded text-3 pl-8\"\r\n      placeholder=\"Search\"\r\n      (input)=\"onSearchChanged($event)\"\r\n      [value]=\"search\" />\r\n    <ng-container *ngIf=\"search != ''\">\r\n      <i class=\"absolute right-4 fa-kit fa-talos-cancel text-2 text-neutral-300\"></i>\r\n      <button\r\n        (click)=\"clearSearchTerm()\"\r\n        type=\"button\"\r\n        class=\"absolute right-4 w-11 h-11\"></button>\r\n    </ng-container>\r\n  </div>\r\n  <div class=\"flex justify-end mb-1\">\r\n    <button (click)=\"clearAllSelected()\" type=\"button\" class=\"text-secondary-750\">\r\n      Clear all\r\n    </button>\r\n  </div>\r\n  <div class=\"max-h-[194px] overflow-x-hidden\">\r\n    <div *ngFor=\"let item of dropdownDataArray$ | async; let index = index\">\r\n      <talos-input-checkbox\r\n        [checked]=\"item.isActive\"\r\n        [entity]=\"item.isActive\"\r\n        [inputName]=\"item.value\"\r\n        [inputLabel]=\"item.value\"\r\n        [icon]=\"item.icon ?? ''\"\r\n        [iconStyles]=\"iconStyles\"\r\n        [title]=\"item.value.length >= textMaxLength ? item.value : ''\"\r\n        (toggleEntityEvent)=\"selectItem(item)\"\r\n        class=\"block pl-5 hover:bg-neutral-50\">\r\n      </talos-input-checkbox>\r\n    </div>\r\n  </div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: TalosInputCheckboxComponent, selector: "talos-input-checkbox", inputs: ["checked", "entity", "inputName", "inputLabel", "isChild", "width", "icon", "iconStyles", "uncheckedIcon", "checkedIcon", "newIcon", "avatar", "initials", "tag"], outputs: ["toggleEntityEvent"] }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosSearchSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-search-select', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<button\r\n  (click)=\"toggleDropdown($event)\"\r\n  class=\"relative w-full hover:shadow-input p-4 dropdown-parent\"\r\n  [name]=\"htmlIdentifier\"\r\n  [id]=\"htmlIdentifier\"\r\n  [disabled]=\"disabled\"\r\n  [ngClass]=\"{\r\n    'cursor-not-allowed bg-neutral-50 text-neutral-400': disabled,\r\n    'border-error-500': errorState\r\n  }\"\r\n  type=\"button\">\r\n  <div class=\"flex justify-between pr-6\">\r\n    <ng-container\r\n      *ngIf=\"selectedItem$ | async as selectedItem; else selectedTextTemplate\">\r\n      <div class=\"flex\">\r\n        <img src=\"{{ selectedItem.icon }}\" class=\"{{ iconStyles }}\" alt=\"checkbox icon\" />\r\n        <span class=\"block mt-0.5 ml-4 text-ellipsis overflow-hidden whitespace-nowrap\">\r\n          {{ selectedItem.value }}\r\n        </span>\r\n      </div>\r\n    </ng-container>\r\n\r\n    <ng-template #selectedTextTemplate>\r\n      <div class=\"pr-4 text-ellipsis overflow-hidden whitespace-nowrap\">\r\n        <span [ngClass]=\"{ 'text-neutral-400': errorState }\">\r\n          {{ selectedText }}\r\n        </span>\r\n      </div>\r\n    </ng-template>\r\n    <i\r\n      *ngIf=\"errorState\"\r\n      class=\"fa-kit fa-alert absolute absolute-center-y right-9 text-3 text-error-500\"></i>\r\n    <i\r\n      *ngIf=\"!disabled\"\r\n      class=\"far fa-chevron-down absolute absolute-center-y right-5 text-1 text-neutral-500\"></i>\r\n  </div>\r\n  <p\r\n    *ngIf=\"errorState && !isDropdownOpen\"\r\n    class=\"absolute left-0 top-10 text-error-500 text-2\">\r\n    This field is required. Please enter a value.\r\n  </p>\r\n</button>\r\n\r\n<div *ngIf=\"isDropdownOpen\" class=\"dropdown w-[26rem] max-h-[250px] overflow-hidden z-50\">\r\n  <div class=\"relative flex items-center mb-2\">\r\n    <i class=\"fak fa-talos-search absolute left-4 absolute-center-y text-neutral-400\"></i>\r\n    <input\r\n      type=\"search\"\r\n      class=\"w-full block border h-44 p-4 rounded text-3 pl-8\"\r\n      placeholder=\"Search\"\r\n      (input)=\"onSearchChanged($event)\"\r\n      [value]=\"search\" />\r\n    <ng-container *ngIf=\"search != ''\">\r\n      <i class=\"absolute right-4 fa-kit fa-talos-cancel text-2 text-neutral-300\"></i>\r\n      <button\r\n        (click)=\"clearSearchTerm()\"\r\n        type=\"button\"\r\n        class=\"absolute right-4 w-11 h-11\"></button>\r\n    </ng-container>\r\n  </div>\r\n  <div class=\"flex justify-end mb-1\">\r\n    <button (click)=\"clearAllSelected()\" type=\"button\" class=\"text-secondary-750\">\r\n      Clear all\r\n    </button>\r\n  </div>\r\n  <div class=\"max-h-[194px] overflow-x-hidden\">\r\n    <div *ngFor=\"let item of dropdownDataArray$ | async; let index = index\">\r\n      <talos-input-checkbox\r\n        [checked]=\"item.isActive\"\r\n        [entity]=\"item.isActive\"\r\n        [inputName]=\"item.value\"\r\n        [inputLabel]=\"item.value\"\r\n        [icon]=\"item.icon ?? ''\"\r\n        [iconStyles]=\"iconStyles\"\r\n        [title]=\"item.value.length >= textMaxLength ? item.value : ''\"\r\n        (toggleEntityEvent)=\"selectItem(item)\"\r\n        class=\"block pl-5 hover:bg-neutral-50\">\r\n      </talos-input-checkbox>\r\n    </div>\r\n  </div>\r\n</div>\r\n" }]
        }], propDecorators: { htmlIdentifier: [{
                type: Input
            }], disabled: [{
                type: Input
            }], selectedHasIcon: [{
                type: Input
            }], selectDataArray: [{
                type: Input
            }], selectedMultiName: [{
                type: Input
            }], selectPlaceholder: [{
                type: Input
            }], iconStyles: [{
                type: Input
            }], textMaxLength: [{
                type: Input
            }], selectItemEvent: [{
                type: Output
            }] } });

class TalosSelectService {
    constructor() {
        this.openDropdowns = new Set();
        this.renderView$ = new Subject();
    }
    isOpen(dropdownId) {
        return this.openDropdowns.has(dropdownId);
    }
    openDropdown(dropdownId) {
        this.openDropdowns.add(dropdownId);
        this.renderView$.next();
    }
    closeDropdown(dropdownId) {
        this.openDropdowns.delete(dropdownId);
        this.renderView$.next();
    }
    closeAllDropdowns() {
        this.openDropdowns.clear();
        this.renderView$.next();
    }
    getAll() {
        return this.openDropdowns.values();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosSelectService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosSelectService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosSelectService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class TalosSelectComponent {
    clickListener(event) {
        if (!this.el.nativeElement.contains(event.target) &&
            this.service.isOpen(this.dropdownId)) {
            this.service.closeAllDropdowns();
        }
    }
    constructor(service, el, cd) {
        this.service = service;
        this.el = el;
        this.cd = cd;
        this.selectedItemText = '';
        this.htmlIdentifier = '';
        this.isLoading = false;
        this.isPositionedAbove = false;
        this.isRelative = false;
        this.heightClass = '';
        this.inputHeightClass = '';
        this.fontSize = '';
        this.roundedClass = '';
        this.disabled = false;
        this.isMulti = false;
        this.allowAny = false;
        this.anyButtonStyles = 'dropdown-button text-left';
        this.hasRightSideIcon = null;
        this.hasLeftSideIcon = undefined;
        this.iconStyles = '';
        this.selectPlaceholder = '';
        // pass in tailwind value to hasLargerDropdownMenu if
        // you want the dropdown menu to be larger than the select box
        this.hasLargerDropdownMenu = null;
        this.width = 'w-full';
        this.hasValidationError = false;
        this.toggleDropdownEvent = new EventEmitter();
        this.cleanDropdownEvent = new EventEmitter();
        this.dropdownId = '';
        this.subscriptions = new Subscription();
        this.generateDropdownId();
        this.subscriptions.add(this.service.renderView$.subscribe(_ => {
            this.cd.markForCheck();
        }));
    }
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
    generateDropdownId() {
        this.dropdownId = 'dropdown_' + Math.random().toString(36).substring(7);
    }
    toggleDropdown($event, isMulti = false) {
        $event.stopPropagation();
        if (isMulti) {
            return;
        }
        if (this.service.isOpen(this.dropdownId)) {
            this.service.closeDropdown(this.dropdownId);
        }
        else {
            this.service.closeAllDropdowns();
            this.service.openDropdown(this.dropdownId);
        }
        this.toggleDropdownEvent.emit(this.service.isOpen(this.dropdownId));
    }
    isOpen(id) {
        return this.service.isOpen(id);
    }
    cleanDropdown($event) {
        this.service.closeDropdown(this.dropdownId);
        this.toggleDropdownEvent.emit(this.service.isOpen(this.dropdownId));
        this.cleanDropdownEvent.emit();
    }
    closeDropdown() {
        this.service.closeDropdown(this.dropdownId);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosSelectComponent, deps: [{ token: TalosSelectService }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosSelectComponent, isStandalone: false, selector: "talos-select", inputs: { selectedItemText: "selectedItemText", htmlIdentifier: "htmlIdentifier", isLoading: "isLoading", isPositionedAbove: "isPositionedAbove", isRelative: "isRelative", heightClass: "heightClass", inputHeightClass: "inputHeightClass", fontSize: "fontSize", roundedClass: "roundedClass", disabled: "disabled", isMulti: "isMulti", allowAny: "allowAny", anyButtonStyles: "anyButtonStyles", hasRightSideIcon: "hasRightSideIcon", hasLeftSideIcon: "hasLeftSideIcon", iconStyles: "iconStyles", selectPlaceholder: "selectPlaceholder", hasLargerDropdownMenu: "hasLargerDropdownMenu", width: "width", hasValidationError: "hasValidationError" }, outputs: { toggleDropdownEvent: "toggleDropdownEvent", cleanDropdownEvent: "cleanDropdownEvent" }, host: { listeners: { "document:click": "clickListener($event)" } }, ngImport: i0, template: "<div [ngClass]=\"{ relative: !hasLargerDropdownMenu }\">\r\n  <button\r\n    (click)=\"toggleDropdown($event)\"\r\n    [ngClass]=\"{\r\n      active: isOpen(dropdownId),\r\n      'bg-neutral-50 text-neutral-400 cursor-not-allowed': disabled,\r\n      'border border-error-500' : hasValidationError\r\n    }\"\r\n    class=\"w-full hover:shadow-input {{ inputHeightClass }} p-4 dropdown-parent {{\r\n      roundedClass\r\n    }}\"\r\n    [name]=\"htmlIdentifier\"\r\n    [id]=\"htmlIdentifier\"\r\n    [disabled]=\"disabled\"\r\n    type=\"button\">\r\n    <div class=\"flex justify-between flex-row\">\r\n      <div class=\"flex\">\r\n        <img\r\n          src=\"{{ hasLeftSideIcon }}\"\r\n          *ngIf=\"!isLoading && !disabled && hasLeftSideIcon\"\r\n          class=\"mr-2 {{ iconStyles }}\" />\r\n\r\n        <div class=\"pr-4\">\r\n          <span [ngClass]=\"[fontSize, 'block', width, 'truncate']\">\r\n            {{ selectedItemText || selectPlaceholder }}\r\n          </span>\r\n        </div>\r\n      </div>\r\n\r\n      <i\r\n        *ngIf=\"!isLoading && !disabled && !hasRightSideIcon\"\r\n        class=\"far fa-chevron-down absolute absolute-center-y right-5 text-1 text-neutral-500\"></i>\r\n      <img\r\n        src=\"{{ hasRightSideIcon }}\"\r\n        *ngIf=\"!isLoading && !disabled && hasRightSideIcon\"\r\n        class=\"text-1 text-dark w-11\" />\r\n    </div>\r\n    <div *ngIf=\"isLoading\" class=\"absolute absolute-center-y right-5 flex items-center\">\r\n      <div class=\"spinner size-5 primary-300\"></div>\r\n    </div>\r\n  </button>\r\n\r\n  <div\r\n    *ngIf=\"isOpen(dropdownId)\"\r\n    (click)=\"toggleDropdown($event, isMulti)\"\r\n    [ngClass]=\"[\r\n      isPositionedAbove ? 'bottom-10 right-none' : '',\r\n      isRelative ? '' : 'top-10 right-none',\r\n      heightClass,\r\n      hasLargerDropdownMenu ? hasLargerDropdownMenu + ' absolute' : 'w-full'\r\n    ]\"\r\n    class=\"dropdown p-2 z-50\">\r\n    <div class=\"max-h-218 overflow-x-hidden\">\r\n      <div class=\"pr-2\">\r\n        <button\r\n          *ngIf=\"allowAny\"\r\n          (click)=\"cleanDropdown($event)\"\r\n          id=\"clean-select-option\"\r\n          class=\"{{ anyButtonStyles }}\"\r\n          type=\"button\">\r\n          Any\r\n        </button>\r\n        <ng-content> </ng-content>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n\r\n<!-- outsideClick\r\n(outsideClicked)=\"clickOutside($event)\" -->\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-select', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<div [ngClass]=\"{ relative: !hasLargerDropdownMenu }\">\r\n  <button\r\n    (click)=\"toggleDropdown($event)\"\r\n    [ngClass]=\"{\r\n      active: isOpen(dropdownId),\r\n      'bg-neutral-50 text-neutral-400 cursor-not-allowed': disabled,\r\n      'border border-error-500' : hasValidationError\r\n    }\"\r\n    class=\"w-full hover:shadow-input {{ inputHeightClass }} p-4 dropdown-parent {{\r\n      roundedClass\r\n    }}\"\r\n    [name]=\"htmlIdentifier\"\r\n    [id]=\"htmlIdentifier\"\r\n    [disabled]=\"disabled\"\r\n    type=\"button\">\r\n    <div class=\"flex justify-between flex-row\">\r\n      <div class=\"flex\">\r\n        <img\r\n          src=\"{{ hasLeftSideIcon }}\"\r\n          *ngIf=\"!isLoading && !disabled && hasLeftSideIcon\"\r\n          class=\"mr-2 {{ iconStyles }}\" />\r\n\r\n        <div class=\"pr-4\">\r\n          <span [ngClass]=\"[fontSize, 'block', width, 'truncate']\">\r\n            {{ selectedItemText || selectPlaceholder }}\r\n          </span>\r\n        </div>\r\n      </div>\r\n\r\n      <i\r\n        *ngIf=\"!isLoading && !disabled && !hasRightSideIcon\"\r\n        class=\"far fa-chevron-down absolute absolute-center-y right-5 text-1 text-neutral-500\"></i>\r\n      <img\r\n        src=\"{{ hasRightSideIcon }}\"\r\n        *ngIf=\"!isLoading && !disabled && hasRightSideIcon\"\r\n        class=\"text-1 text-dark w-11\" />\r\n    </div>\r\n    <div *ngIf=\"isLoading\" class=\"absolute absolute-center-y right-5 flex items-center\">\r\n      <div class=\"spinner size-5 primary-300\"></div>\r\n    </div>\r\n  </button>\r\n\r\n  <div\r\n    *ngIf=\"isOpen(dropdownId)\"\r\n    (click)=\"toggleDropdown($event, isMulti)\"\r\n    [ngClass]=\"[\r\n      isPositionedAbove ? 'bottom-10 right-none' : '',\r\n      isRelative ? '' : 'top-10 right-none',\r\n      heightClass,\r\n      hasLargerDropdownMenu ? hasLargerDropdownMenu + ' absolute' : 'w-full'\r\n    ]\"\r\n    class=\"dropdown p-2 z-50\">\r\n    <div class=\"max-h-218 overflow-x-hidden\">\r\n      <div class=\"pr-2\">\r\n        <button\r\n          *ngIf=\"allowAny\"\r\n          (click)=\"cleanDropdown($event)\"\r\n          id=\"clean-select-option\"\r\n          class=\"{{ anyButtonStyles }}\"\r\n          type=\"button\">\r\n          Any\r\n        </button>\r\n        <ng-content> </ng-content>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n\r\n<!-- outsideClick\r\n(outsideClicked)=\"clickOutside($event)\" -->\r\n" }]
        }], ctorParameters: () => [{ type: TalosSelectService }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }], propDecorators: { selectedItemText: [{
                type: Input
            }], htmlIdentifier: [{
                type: Input
            }], isLoading: [{
                type: Input
            }], isPositionedAbove: [{
                type: Input
            }], isRelative: [{
                type: Input
            }], heightClass: [{
                type: Input
            }], inputHeightClass: [{
                type: Input
            }], fontSize: [{
                type: Input
            }], roundedClass: [{
                type: Input
            }], disabled: [{
                type: Input
            }], isMulti: [{
                type: Input
            }], allowAny: [{
                type: Input
            }], anyButtonStyles: [{
                type: Input
            }], hasRightSideIcon: [{
                type: Input
            }], hasLeftSideIcon: [{
                type: Input
            }], iconStyles: [{
                type: Input
            }], selectPlaceholder: [{
                type: Input
            }], hasLargerDropdownMenu: [{
                type: Input
            }], width: [{
                type: Input
            }], hasValidationError: [{
                type: Input
            }], toggleDropdownEvent: [{
                type: Output
            }], cleanDropdownEvent: [{
                type: Output
            }], clickListener: [{
                type: HostListener,
                args: ['document:click', ['$event']]
            }] } });

class TalosTagComponent {
    constructor() {
        this.text = '';
        this.icon = '';
        this.iconStyles = 'ml-2 text-3 text-primary-500';
        this.tagStyles = 'py-1 px-5 text-primary-500 text-3';
        this.tooltipHeader = '';
        this.tooltipText = '';
        this.tooltipPosition = 'points-bottom-left left-0 -top-10 w-276';
        this.onTagClickEvent = new EventEmitter();
    }
    clickOnTag() {
        this.onTagClickEvent.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosTagComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosTagComponent, isStandalone: false, selector: "talos-tag", inputs: { text: "text", icon: "icon", iconStyles: "iconStyles", tagStyles: "tagStyles", tooltipHeader: "tooltipHeader", tooltipText: "tooltipText", tooltipPosition: "tooltipPosition" }, outputs: { onTagClickEvent: "onTagClickEvent" }, ngImport: i0, template: "<button\n  (click)=\"clickOnTag()\"\n  [id]=\"'talos-tag-' + text\"\n  type=\"button\"\n  class=\"{{ tagStyles }} bg-primary-25 border-1 border-primary-100 rounded-full\">\n  <span>{{ text }}</span>\n  <i *ngIf=\"icon\" class=\"fak {{ icon }} {{ iconStyles }} tooltip-subject\">\n    <div class=\"tooltip {{ tooltipPosition }}\">\n      <div class=\"tooltip-content\">\n        <p *ngIf=\"tooltipHeader\" class=\"tooltip-header\">{{ tooltipHeader }}</p>\n        <p class=\"paragraph-1\">\n          {{ tooltipText }}\n        </p>\n      </div>\n    </div>\n  </i>\n</button>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosTagComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-tag', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<button\n  (click)=\"clickOnTag()\"\n  [id]=\"'talos-tag-' + text\"\n  type=\"button\"\n  class=\"{{ tagStyles }} bg-primary-25 border-1 border-primary-100 rounded-full\">\n  <span>{{ text }}</span>\n  <i *ngIf=\"icon\" class=\"fak {{ icon }} {{ iconStyles }} tooltip-subject\">\n    <div class=\"tooltip {{ tooltipPosition }}\">\n      <div class=\"tooltip-content\">\n        <p *ngIf=\"tooltipHeader\" class=\"tooltip-header\">{{ tooltipHeader }}</p>\n        <p class=\"paragraph-1\">\n          {{ tooltipText }}\n        </p>\n      </div>\n    </div>\n  </i>\n</button>\n" }]
        }], propDecorators: { text: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconStyles: [{
                type: Input
            }], tagStyles: [{
                type: Input
            }], tooltipHeader: [{
                type: Input
            }], tooltipText: [{
                type: Input
            }], tooltipPosition: [{
                type: Input
            }], onTagClickEvent: [{
                type: Output
            }] } });

class TalosUserDetailTooltipComponent {
    constructor() {
        this.userDetail = {
            accountId: 0,
            initials: '',
            name: '',
            jobTitle: '',
            email: '',
            phoneNumber: '',
            avatarColour: {
                textColour: '',
                backgroundColour: '',
            },
            isCandidate: false,
        };
        this.shouldShowTooltip = false;
        this.clickX = 0;
        this.clickY = 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosUserDetailTooltipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosUserDetailTooltipComponent, isStandalone: false, selector: "talos-user-detail-tooltip", inputs: { userDetail: "userDetail", shouldShowTooltip: "shouldShowTooltip", clickX: "clickX", clickY: "clickY" }, ngImport: i0, template: "<custom-tooltip [shouldShow]=\"shouldShowTooltip\" [x]=\"clickX\" [y]=\"clickY\" class=\"block\">\n  <div\n    class=\"text-primary-400 fixed h-auto w-[344px] p-5 bg-light shadow-tooltip rounded-sm border-1 border-neutral-100\">\n    <div class=\"flex\">\n      <div class=\"pr-5\" [style.padding-top]=\"'3px'\">\n        <div\n          [ngStyle]=\"{\n            color: userDetail.avatarColour.textColour,\n            background: userDetail.avatarColour.backgroundColour\n          }\"\n          class=\"flex justify-center items-center w-[32px] h-[32px] rounded-full\">\n          <p class=\"font-medium mt-[1px] ml-[0,5px]\">\n            {{ userDetail.initials }}\n          </p>\n        </div>\n      </div>\n      <div class=\"flex flex-col\">\n        <ng-container *ngIf=\"userDetail.isCandidate\">\n          <p class=\"text-2 text-neutral-500\">Candidate</p>\n          <p class=\"text-dark font-medium text-3\">\n            {{ userDetail.name }}\n          </p>\n        </ng-container>\n\n        <ng-container *ngIf=\"!userDetail.isCandidate\">\n          <p class=\"text-dark font-medium text-3\">\n            {{ userDetail.name }}\n          </p>\n          <p class=\"text-neutral-500 text-3\">\n            {{ userDetail.jobTitle }}\n          </p>\n          <a [href]=\"'mailto:' + userDetail.email\" class=\"text-3 mt-2 text-secondary-750\">\n            <div class=\"flex items-center\">\n              <i class=\"text-4 fa-kit fa-talos-envelope text-neutral-500 mr-2\"></i>\n              <span class=\"hover:underline\">{{ userDetail.email }}</span>\n            </div>\n          </a>\n          <div *ngIf=\"userDetail.phoneNumber.length > 0\">\n            <a\n              [href]=\"'tel:' + userDetail.phoneNumber\"\n              class=\"text-3 mt-2 text-secondary-750\">\n              <i class=\"fa-kit fa-talos-mobile text-neutral-500 mr-2\"></i>\n              <span>{{ userDetail.phoneNumber }}</span>\n            </a>\n          </div>\n        </ng-container>\n      </div>\n    </div>\n  </div>\n</custom-tooltip>\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: CustomTooltipComponent, selector: "custom-tooltip", inputs: ["x", "y", "delayClose", "shouldShow"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosUserDetailTooltipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-user-detail-tooltip', standalone: false, template: "<custom-tooltip [shouldShow]=\"shouldShowTooltip\" [x]=\"clickX\" [y]=\"clickY\" class=\"block\">\n  <div\n    class=\"text-primary-400 fixed h-auto w-[344px] p-5 bg-light shadow-tooltip rounded-sm border-1 border-neutral-100\">\n    <div class=\"flex\">\n      <div class=\"pr-5\" [style.padding-top]=\"'3px'\">\n        <div\n          [ngStyle]=\"{\n            color: userDetail.avatarColour.textColour,\n            background: userDetail.avatarColour.backgroundColour\n          }\"\n          class=\"flex justify-center items-center w-[32px] h-[32px] rounded-full\">\n          <p class=\"font-medium mt-[1px] ml-[0,5px]\">\n            {{ userDetail.initials }}\n          </p>\n        </div>\n      </div>\n      <div class=\"flex flex-col\">\n        <ng-container *ngIf=\"userDetail.isCandidate\">\n          <p class=\"text-2 text-neutral-500\">Candidate</p>\n          <p class=\"text-dark font-medium text-3\">\n            {{ userDetail.name }}\n          </p>\n        </ng-container>\n\n        <ng-container *ngIf=\"!userDetail.isCandidate\">\n          <p class=\"text-dark font-medium text-3\">\n            {{ userDetail.name }}\n          </p>\n          <p class=\"text-neutral-500 text-3\">\n            {{ userDetail.jobTitle }}\n          </p>\n          <a [href]=\"'mailto:' + userDetail.email\" class=\"text-3 mt-2 text-secondary-750\">\n            <div class=\"flex items-center\">\n              <i class=\"text-4 fa-kit fa-talos-envelope text-neutral-500 mr-2\"></i>\n              <span class=\"hover:underline\">{{ userDetail.email }}</span>\n            </div>\n          </a>\n          <div *ngIf=\"userDetail.phoneNumber.length > 0\">\n            <a\n              [href]=\"'tel:' + userDetail.phoneNumber\"\n              class=\"text-3 mt-2 text-secondary-750\">\n              <i class=\"fa-kit fa-talos-mobile text-neutral-500 mr-2\"></i>\n              <span>{{ userDetail.phoneNumber }}</span>\n            </a>\n          </div>\n        </ng-container>\n      </div>\n    </div>\n  </div>\n</custom-tooltip>\n" }]
        }], propDecorators: { userDetail: [{
                type: Input
            }], shouldShowTooltip: [{
                type: Input
            }], clickX: [{
                type: Input
            }], clickY: [{
                type: Input
            }] } });

class TalosHidingTooltipDirective {
    constructor(elementRef, renderer) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.hidingTooltip = '';
        this.tooltipClasses = {};
        this.tooltip = null;
        this.TABLET_VIEWPORT = 768;
    }
    showTooltip($event) {
        const isMobileViewport = window.innerWidth <= this.TABLET_VIEWPORT;
        if (!this.tooltip && !isMobileViewport) {
            this.tooltip = this.renderer.createElement('div');
            this.tooltip.textContent = this.hidingTooltip;
            this.renderer.addClass(this.tooltip, 'hiding-tooltip');
            this.renderer.addClass(this.tooltip, 'active');
            const tooltipPosition = this.showElementOnTop($event) ? 'onTop' : 'onBottom';
            const classesArray = this.tooltipClasses[tooltipPosition]
                .split(' ')
                .filter(className => !!className);
            this.addClassFromArray(this.tooltip, classesArray);
            if (!this.showElementOnTop($event)) {
                this.renderer.addClass(this.tooltip, 'bottom');
            }
            this.renderer.appendChild(this.elementRef.nativeElement, this.tooltip);
            setTimeout(() => {
                this.hideTooltip();
            }, 500);
        }
    }
    hideTooltip() {
        if (this.tooltip) {
            this.renderer.removeChild(this.elementRef.nativeElement, this.tooltip);
            this.tooltip = null;
        }
    }
    addClassFromArray(element, classes) {
        classes.forEach(className => {
            this.renderer.addClass(element, className);
        });
    }
    showElementOnTop($mouseEvent) {
        const buttonElement = $mouseEvent.currentTarget;
        if (this.outerContainer) {
            const buttonRect = buttonElement.getBoundingClientRect();
            const containerRect = this.outerContainer.getBoundingClientRect();
            const distanceFromTop = Math.round(buttonRect.top - containerRect.top);
            return distanceFromTop > 60;
        }
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosHidingTooltipDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.6", type: TalosHidingTooltipDirective, isStandalone: false, selector: "[hidingTooltip]", inputs: { hidingTooltip: "hidingTooltip", tooltipClasses: "tooltipClasses", outerContainer: "outerContainer" }, host: { listeners: { "mouseenter": "showTooltip($event)", "mouseleave": "hideTooltip()" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosHidingTooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[hidingTooltip]',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { hidingTooltip: [{
                type: Input
            }], tooltipClasses: [{
                type: Input
            }], outerContainer: [{
                type: Input
            }], showTooltip: [{
                type: HostListener,
                args: ['mouseenter', ['$event']]
            }], hideTooltip: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });

var AtsErrorCodes;
(function (AtsErrorCodes) {
    AtsErrorCodes["CPB_API_50"] = "CpbAPI_50";
})(AtsErrorCodes || (AtsErrorCodes = {}));

var FileTypes;
(function (FileTypes) {
    FileTypes["JPEG"] = "image/jpeg";
    FileTypes["JPG"] = "image/jpg";
    FileTypes["PNG"] = "image/png";
    FileTypes["ICO"] = "image/x-icon";
})(FileTypes || (FileTypes = {}));

class FileReaderService {
    getFileAsBase64(file) {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(file);
        return new Observable((observer) => {
            fileReader.onload = (event) => {
                if (!event || !event.target) {
                    observer.error({
                        error: 'An error occurred uploading the file. Please contact your administrator',
                    });
                }
                else {
                    const fileAsBase64 = event.target.result;
                    observer.next({
                        fileAsBase64: fileAsBase64,
                        file: file,
                    });
                }
                observer.complete();
            };
            fileReader.onerror = (error) => {
                observer.error(error);
            };
        });
    }
    getFile(fileList) {
        const files = Array.from(fileList);
        const file = files[0];
        if (!file) {
            throw Error('An error occurred uploading the file.');
        }
        return file;
    }
    convertToReadableFormat(fileSizeBytes) {
        const uomPrefix = fileSizeBytes > 0 ? Math.floor(Math.log2(fileSizeBytes) / 10) : 0;
        const rank = (uomPrefix > 0 ? 'KM'[uomPrefix - 1] : '') + 'B';
        const count = fileSizeBytes > 0 ? Math.floor(fileSizeBytes / Math.pow(1024, uomPrefix)) : 0;
        return count + rank;
    }
    getFileTypeFriendlyName(fileType) {
        switch (fileType) {
            case FileTypes.JPEG:
                return 'JPEG';
            case FileTypes.JPG:
                return 'JPG';
            case FileTypes.PNG:
                return 'PNG';
            case FileTypes.ICO:
                return 'ICO';
            default:
                return fileType;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: FileReaderService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: FileReaderService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: FileReaderService, decorators: [{
            type: Injectable
        }] });

class SingleImageUploadElement {
    constructor(fileReaderService, httpClient) {
        this.fileReaderService = fileReaderService;
        this.httpClient = httpClient;
        this.imageUrl = '';
        this.uploadButtonText = 'Upload image';
        this.endpointImageName = 'image';
        this.endpoint = '';
        this.customValidFileTypes = '';
        this.maxFileSize = 1048576; //1MB
        this.onImageUploadedEvent = new EventEmitter();
        this.onImageUploadErrorEvent = new EventEmitter();
        this.onImageAlreadyExistsErrorEvent = new EventEmitter();
        this.validDefaultFileTypes = [FileTypes.JPEG, FileTypes.JPG, FileTypes.PNG];
        this.uploadProgress = 0;
        this.isUploading = false;
        this.validFileTypes = this.validDefaultFileTypes;
        this.onDestroy$ = new Subject();
    }
    ngOnDestroy() {
        this.onDestroy$.next();
    }
    uploadFile(event) {
        const file = this.getFile(event);
        this.tryUploadFile(file, this.endpoint);
    }
    tryUploadFile(originalFile, endpoint) {
        this.fileReference.nativeElement.value = '';
        of(originalFile)
            .pipe(mergeMap(file => this.validateFile(file)), map(file => this.createFormData(file)), tap(() => (this.isUploading = true)), concatMap(fileForm => this.uploadImage(fileForm, endpoint).pipe(tap(progress => this.setUploadProgress(progress)))), filter(response => response.type === HttpEventType.Response), map(response => response), tap(response => this.onImageUploadedEvent.emit(response.body)), tap(() => (this.isUploading = false)), catchError(errors => this.handleErrors(errors, originalFile)), takeUntil(this.onDestroy$))
            .subscribe();
    }
    getFile(event) {
        return this.fileReaderService.getFile(event.target.files);
    }
    validateFile(file) {
        if (this.customValidFileTypes) {
            this.validFileTypes = this.customValidFileTypes
                .split(',')
                .map(fileType => fileType);
        }
        if (!this.validFileTypes.includes(file.type)) {
            this.onImageUploadErrorEvent.emit([
                `Invalid File Format`,
                `Please upload a file in one of the following formats: ${this.getValidFileTypeErrorString()}. (${this.fileReaderService.convertToReadableFormat(this.maxFileSize)} max)`,
            ]);
            return EMPTY;
        }
        if (file.size > this.maxFileSize) {
            this.onImageUploadErrorEvent.emit([
                `Invalid File Size`,
                `Please upload a file which is no larger than ${this.fileReaderService.convertToReadableFormat(this.maxFileSize)}.`,
            ]);
            return EMPTY;
        }
        return of(file);
    }
    getValidFileTypeErrorString() {
        return this.validFileTypes
            .map(fileTypes => this.fileReaderService.getFileTypeFriendlyName(fileTypes))
            .join(', ');
    }
    createFormData(file) {
        const form = new FormData();
        form.append(this.endpointImageName, file);
        return form;
    }
    uploadImage(fileForm, endpoint) {
        return this.httpClient.post(endpoint, fileForm, {
            reportProgress: true,
            observe: 'events',
        });
    }
    setUploadProgress(progress) {
        if (progress.type === HttpEventType.UploadProgress && progress.total) {
            this.uploadProgress = Math.round((progress.loaded / progress.total) * 100);
        }
    }
    handleErrors(errors, file) {
        if (errors.length > 0 && errors[0].errorCode === AtsErrorCodes.CPB_API_50) {
            this.onImageAlreadyExistsErrorEvent.emit((endpoint) => this.tryUploadFile(file, endpoint));
            this.isUploading = false;
        }
        return of(errors);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: SingleImageUploadElement, deps: [{ token: FileReaderService }, { token: i2$1.HttpClient }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: SingleImageUploadElement, isStandalone: false, selector: "single-image-upload-element", inputs: { imageUrl: ["image-url", "imageUrl"], uploadButtonText: ["upload-button-text", "uploadButtonText"], endpointImageName: ["endpoint-image-name", "endpointImageName"], endpoint: "endpoint", customValidFileTypes: ["custom-valid-file-types", "customValidFileTypes"], maxFileSize: ["max-file-size-kb", "maxFileSize"] }, outputs: { onImageUploadedEvent: "onImageUploadedEvent", onImageUploadErrorEvent: "onImageUploadErrorEvent", onImageAlreadyExistsErrorEvent: "onImageAlreadyExistsErrorEvent" }, viewQueries: [{ propertyName: "fileReference", first: true, predicate: ["file"], descendants: true }], ngImport: i0, template: "<!--TODO: Replace w-310 with w-full after MVP release-->\n<div\n  class=\"w-310 relative flex items-center justify-center h-100 overflow-hidden bg-light border-1 border-neutral-200 shadow-300 rounded\">\n  <div\n    [ngStyle]=\"{\n     'background-image': imageUrl | backgroundImageUrl\n  }\"\n    class=\"h-4/5 w-2/5 bg-no-repeat bg-center bg-contain\"></div>\n\n  <div *ngIf=\"isUploading\" class=\"absolute bottom-0 left-0 w-full\">\n    <div class=\"h-PROGRESS bg-neutral-75 w-full\">\n      <div\n        [style]=\"'width:' + uploadProgress + '%'\"\n        class=\"bg-secondary-300 h-PROGRESS transition-all\"></div>\n    </div>\n  </div>\n</div>\n\n<button\n  (click)=\"file.click()\"\n  class=\"primary px-6 py-4 mt-5\"\n  id=\"single-image-upload-element\"\n  type=\"button\">\n  <span> {{uploadButtonText}} </span>\n</button>\n\n<input\n  (input)=\"uploadFile($event)\"\n  #file\n  class=\"hidden\"\n  type=\"file\"\n  accept=\".png, .jpg, jpeg, .ico\"\n  name=\"single-image-upload-element\" />\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: BackgroundImageUrlPipe, name: "backgroundImageUrl" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: SingleImageUploadElement, decorators: [{
            type: Component,
            args: [{ selector: 'single-image-upload-element', standalone: false, template: "<!--TODO: Replace w-310 with w-full after MVP release-->\n<div\n  class=\"w-310 relative flex items-center justify-center h-100 overflow-hidden bg-light border-1 border-neutral-200 shadow-300 rounded\">\n  <div\n    [ngStyle]=\"{\n     'background-image': imageUrl | backgroundImageUrl\n  }\"\n    class=\"h-4/5 w-2/5 bg-no-repeat bg-center bg-contain\"></div>\n\n  <div *ngIf=\"isUploading\" class=\"absolute bottom-0 left-0 w-full\">\n    <div class=\"h-PROGRESS bg-neutral-75 w-full\">\n      <div\n        [style]=\"'width:' + uploadProgress + '%'\"\n        class=\"bg-secondary-300 h-PROGRESS transition-all\"></div>\n    </div>\n  </div>\n</div>\n\n<button\n  (click)=\"file.click()\"\n  class=\"primary px-6 py-4 mt-5\"\n  id=\"single-image-upload-element\"\n  type=\"button\">\n  <span> {{uploadButtonText}} </span>\n</button>\n\n<input\n  (input)=\"uploadFile($event)\"\n  #file\n  class=\"hidden\"\n  type=\"file\"\n  accept=\".png, .jpg, jpeg, .ico\"\n  name=\"single-image-upload-element\" />\n" }]
        }], ctorParameters: () => [{ type: FileReaderService }, { type: i2$1.HttpClient }], propDecorators: { imageUrl: [{
                type: Input,
                args: ['image-url']
            }], uploadButtonText: [{
                type: Input,
                args: ['upload-button-text']
            }], endpointImageName: [{
                type: Input,
                args: ['endpoint-image-name']
            }], endpoint: [{
                type: Input,
                args: ['endpoint']
            }], customValidFileTypes: [{
                type: Input,
                args: ['custom-valid-file-types']
            }], maxFileSize: [{
                type: Input,
                args: ['max-file-size-kb']
            }], onImageUploadedEvent: [{
                type: Output
            }], onImageUploadErrorEvent: [{
                type: Output
            }], onImageAlreadyExistsErrorEvent: [{
                type: Output
            }], fileReference: [{
                type: ViewChild,
                args: ['file']
            }] } });

var ToastTypes;
(function (ToastTypes) {
    ToastTypes[ToastTypes["SUCCESS"] = 0] = "SUCCESS";
    ToastTypes[ToastTypes["ERROR"] = 1] = "ERROR";
    ToastTypes[ToastTypes["WARNING"] = 2] = "WARNING";
})(ToastTypes || (ToastTypes = {}));

class ToastEvents {
    static { this.TOAST_EVENT_NAME = 'toast-event'; }
    static dispatchError(title, description, errorCode) {
        const event = {
            type: ToastTypes.ERROR,
            title: title,
            description: description,
            errorCode: errorCode ?? '',
        };
        dispatchEvent(new CustomEvent(this.TOAST_EVENT_NAME, {
            bubbles: true,
            detail: event,
        }));
    }
    static dispatchWarning(title, description) {
        const event = {
            type: ToastTypes.WARNING,
            title: title,
            description: description,
        };
        dispatchEvent(new CustomEvent(this.TOAST_EVENT_NAME, {
            bubbles: true,
            detail: event,
        }));
    }
    static dispatchSuccess(title, description) {
        const event = {
            type: ToastTypes.SUCCESS,
            title: title,
            description: description,
        };
        dispatchEvent(new CustomEvent(this.TOAST_EVENT_NAME, {
            bubbles: true,
            detail: event,
        }));
    }
}

class ToastElement {
    constructor() {
        this.onDestroy$ = new Subject();
        this.toasts = [];
        fromEvent(window, ToastEvents.TOAST_EVENT_NAME)
            .pipe(map(event => this.generateModel(event)), tap(toast => (this.toasts = [...this.toasts, toast])), filter(toast => toast.type === ToastTypes.SUCCESS), delay(5000), tap(toast => (this.toasts = this.toasts.filter(t => t.id !== toast.id))), takeUntil(this.onDestroy$))
            .subscribe();
    }
    generateModel(event) {
        const theme = getTheme(event.detail.type);
        return {
            id: v4(),
            type: event.detail.type,
            title: event.detail.title,
            description: event.detail.description,
            backgroundColour: this.getBackgroundColour(theme),
            shouldShowClose: theme !== Themes.success,
            borderColour: this.getBorderColour(theme),
            icon: this.getIcon(theme),
            iconColour: this.getIconColour(theme),
            colour: 'text-text',
            errorCode: event.detail.errorCode,
        };
        function getTheme(toastType) {
            switch (toastType) {
                case ToastTypes.SUCCESS:
                    return Themes.success;
                case ToastTypes.WARNING:
                    return Themes.warning;
                case ToastTypes.ERROR:
                    return Themes.error;
                default:
                    throw Error(`Unexpected ToastType implemented when trying to get theme: ${toastType}`);
            }
        }
    }
    ngOnDestroy() {
        this.onDestroy$.next();
    }
    close(toast) {
        this.toasts = this.toasts.filter(t => t.id !== toast.id);
    }
    getBackgroundColour(alertType) {
        switch (alertType) {
            case Themes.warning:
                return 'bg-warning-50';
            case Themes.error:
                return 'bg-error-50';
            case Themes.success:
                return 'bg-secondary-50';
            case Themes.warningAlt:
            case Themes.errorAlt:
            case Themes.successAlt:
                return 'bg-light';
            default:
                throw Error(`Theme "${alertType}" has no implementation for background colour`);
        }
    }
    getBorderColour(alertType) {
        switch (alertType) {
            case Themes.error:
            case Themes.errorAlt:
                return 'border-error-400';
            case Themes.success:
            case Themes.successAlt:
                return 'border-success-400';
            case Themes.warning:
            case Themes.warningAlt:
                return 'border-warning-500';
            default:
                throw Error(`Theme "${alertType}" has no implementation for border colour`);
        }
    }
    getIconColour(alertType) {
        switch (alertType) {
            case Themes.error:
            case Themes.errorAlt:
                return 'bg-error-400';
            case Themes.success:
            case Themes.successAlt:
                return 'bg-success-400';
            case Themes.warning:
            case Themes.warningAlt:
                return 'bg-warning-500';
            default:
                throw Error(`Theme "${alertType}" has no implementation for text colour`);
        }
    }
    getIcon(alertType) {
        switch (alertType) {
            case Themes.error:
            case Themes.errorAlt:
                return 'error mask-22';
            case Themes.warning:
            case Themes.warningAlt:
                return 'warning';
            case Themes.success:
            case Themes.successAlt:
                return 'success';
            default:
                throw Error(`Theme "${alertType}" has no implementation for icon`);
        }
    }
    trackByToastId(index, toast) {
        return toast.id;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ToastElement, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: ToastElement, isStandalone: false, selector: "app-toast-element", ngImport: i0, template: "<div class=\"fixed laptop:right-8 z-TOOLTIP w-310 bottom-5\">\r\n  <ng-container *ngFor=\"let toast of toasts; trackBy:trackByToastId;\">\r\n    <div\r\n      [@slideFromRight]\r\n      [ngClass]=\"[toast.borderColour, toast.backgroundColour]\"\r\n      class=\"w-310 border-1 rounded shadow py-4 px-6 mb-4 transition-all\"\r\n      role=\"alert\">\r\n      <div class=\"flex items-start\">\r\n        <div [ngClass]=\"[toast.icon, toast.iconColour]\" class=\"w-18 h-18 shrink-0\"></div>\r\n        <div class=\"ml-5\">\r\n          <div class=\"text-3 font-bold\">{{toast.title}}</div>\r\n          <p class=\"text-3\">{{toast.description}}</p>\r\n          <p *ngIf=\"toast.errorCode\" class=\"mt-2\">\r\n            <span class=\"text-error-500\"> Error code: </span>\r\n            <span class=\"text-error-500 font-medium\"> {{toast.errorCode}} </span>\r\n          </p>\r\n        </div>\r\n        <button\r\n          *ngIf=\"toast.shouldShowClose\"\r\n          (click)=\"close(toast)\"\r\n          class=\"ml-auto\"\r\n          type=\"button\">\r\n          <div class=\"h-11 w-11 close bg-text shrink-0\"></div>\r\n        </button>\r\n      </div>\r\n    </div>\r\n  </ng-container>\r\n</div>\r\n", styles: [".mask-22{-webkit-mask-size:22px!important;mask-size:22px!important}@tailwind base;@tailwind components;@tailwind utilities;img.text-error-400 svg{color:theme(\"colors.error.400\")}.success{-webkit-mask:url(/App/1/assets/success-round.svg) no-repeat center;mask:url(/App/1/assets/success-round.svg) no-repeat center}.warning{-webkit-mask:url(/App/1/assets/exclamation-triangle.svg) no-repeat center;mask:url(/App/1/assets/exclamation-triangle.svg) no-repeat center}.error{-webkit-mask:url(/App/1/assets/exclamation-round.svg) no-repeat center;mask:url(/App/1/assets/exclamation-round.svg) no-repeat center}.close{-webkit-mask:url(/App/1/assets/cross.svg) no-repeat center;mask:url(/App/1/assets/cross.svg) no-repeat center}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [slideFromRight], encapsulation: i0.ViewEncapsulation.ShadowDom }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: ToastElement, decorators: [{
            type: Component,
            args: [{ selector: 'app-toast-element', animations: [slideFromRight], encapsulation: ViewEncapsulation.ShadowDom, standalone: false, template: "<div class=\"fixed laptop:right-8 z-TOOLTIP w-310 bottom-5\">\r\n  <ng-container *ngFor=\"let toast of toasts; trackBy:trackByToastId;\">\r\n    <div\r\n      [@slideFromRight]\r\n      [ngClass]=\"[toast.borderColour, toast.backgroundColour]\"\r\n      class=\"w-310 border-1 rounded shadow py-4 px-6 mb-4 transition-all\"\r\n      role=\"alert\">\r\n      <div class=\"flex items-start\">\r\n        <div [ngClass]=\"[toast.icon, toast.iconColour]\" class=\"w-18 h-18 shrink-0\"></div>\r\n        <div class=\"ml-5\">\r\n          <div class=\"text-3 font-bold\">{{toast.title}}</div>\r\n          <p class=\"text-3\">{{toast.description}}</p>\r\n          <p *ngIf=\"toast.errorCode\" class=\"mt-2\">\r\n            <span class=\"text-error-500\"> Error code: </span>\r\n            <span class=\"text-error-500 font-medium\"> {{toast.errorCode}} </span>\r\n          </p>\r\n        </div>\r\n        <button\r\n          *ngIf=\"toast.shouldShowClose\"\r\n          (click)=\"close(toast)\"\r\n          class=\"ml-auto\"\r\n          type=\"button\">\r\n          <div class=\"h-11 w-11 close bg-text shrink-0\"></div>\r\n        </button>\r\n      </div>\r\n    </div>\r\n  </ng-container>\r\n</div>\r\n", styles: [".mask-22{-webkit-mask-size:22px!important;mask-size:22px!important}@tailwind base;@tailwind components;@tailwind utilities;img.text-error-400 svg{color:theme(\"colors.error.400\")}.success{-webkit-mask:url(/App/1/assets/success-round.svg) no-repeat center;mask:url(/App/1/assets/success-round.svg) no-repeat center}.warning{-webkit-mask:url(/App/1/assets/exclamation-triangle.svg) no-repeat center;mask:url(/App/1/assets/exclamation-triangle.svg) no-repeat center}.error{-webkit-mask:url(/App/1/assets/exclamation-round.svg) no-repeat center;mask:url(/App/1/assets/exclamation-round.svg) no-repeat center}.close{-webkit-mask:url(/App/1/assets/cross.svg) no-repeat center;mask:url(/App/1/assets/cross.svg) no-repeat center}\n"] }]
        }], ctorParameters: () => [] });

class GuardDeactivateService {
    invokeActionsWhenDeactivating(store, isDirty, actions) {
        return of(isDirty).pipe(map(isDirty => isDirty
            ? [
                confirm('Are you sure you want to navigate away and lose your changes?'),
                isDirty,
            ]
            : [false, isDirty] // don't reload data, no unsaved changes
        ), map(([shouldInvokeActions, isDirty]) => shouldInvokeActions ? invokeActionsAndDeactivate(store) : canDeactivate(isDirty)));
        function invokeActionsAndDeactivate(store) {
            actions.forEach(action => store.dispatch(action()));
            return true;
        }
        function canDeactivate(isDirty) {
            if (isDirty) {
                return false; // user has unsaved changes and pressed cancel on "are you sure you want to leave?"
            }
            return true; // user has no unsaved changes and is free to leave
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: GuardDeactivateService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: GuardDeactivateService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: GuardDeactivateService, decorators: [{
            type: Injectable
        }] });

class TalosDrawerComponent {
    onResize() {
        if (this.isTabletOnwards() && this.openBurgerMenu) {
            this.openBurgerMenu = false;
        }
    }
    constructor(cd) {
        this.cd = cd;
        this.openDrawer = false;
        this.openBurgerMenu = false;
        this.subtitle = null;
        this.openDrawerChange = new EventEmitter();
        this.subscription = new Subscription();
    }
    ngOnInit() {
        this.closeMobileMenuOnNavClick();
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    closeMobileMenuOnNavClick() {
        this.subscription.add(this.navItemClicked.subscribe(_ => {
            if (!this.isTabletOnwards() && this.openBurgerMenu) {
                this.toggleBurgerMenu();
                this.cd.markForCheck();
            }
        }));
    }
    toggleDrawer() {
        this.openDrawer = !this.openDrawer;
        this.openDrawerChange.emit(this.openDrawer);
    }
    toggleBurgerMenu() {
        this.openBurgerMenu = !this.openBurgerMenu;
    }
    isTabletOnwards() {
        const tabletWidth = 768;
        return window.innerWidth >= tabletWidth;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosDrawerComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosDrawerComponent, isStandalone: false, selector: "talos-drawer", inputs: { openDrawer: "openDrawer", openBurgerMenu: "openBurgerMenu", navItemClicked: "navItemClicked", subtitle: "subtitle" }, outputs: { openDrawerChange: "openDrawerChange" }, host: { listeners: { "window:resize": "onResize()" } }, ngImport: i0, template: "<!-- overlay -->\r\n<div\r\n  *ngIf=\"openDrawer\"\r\n  class=\"fixed top-0 h-screen w-full z-10 bg-black bg-opacity-25\"\r\n  [@fadeOut]=\"{ value: openDrawer, params: { duration: '.5s' } }\"\r\n  (click)=\"toggleDrawer()\"></div>\r\n<!-- drawer -->\r\n<div\r\n  *ngIf=\"openDrawer\"\r\n  class=\"fixed top-0 right-0 z-10 w-full tablet:w-[83rem] h-screen bg-white flex\"\r\n  [@slideFromRight]=\"openDrawer\">\r\n  <!-- left menu -->\r\n  <div\r\n    id=\"left-column\"\r\n    class=\"pt-8 hidden tablet:flex border-r border-neutral-100 w-[201px] flex-shrink-0 flex-col items-start\">\r\n    <!-- slot to project menu items -->\r\n    <ng-content select=\"[menu]\"></ng-content>\r\n  </div>\r\n\r\n  <!-- right title and content -->\r\n  <div class=\"flex flex-col flex-grow\">\r\n    <!-- header div (fixed) -->\r\n    <div id=\"fixed-header\" class=\"pt-0 pr-8 pb-0 pl-8 bg-white z-20\">\r\n      <div\r\n        class=\"flex\"\r\n        [ngClass]=\"{\r\n          'shadow-edgeShadow border-b border-neutral-200': subtitle == 'Talent Analytics'\r\n        }\">\r\n        <!-- header component -->\r\n        <div id=\"header\" class=\"flex-grow\">\r\n          <ng-content *ngIf=\"!openBurgerMenu\" select=\"[header]\"></ng-content>\r\n        </div>\r\n        <!-- buttons -->\r\n        <div id=\"buttons\" class=\"flex pt-8\">\r\n          <!-- button to toggle the mobile menu -->\r\n          <button\r\n            (click)=\"toggleBurgerMenu()\"\r\n            class=\"tablet:hidden w-[2.2rem] h-[2.2rem] p-2 mr-2\"\r\n            *ngIf=\"!openBurgerMenu\">\r\n            <i class=\"fak fa-menu-closed text-6 text-neutral-500\"></i>\r\n          </button>\r\n          <!-- button toggle drawer -->\r\n          <ng-container *ngIf=\"!openBurgerMenu\">\r\n            <button (click)=\"toggleDrawer()\" class=\"w-[2.2rem] h-[2.2rem] p-2 ml-2\">\r\n              <i class=\"fa-kit fa-talos-cross text-neutral-500 text-1\"></i>\r\n            </button>\r\n          </ng-container>\r\n          <ng-container *ngIf=\"openBurgerMenu\">\r\n            <button (click)=\"toggleBurgerMenu()\" class=\"w-[2.2rem] h-[2.2rem] p-2 ml-2\">\r\n              <i class=\"fa-kit fa-talos-cross text-neutral-500\"></i>\r\n            </button>\r\n          </ng-container>\r\n        </div>\r\n      </div>\r\n    </div>\r\n\r\n    <!-- scrollable content container -->\r\n    <div class=\"flex-grow overflow-y-auto\">\r\n      <ng-container *ngIf=\"!openBurgerMenu; else showContent\">\r\n        <!-- slot to project content -->\r\n        <ng-content select=\"[content]\"></ng-content>\r\n      </ng-container>\r\n      <ng-template #showContent>\r\n        <!-- show mobile menu -->\r\n        <ng-content select=\"[mobile-menu]\"></ng-content>\r\n      </ng-template>\r\n    </div>\r\n  </div>\r\n</div>\r\n", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [slideFromRight, fadeOut], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosDrawerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-drawer', animations: [slideFromRight, fadeOut], changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<!-- overlay -->\r\n<div\r\n  *ngIf=\"openDrawer\"\r\n  class=\"fixed top-0 h-screen w-full z-10 bg-black bg-opacity-25\"\r\n  [@fadeOut]=\"{ value: openDrawer, params: { duration: '.5s' } }\"\r\n  (click)=\"toggleDrawer()\"></div>\r\n<!-- drawer -->\r\n<div\r\n  *ngIf=\"openDrawer\"\r\n  class=\"fixed top-0 right-0 z-10 w-full tablet:w-[83rem] h-screen bg-white flex\"\r\n  [@slideFromRight]=\"openDrawer\">\r\n  <!-- left menu -->\r\n  <div\r\n    id=\"left-column\"\r\n    class=\"pt-8 hidden tablet:flex border-r border-neutral-100 w-[201px] flex-shrink-0 flex-col items-start\">\r\n    <!-- slot to project menu items -->\r\n    <ng-content select=\"[menu]\"></ng-content>\r\n  </div>\r\n\r\n  <!-- right title and content -->\r\n  <div class=\"flex flex-col flex-grow\">\r\n    <!-- header div (fixed) -->\r\n    <div id=\"fixed-header\" class=\"pt-0 pr-8 pb-0 pl-8 bg-white z-20\">\r\n      <div\r\n        class=\"flex\"\r\n        [ngClass]=\"{\r\n          'shadow-edgeShadow border-b border-neutral-200': subtitle == 'Talent Analytics'\r\n        }\">\r\n        <!-- header component -->\r\n        <div id=\"header\" class=\"flex-grow\">\r\n          <ng-content *ngIf=\"!openBurgerMenu\" select=\"[header]\"></ng-content>\r\n        </div>\r\n        <!-- buttons -->\r\n        <div id=\"buttons\" class=\"flex pt-8\">\r\n          <!-- button to toggle the mobile menu -->\r\n          <button\r\n            (click)=\"toggleBurgerMenu()\"\r\n            class=\"tablet:hidden w-[2.2rem] h-[2.2rem] p-2 mr-2\"\r\n            *ngIf=\"!openBurgerMenu\">\r\n            <i class=\"fak fa-menu-closed text-6 text-neutral-500\"></i>\r\n          </button>\r\n          <!-- button toggle drawer -->\r\n          <ng-container *ngIf=\"!openBurgerMenu\">\r\n            <button (click)=\"toggleDrawer()\" class=\"w-[2.2rem] h-[2.2rem] p-2 ml-2\">\r\n              <i class=\"fa-kit fa-talos-cross text-neutral-500 text-1\"></i>\r\n            </button>\r\n          </ng-container>\r\n          <ng-container *ngIf=\"openBurgerMenu\">\r\n            <button (click)=\"toggleBurgerMenu()\" class=\"w-[2.2rem] h-[2.2rem] p-2 ml-2\">\r\n              <i class=\"fa-kit fa-talos-cross text-neutral-500\"></i>\r\n            </button>\r\n          </ng-container>\r\n        </div>\r\n      </div>\r\n    </div>\r\n\r\n    <!-- scrollable content container -->\r\n    <div class=\"flex-grow overflow-y-auto\">\r\n      <ng-container *ngIf=\"!openBurgerMenu; else showContent\">\r\n        <!-- slot to project content -->\r\n        <ng-content select=\"[content]\"></ng-content>\r\n      </ng-container>\r\n      <ng-template #showContent>\r\n        <!-- show mobile menu -->\r\n        <ng-content select=\"[mobile-menu]\"></ng-content>\r\n      </ng-template>\r\n    </div>\r\n  </div>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { openDrawer: [{
                type: Input
            }], openBurgerMenu: [{
                type: Input
            }], navItemClicked: [{
                type: Input
            }], subtitle: [{
                type: Input
            }], openDrawerChange: [{
                type: Output
            }], onResize: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

var Vacancy;
(function (Vacancy) {
    Vacancy["CanSeeSalaries"] = "CanSeeSalaries";
})(Vacancy || (Vacancy = {}));
var Referencing;
(function (Referencing) {
    Referencing["ViewReference"] = "ViewReference";
    Referencing["InviteCandidate"] = "InviteCandidate";
    Referencing["ResendToCandidate"] = "ResendToCandidate";
    Referencing["ResendToReferee"] = "ResendToReferee";
    Referencing["AddReferee"] = "AddReferee";
    Referencing["AddReference"] = "AddReference";
    Referencing["CancelReference"] = "CancelReference";
    Referencing["UncancelReference"] = "UncancelReference";
    Referencing["DeleteReferee"] = "DeleteReferee";
    Referencing["ChangeReferenceType"] = "ChangeReferenceType";
    Referencing["ProceedWithHire"] = "ProceedWithHire";
    Referencing["WithdrawOffer"] = "WithdrawOffer";
})(Referencing || (Referencing = {}));
var CPB;
(function (CPB) {
    CPB["CanCreateExternalSites"] = "CanCreateExternalSites";
    CPB["CanCreateInternalSites"] = "CanCreateInternalSites";
    CPB["CanCreateOnboardingSites"] = "CanCreateOnboardingSites";
    CPB["CanCreateMorePages"] = "CanCreateMorePages";
    CPB["CanUsePremiumFeatures"] = "CanUsePremiumFeatures";
    CPB["CanAccessCareersPageBuilder"] = "CanAccessCareersPageBuilder";
    CPB["CanRemoveTalosLogo"] = "CanRemoveTalosLogo";
})(CPB || (CPB = {}));
var TalosEmployeeCPB;
(function (TalosEmployeeCPB) {
    TalosEmployeeCPB["CanChangeClientCPBDomains"] = "CanChangeClientCPBDomains";
})(TalosEmployeeCPB || (TalosEmployeeCPB = {}));
var TalosEmployeeConfiguration;
(function (TalosEmployeeConfiguration) {
    TalosEmployeeConfiguration["CanConfigureClient"] = "CanConfigureClient";
})(TalosEmployeeConfiguration || (TalosEmployeeConfiguration = {}));
class TalosEmployeeAdmin {
    static { this.CPB = TalosEmployeeCPB; }
    static { this.Configuration = TalosEmployeeConfiguration; }
}
var Onboarding;
(function (Onboarding) {
    Onboarding["CanInviteToOnboardingMicrosite"] = "CanInviteToOnboardingMicrosite";
    Onboarding["CanViewOfferDetails"] = "CanViewOfferDetails";
    Onboarding["CanSaveOfferDetails"] = "CanSaveOfferDetails";
    Onboarding["CanViewOnboardingInvite"] = "CanViewOnboardingInvite";
})(Onboarding || (Onboarding = {}));
var CandidateSearch;
(function (CandidateSearch) {
    CandidateSearch["CanSeeCandidateSearch"] = "CanSeeCandidateSearch";
})(CandidateSearch || (CandidateSearch = {}));
var Credence;
(function (Credence) {
    Credence["CredenceViewer"] = "CredenceViewer";
    Credence["CredenceRequester"] = "CredenceRequester";
    Credence["CredenceDownloader"] = "CredenceDownloader";
    Credence["CredenceCanVerifyIdentity"] = "CredenceCanVerifyIdentity";
    Credence["CredenceCanUploadEmployeeDocuments"] = "CredenceCanUploadEmployeeDocuments";
})(Credence || (Credence = {}));
var TalentStrengths;
(function (TalentStrengths) {
    TalentStrengths["CanViewTalentStrengths"] = "CanViewTalentStrengths";
    TalentStrengths["CanViewTalentStrengthsAccordion"] = "CanViewTalentStrengthsAccordion";
    TalentStrengths["CanRequestTalentStrengths"] = "CanRequestTalentStrengths";
    TalentStrengths["CanDownloadTalentStrengths"] = "CanDownloadTalentStrengths";
})(TalentStrengths || (TalentStrengths = {}));
var VacancySidebar;
(function (VacancySidebar) {
    VacancySidebar["CanDeleteOwnNotes"] = "CanDeleteOwnVacancyNotes";
    VacancySidebar["CanDeleteOthersNotes"] = "CanDeleteOthersVacancyNotes";
    VacancySidebar["CanEditOwnVacancyNotes"] = "CanEditOwnVacancyNotes";
})(VacancySidebar || (VacancySidebar = {}));
var HRConnect;
(function (HRConnect) {
    HRConnect["CanViewHRConnect"] = "CanViewHRConnect";
})(HRConnect || (HRConnect = {}));
var InterviewBooking;
(function (InterviewBooking) {
    InterviewBooking["CanViewIBAccordionHeader"] = "CanViewIBAccordionHeader";
    InterviewBooking["CanViewIBAccordionContents"] = "CanViewIBAccordionContents";
    InterviewBooking["CanUseIBProgressionStagePopUp"] = "CanUseIBProgressionStagePopUp";
    InterviewBooking["CanSeeIBNewInterviewButton"] = "CanSeeIBNewInterviewButton";
    InterviewBooking["CanSeeIBEditOption"] = "CanSeeIBEditOption";
    InterviewBooking["CanViewIBInterviewPlanner"] = "CanViewIBInterviewPlanner";
    InterviewBooking["CanAcceptIBOnBehalfOf"] = "CanAcceptIBOnBehalfOf";
    InterviewBooking["CanViewIBOtherCalendars"] = "CanViewIBOtherCalendars";
    InterviewBooking["CanViewAccordionHeader"] = "CanViewAccordionHeader";
})(InterviewBooking || (InterviewBooking = {}));
var TalentAnalytics;
(function (TalentAnalytics) {
    TalentAnalytics["CanViewTA"] = "CanViewTA";
    TalentAnalytics["CanSearchTA"] = "CanSearchTA";
    TalentAnalytics["CanViewMyTASearches"] = "CanViewMyTASearches";
    TalentAnalytics["CanViewAllTASearches"] = "CanViewAllTASearches";
    TalentAnalytics["CanSeeGlobalTAButton"] = "CanSeeGlobalTAButton";
    TalentAnalytics["HasFullAccessTA"] = "HasFullAccessTA";
})(TalentAnalytics || (TalentAnalytics = {}));
var Configuration;
(function (Configuration) {
    Configuration["CanAccessConfiguration"] = "CanAccessConfiguration";
    Configuration["AdvertManagement"] = "AdvertManagement";
    Configuration["CareersPageManagement"] = "CareersPageManagement";
    Configuration["UserAndHierarchyManagement"] = "UserAndHierarchyManagement";
    Configuration["WorkflowManagement"] = "WorkflowManagement";
    Configuration["SystemConfiguration"] = "SystemConfiguration";
    Configuration["IntegrationsManagement"] = "IntegrationsManagement";
    Configuration["FormManagement"] = "FormManagement";
})(Configuration || (Configuration = {}));
var Intercom;
(function (Intercom) {
    Intercom["IntercomLiveChat"] = "IntercomLiveChat";
})(Intercom || (Intercom = {}));
class AtsPermissions {
    static { this.Vacancy = Vacancy; }
    static { this.Referencing = Referencing; }
    static { this.CPB = CPB; }
    static { this.TalosEmployeeAdmin = TalosEmployeeAdmin; }
    static { this.Onboarding = Onboarding; }
    static { this.CandidateSearch = CandidateSearch; }
    static { this.Credence = Credence; }
    static { this.TalentStrengths = TalentStrengths; }
    static { this.VacancySidebar = VacancySidebar; }
    static { this.HRConnect = HRConnect; }
    static { this.InterviewBooking = InterviewBooking; }
    static { this.TalentAnalytics = TalentAnalytics; }
    static { this.Configuraton = Configuration; }
    static { this.Intercom = Intercom; }
}

var AtsModuleSetting;
(function (AtsModuleSetting) {
    AtsModuleSetting[AtsModuleSetting["MultiSource"] = 1] = "MultiSource";
    AtsModuleSetting[AtsModuleSetting["Repost"] = 2] = "Repost";
    AtsModuleSetting[AtsModuleSetting["FourthEmployeeAPI"] = 3] = "FourthEmployeeAPI";
    AtsModuleSetting[AtsModuleSetting["SendSMS"] = 4] = "SendSMS";
    AtsModuleSetting[AtsModuleSetting["ScoreCardAdmin"] = 5] = "ScoreCardAdmin";
    AtsModuleSetting[AtsModuleSetting["CanForwardCandidates"] = 6] = "CanForwardCandidates";
    AtsModuleSetting[AtsModuleSetting["CanDownloadCandidates"] = 7] = "CanDownloadCandidates";
    AtsModuleSetting[AtsModuleSetting["CanPrintCvs"] = 8] = "CanPrintCvs";
    AtsModuleSetting[AtsModuleSetting["VideoInterview"] = 9] = "VideoInterview";
    AtsModuleSetting[AtsModuleSetting["ProgressionStages"] = 10] = "ProgressionStages";
    AtsModuleSetting[AtsModuleSetting["CommunicationTemplates"] = 11] = "CommunicationTemplates";
    AtsModuleSetting[AtsModuleSetting["Users"] = 12] = "Users";
    AtsModuleSetting[AtsModuleSetting["HierarchyTeams"] = 13] = "HierarchyTeams";
    AtsModuleSetting[AtsModuleSetting["Client"] = 14] = "Client";
    AtsModuleSetting[AtsModuleSetting["ClientSettings"] = 15] = "ClientSettings";
    AtsModuleSetting[AtsModuleSetting["Agencies"] = 16] = "Agencies";
    AtsModuleSetting[AtsModuleSetting["Agents"] = 17] = "Agents";
    AtsModuleSetting[AtsModuleSetting["AdvertTemplates"] = 18] = "AdvertTemplates";
    AtsModuleSetting[AtsModuleSetting["Esign"] = 19] = "Esign";
    AtsModuleSetting[AtsModuleSetting["FourthClientAdministration"] = 20] = "FourthClientAdministration";
    AtsModuleSetting[AtsModuleSetting["Twilio"] = 21] = "Twilio";
    AtsModuleSetting[AtsModuleSetting["Media"] = 22] = "Media";
    AtsModuleSetting[AtsModuleSetting["SMTPSettings"] = 23] = "SMTPSettings";
    AtsModuleSetting[AtsModuleSetting["JobBoardOnEmail"] = 24] = "JobBoardOnEmail";
    AtsModuleSetting[AtsModuleSetting["SpeculativeCVPots"] = 25] = "SpeculativeCVPots";
    AtsModuleSetting[AtsModuleSetting["VideoInterviewArchiving"] = 26] = "VideoInterviewArchiving";
    AtsModuleSetting[AtsModuleSetting["DataCaptureAdmin"] = 27] = "DataCaptureAdmin";
    AtsModuleSetting[AtsModuleSetting["ApplicationFormAdmin"] = 28] = "ApplicationFormAdmin";
    AtsModuleSetting[AtsModuleSetting["VideoScreeningAdmin"] = 29] = "VideoScreeningAdmin";
    AtsModuleSetting[AtsModuleSetting["Forms"] = 30] = "Forms";
    AtsModuleSetting[AtsModuleSetting["AutoAssessmentAdmin"] = 31] = "AutoAssessmentAdmin";
    AtsModuleSetting[AtsModuleSetting["Videos"] = 33] = "Videos";
    AtsModuleSetting[AtsModuleSetting["ATRBuilder"] = 34] = "ATRBuilder";
    AtsModuleSetting[AtsModuleSetting["AuthenticatorApp"] = 37] = "AuthenticatorApp";
    AtsModuleSetting[AtsModuleSetting["MicrosoftAuthentication"] = 39] = "MicrosoftAuthentication";
    AtsModuleSetting[AtsModuleSetting["SMSAuthentication"] = 40] = "SMSAuthentication";
    AtsModuleSetting[AtsModuleSetting["PhoneAuthentication"] = 41] = "PhoneAuthentication";
    AtsModuleSetting[AtsModuleSetting["RTWDigital"] = 42] = "RTWDigital";
    AtsModuleSetting[AtsModuleSetting["Internal"] = 44] = "Internal";
    AtsModuleSetting[AtsModuleSetting["External"] = 45] = "External";
    AtsModuleSetting[AtsModuleSetting["Onboarding"] = 46] = "Onboarding";
    AtsModuleSetting[AtsModuleSetting["PremiumFeatures"] = 47] = "PremiumFeatures";
    AtsModuleSetting[AtsModuleSetting["CareersPageBuilder"] = 48] = "CareersPageBuilder";
    AtsModuleSetting[AtsModuleSetting["RTWInPerson"] = 49] = "RTWInPerson";
    AtsModuleSetting[AtsModuleSetting["Personal"] = 50] = "Personal";
    AtsModuleSetting[AtsModuleSetting["Professional"] = 51] = "Professional";
    AtsModuleSetting[AtsModuleSetting["CareersSites"] = 52] = "CareersSites";
    AtsModuleSetting[AtsModuleSetting["Match"] = 53] = "Match";
    AtsModuleSetting[AtsModuleSetting["Insights"] = 54] = "Insights";
    AtsModuleSetting[AtsModuleSetting["FourthHierarchyAPI"] = 55] = "FourthHierarchyAPI";
    AtsModuleSetting[AtsModuleSetting["FourthImporter"] = 56] = "FourthImporter";
    AtsModuleSetting[AtsModuleSetting["RequestCheck"] = 57] = "RequestCheck";
    AtsModuleSetting[AtsModuleSetting["IdentityVerification"] = 58] = "IdentityVerification";
    AtsModuleSetting[AtsModuleSetting["EmployeeDocuments"] = 59] = "EmployeeDocuments";
    AtsModuleSetting[AtsModuleSetting["CandidateAssessments"] = 60] = "CandidateAssessments";
    AtsModuleSetting[AtsModuleSetting["TalosMarketplace"] = 61] = "TalosMarketplace";
    AtsModuleSetting[AtsModuleSetting["InterviewScheduler"] = 62] = "InterviewScheduler";
    AtsModuleSetting[AtsModuleSetting["FourthOnboardingAPI"] = 63] = "FourthOnboardingAPI";
    AtsModuleSetting[AtsModuleSetting["Intercom"] = 64] = "Intercom";
    AtsModuleSetting[AtsModuleSetting["FullAccess"] = 65] = "FullAccess";
    AtsModuleSetting[AtsModuleSetting["FullAnalytics"] = 66] = "FullAnalytics";
})(AtsModuleSetting || (AtsModuleSetting = {}));

class TalosDrawerMenuComponent {
    constructor(cdr) {
        this.cdr = cdr;
        this.AtsModuleSetting = AtsModuleSetting;
        this.fullAccess = false;
        //menu items
        this.menuItems = [];
        //permissions
        this.modulesjson = [];
        this.permissionsjson = [];
        this.TalentAnalyticsPermissions = AtsPermissions.TalentAnalytics;
        // Output property for emitting selected item or page
        this.itemSelected = new EventEmitter();
    }
    ngOnInit() { }
    ngOnChanges(changes) {
        if (changes.menuItems &&
            changes.menuItems.currentValue !== changes.menuItems.previousValue) {
            this.menuItems = changes.menuItems.currentValue;
        }
        if (changes.permissionsjson &&
            changes.permissionsjson.currentValue !== changes.permissionsjson.previousValue) {
            this.permissionsjson = changes.permissionsjson.currentValue;
            this.fullAccess = this.permissionsjson.includes(this.TalentAnalyticsPermissions.HasFullAccessTA);
            this.cdr.detectChanges();
        }
    }
    toggleSubMenu(item) {
        if (item.subMenuOpen == true) {
            item.subMenuOpen = false;
        }
        else {
            item.subMenuOpen = true;
        }
    }
    makeActive(item, submenu) {
        // Deactivate all menu items and their submenus
        for (let menuItem of this.menuItems) {
            menuItem.active = false;
            if (menuItem.subMenu) {
                for (let subItem of menuItem.subMenu) {
                    subItem.active = false;
                }
            }
        }
        // Activate the selected item
        item.active = true;
        // Emit the selected item or submenu to the parent component
        if (submenu) {
            submenu.active = true;
            this.itemSelected.emit(submenu); // Emit the submenu
        }
        else {
            this.itemSelected.emit(item); // Emit the main item
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosDrawerMenuComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "19.0.6", type: TalosDrawerMenuComponent, isStandalone: false, selector: "talos-drawer-menu", inputs: { menuItems: "menuItems", modulesjson: "modulesjson", permissionsjson: "permissionsjson" }, outputs: { itemSelected: "itemSelected" }, usesOnChanges: true, ngImport: i0, template: "<div id=\"side-menu\" class=\"flex flex-col items-start px-8 self-stretch text-[14px]\">\r\n  <ng-container *ngFor=\"let item of menuItems\">\r\n    <!-- main page -->\r\n    <div\r\n      id=\"tab-menu\"\r\n      (click)=\"!item.subMenu && makeActive(item, null)\"\r\n      class=\"flex items-center gap-2 self-stretch cursor-pointer w-[150px]\">\r\n      <div\r\n        id=\"active-marker\"\r\n        [ngClass]=\"{\r\n          visible: item.active,\r\n          invisible: !item.active\r\n        }\"\r\n        class=\"bg-secondary-750 rounded-sm h-18 w-2\"></div>\r\n\r\n      <div\r\n        id=\"base-tab\"\r\n        class=\"flex h-44 items-center gap-2 justify-between\"\r\n        style=\"width: 100%\">\r\n        <span\r\n          id=\"text\"\r\n          [ngClass]=\"{\r\n            'text-secondary-750': item.active,\r\n            'text-neutral-600': !item.active,\r\n            'hover:text-secondary-750': true\r\n          }\"\r\n          class=\"text-[14px] font-normal leading-5\">\r\n          {{ item.name }}</span\r\n        >\r\n        <!-- <i\r\n          *ngIf=\"item.subMenu\"\r\n          class=\"fa-kit fa-talos-chevron text-neutral-500 text-[0.6rem] p-[0.8rem] transition-transform\"\r\n          [ngClass]=\"{\r\n            'rotate-0': !item.subMenuOpen,\r\n            'rotate-180': item.subMenuOpen\r\n          }\"></i> -->\r\n      </div>\r\n    </div>\r\n\r\n    <!-- child pages -->\r\n    <ng-container *ngIf=\"item.subMenuOpen\">\r\n      <div\r\n        *ngFor=\"let subItem of item.subMenu\"\r\n        id=\"tab-menu\"\r\n        (click)=\"makeActive(item, subItem)\"\r\n        class=\"flex flex-1 items-center gap-2 self-stretch cursor-pointer\"\r\n        style=\"width: 150px\">\r\n        <div\r\n          id=\"active-marker\"\r\n          class=\"invisible bg-secondary-750 rounded-sm h-18 w-2\"></div>\r\n\r\n        <div\r\n          id=\"base-tab\"\r\n          class=\"flex h-44 items-center gap-2 justify-between\"\r\n          style=\"width: 100%\">\r\n          <!-- Submenu text and icon -->\r\n          <span\r\n            id=\"text\"\r\n            [ngClass]=\"{\r\n              'text-dark': subItem.active,\r\n              'font-medium': subItem.active,\r\n              'text-neutral-600': !subItem.active,\r\n              'font-normal': !subItem.active,\r\n              'hover:text-dark': true\r\n            }\"\r\n            class=\"text-[14px] leading-5 flex items-center\">\r\n            <!-- Lock icon or dot depending on subItem.locked -->\r\n            <i\r\n              *ngIf=\"\r\n                !fullAccess &&\r\n                  subItem.name !== 'Overview' &&\r\n                  subItem.name !== 'Hiring History';\r\n                else dotIcon\r\n              \"\r\n              class=\"fa-kit fa-talos-lock text-neutral-600 text-4 pr-2\"\r\n              [ngClass]=\"{\r\n                'text-dark': subItem.active,\r\n                'hover:text-dark': true\r\n              }\"></i>\r\n\r\n            <ng-template #dotIcon>\r\n              <span class=\"before:content-['\u2022'] before:mr-2\"></span>\r\n            </ng-template>\r\n\r\n            {{ subItem.name }}\r\n          </span>\r\n        </div>\r\n      </div>\r\n    </ng-container>\r\n  </ng-container>\r\n</div>\r\n", styles: [""], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: TalosDrawerMenuComponent, decorators: [{
            type: Component,
            args: [{ selector: 'talos-drawer-menu', changeDetection: ChangeDetectionStrategy.OnPush, standalone: false, template: "<div id=\"side-menu\" class=\"flex flex-col items-start px-8 self-stretch text-[14px]\">\r\n  <ng-container *ngFor=\"let item of menuItems\">\r\n    <!-- main page -->\r\n    <div\r\n      id=\"tab-menu\"\r\n      (click)=\"!item.subMenu && makeActive(item, null)\"\r\n      class=\"flex items-center gap-2 self-stretch cursor-pointer w-[150px]\">\r\n      <div\r\n        id=\"active-marker\"\r\n        [ngClass]=\"{\r\n          visible: item.active,\r\n          invisible: !item.active\r\n        }\"\r\n        class=\"bg-secondary-750 rounded-sm h-18 w-2\"></div>\r\n\r\n      <div\r\n        id=\"base-tab\"\r\n        class=\"flex h-44 items-center gap-2 justify-between\"\r\n        style=\"width: 100%\">\r\n        <span\r\n          id=\"text\"\r\n          [ngClass]=\"{\r\n            'text-secondary-750': item.active,\r\n            'text-neutral-600': !item.active,\r\n            'hover:text-secondary-750': true\r\n          }\"\r\n          class=\"text-[14px] font-normal leading-5\">\r\n          {{ item.name }}</span\r\n        >\r\n        <!-- <i\r\n          *ngIf=\"item.subMenu\"\r\n          class=\"fa-kit fa-talos-chevron text-neutral-500 text-[0.6rem] p-[0.8rem] transition-transform\"\r\n          [ngClass]=\"{\r\n            'rotate-0': !item.subMenuOpen,\r\n            'rotate-180': item.subMenuOpen\r\n          }\"></i> -->\r\n      </div>\r\n    </div>\r\n\r\n    <!-- child pages -->\r\n    <ng-container *ngIf=\"item.subMenuOpen\">\r\n      <div\r\n        *ngFor=\"let subItem of item.subMenu\"\r\n        id=\"tab-menu\"\r\n        (click)=\"makeActive(item, subItem)\"\r\n        class=\"flex flex-1 items-center gap-2 self-stretch cursor-pointer\"\r\n        style=\"width: 150px\">\r\n        <div\r\n          id=\"active-marker\"\r\n          class=\"invisible bg-secondary-750 rounded-sm h-18 w-2\"></div>\r\n\r\n        <div\r\n          id=\"base-tab\"\r\n          class=\"flex h-44 items-center gap-2 justify-between\"\r\n          style=\"width: 100%\">\r\n          <!-- Submenu text and icon -->\r\n          <span\r\n            id=\"text\"\r\n            [ngClass]=\"{\r\n              'text-dark': subItem.active,\r\n              'font-medium': subItem.active,\r\n              'text-neutral-600': !subItem.active,\r\n              'font-normal': !subItem.active,\r\n              'hover:text-dark': true\r\n            }\"\r\n            class=\"text-[14px] leading-5 flex items-center\">\r\n            <!-- Lock icon or dot depending on subItem.locked -->\r\n            <i\r\n              *ngIf=\"\r\n                !fullAccess &&\r\n                  subItem.name !== 'Overview' &&\r\n                  subItem.name !== 'Hiring History';\r\n                else dotIcon\r\n              \"\r\n              class=\"fa-kit fa-talos-lock text-neutral-600 text-4 pr-2\"\r\n              [ngClass]=\"{\r\n                'text-dark': subItem.active,\r\n                'hover:text-dark': true\r\n              }\"></i>\r\n\r\n            <ng-template #dotIcon>\r\n              <span class=\"before:content-['\u2022'] before:mr-2\"></span>\r\n            </ng-template>\r\n\r\n            {{ subItem.name }}\r\n          </span>\r\n        </div>\r\n      </div>\r\n    </ng-container>\r\n  </ng-container>\r\n</div>\r\n" }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { menuItems: [{
                type: Input
            }], modulesjson: [{
                type: Input
            }], permissionsjson: [{
                type: Input
            }], itemSelected: [{
                type: Output
            }] } });

class OutsideClickedDirective {
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
        this.outsideClicked = new EventEmitter();
        this.renderer.listen('window', 'click', (e) => {
            if (!e.composedPath().includes(this.el.nativeElement)) {
                this.outsideClicked.emit();
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: OutsideClickedDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.0.6", type: OutsideClickedDirective, isStandalone: false, selector: "[outsideClicked]", outputs: { outsideClicked: "outsideClicked" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: OutsideClickedDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[outsideClicked]',
                    standalone: false
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { outsideClicked: [{
                type: Output
            }] } });

// We add to the exports and providers here so that we have a server-side version of the module which doesn't
//  initialise web components (core.module initialises web components)
const exports = [
    AsPipe,
    SanitizeHtmlPipe,
    BackgroundImageUrlPipe,
    ContentCardButtonComponent,
    ModalLargeComponent,
    TalosCarouselComponent,
    ModalApplicantsElementsComponent,
    TalosDropdownMenuComponent,
    TalosExpandedButtonComponent,
    TalosInputCheckboxComponent,
    TalosInputEmailComponent,
    TalosInputSearchComponent,
    TalosInputTextComponent,
    TalosInputToggleComponent,
    TalosSelectComponent,
    TalosSearchSelectComponent,
    TalosRadioComponent,
    TalosTagComponent,
    TalosInfoMessageComponent,
    SkipToContentButtonComponent,
    TalosDrawerComponent,
    TalosDrawerMenuComponent,
    PaginationComponent,
    PaginatePipe,
    SliderComponent,
    ProgressBarComponent,
    GoogleAutocompleteComponent,
    CustomTooltipComponent,
    TalosUserDetailTooltipComponent,
    TalosLoadingComponent,
    TalosNotificationTagComponent,
    NotificationBannerComponent,
    // Elements
    SingleImageUploadElement,
    ToastElement,
    // Pipes
    IfEmptyThenPipe,
    IsErrorStatePipe,
    ErrorStateTextPipe,
    IsEmailErrorStatePipe,
    HasPermissionPipe,
    HasPermissionsPipe,
    HasEitherPermissionsPipe,
    ShouldTruncateNamePipe,
    HighlightSearchPipe,
    IsObjectEmptyPipe,
    GetTagTextColourPipe,
    GetTagBackgroundPipe,
    HasFeatureFlagPipe,
    HasFeatureFlagsPipe,
    HasModulesPipe,
    HasModulePipe,
    HasModuleSettingPipe,
    HasModuleSettingsPipe,
    // Directives
    ClickOutsideTalosSelectDirective,
    TalosTouchEventsListenerDirective,
    TalosHidingTooltipDirective,
    OutsideClickedDirective,
];
const providers = [
    IsErrorStatePipe,
    IsEmailErrorStatePipe,
    FileReaderService,
    GuardDeactivateService,
];
class CoreServerSideModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: CoreServerSideModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: CoreServerSideModule, declarations: [AsPipe,
            SanitizeHtmlPipe,
            BackgroundImageUrlPipe,
            ContentCardButtonComponent,
            ModalLargeComponent,
            TalosCarouselComponent,
            ModalApplicantsElementsComponent,
            TalosDropdownMenuComponent,
            TalosExpandedButtonComponent,
            TalosInputCheckboxComponent,
            TalosInputEmailComponent,
            TalosInputSearchComponent,
            TalosInputTextComponent,
            ShouldShowInputErrorsPipe,
            TalosInputToggleComponent,
            TalosSelectComponent,
            TalosSearchSelectComponent,
            TalosRadioComponent,
            TalosTagComponent,
            ShouldShowLeftIconPipe,
            ShouldShowRightIconPipe,
            TalosInfoMessageComponent,
            SkipToContentButtonComponent,
            TalosDrawerComponent,
            TalosDrawerMenuComponent,
            PaginationComponent,
            IsPreviousButtonDisabledPipe,
            IsNextButtonDisabledPipe,
            PaginatePipe,
            SliderComponent,
            ProgressBarComponent,
            GoogleAutocompleteComponent,
            CustomTooltipComponent,
            TalosUserDetailTooltipComponent,
            TalosLoadingComponent,
            TalosNotificationTagComponent,
            NotificationBannerComponent,
            NotificationBannerBackgroundPipe,
            NotificationBannerTextPipe,
            NotificationBannerIconPipe,
            NotificationBannerIconColourPipe,
            NotificationBannerBorderPipe,
            // Elements
            SingleImageUploadElement,
            ToastElement,
            // Pipes
            IsErrorStatePipe,
            ErrorStateTextPipe,
            IsEmailErrorStatePipe,
            IfEmptyThenPipe,
            HasPermissionPipe,
            HasPermissionsPipe,
            HasEitherPermissionsPipe,
            ShouldTruncateNamePipe,
            ExpandedButtonBorderStylePipe,
            HasFeatureFlagPipe,
            HasFeatureFlagsPipe,
            HighlightSearchPipe,
            IsObjectEmptyPipe,
            GetTagTextColourPipe,
            GetTagBackgroundPipe,
            HasModulesPipe,
            HasModulePipe,
            HasModuleSettingPipe,
            HasModuleSettingsPipe,
            // Directives
            ClickOutsideTalosSelectDirective,
            TalosTouchEventsListenerDirective,
            TalosHidingTooltipDirective,
            OutsideClickedDirective], imports: [CommonModule, MatSliderModule, RouterModule], exports: [AsPipe,
            SanitizeHtmlPipe,
            BackgroundImageUrlPipe,
            ContentCardButtonComponent,
            ModalLargeComponent,
            TalosCarouselComponent,
            ModalApplicantsElementsComponent,
            TalosDropdownMenuComponent,
            TalosExpandedButtonComponent,
            TalosInputCheckboxComponent,
            TalosInputEmailComponent,
            TalosInputSearchComponent,
            TalosInputTextComponent,
            TalosInputToggleComponent,
            TalosSelectComponent,
            TalosSearchSelectComponent,
            TalosRadioComponent,
            TalosTagComponent,
            TalosInfoMessageComponent,
            SkipToContentButtonComponent,
            TalosDrawerComponent,
            TalosDrawerMenuComponent,
            PaginationComponent,
            PaginatePipe,
            SliderComponent,
            ProgressBarComponent,
            GoogleAutocompleteComponent,
            CustomTooltipComponent,
            TalosUserDetailTooltipComponent,
            TalosLoadingComponent,
            TalosNotificationTagComponent,
            NotificationBannerComponent,
            // Elements
            SingleImageUploadElement,
            ToastElement,
            // Pipes
            IfEmptyThenPipe,
            IsErrorStatePipe,
            ErrorStateTextPipe,
            IsEmailErrorStatePipe,
            HasPermissionPipe,
            HasPermissionsPipe,
            HasEitherPermissionsPipe,
            ShouldTruncateNamePipe,
            HighlightSearchPipe,
            IsObjectEmptyPipe,
            GetTagTextColourPipe,
            GetTagBackgroundPipe,
            HasFeatureFlagPipe,
            HasFeatureFlagsPipe,
            HasModulesPipe,
            HasModulePipe,
            HasModuleSettingPipe,
            HasModuleSettingsPipe,
            // Directives
            ClickOutsideTalosSelectDirective,
            TalosTouchEventsListenerDirective,
            TalosHidingTooltipDirective,
            OutsideClickedDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: CoreServerSideModule, providers: [providers], imports: [CommonModule, MatSliderModule, RouterModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: CoreServerSideModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        AsPipe,
                        SanitizeHtmlPipe,
                        BackgroundImageUrlPipe,
                        ContentCardButtonComponent,
                        ModalLargeComponent,
                        TalosCarouselComponent,
                        ModalApplicantsElementsComponent,
                        TalosDropdownMenuComponent,
                        TalosExpandedButtonComponent,
                        TalosInputCheckboxComponent,
                        TalosInputEmailComponent,
                        TalosInputSearchComponent,
                        TalosInputTextComponent,
                        ShouldShowInputErrorsPipe,
                        TalosInputToggleComponent,
                        TalosSelectComponent,
                        TalosSearchSelectComponent,
                        TalosRadioComponent,
                        TalosTagComponent,
                        ShouldShowLeftIconPipe,
                        ShouldShowRightIconPipe,
                        TalosInfoMessageComponent,
                        SkipToContentButtonComponent,
                        TalosDrawerComponent,
                        TalosDrawerMenuComponent,
                        PaginationComponent,
                        IsPreviousButtonDisabledPipe,
                        IsNextButtonDisabledPipe,
                        PaginatePipe,
                        SliderComponent,
                        ProgressBarComponent,
                        GoogleAutocompleteComponent,
                        CustomTooltipComponent,
                        TalosUserDetailTooltipComponent,
                        TalosLoadingComponent,
                        TalosNotificationTagComponent,
                        NotificationBannerComponent,
                        NotificationBannerBackgroundPipe,
                        NotificationBannerTextPipe,
                        NotificationBannerIconPipe,
                        NotificationBannerIconColourPipe,
                        NotificationBannerBorderPipe,
                        // Elements
                        SingleImageUploadElement,
                        ToastElement,
                        // Pipes
                        IsErrorStatePipe,
                        ErrorStateTextPipe,
                        IsEmailErrorStatePipe,
                        IfEmptyThenPipe,
                        HasPermissionPipe,
                        HasPermissionsPipe,
                        HasEitherPermissionsPipe,
                        ShouldTruncateNamePipe,
                        ExpandedButtonBorderStylePipe,
                        HasFeatureFlagPipe,
                        HasFeatureFlagsPipe,
                        HighlightSearchPipe,
                        IsObjectEmptyPipe,
                        GetTagTextColourPipe,
                        GetTagBackgroundPipe,
                        HasModulesPipe,
                        HasModulePipe,
                        HasModuleSettingPipe,
                        HasModuleSettingsPipe,
                        // Directives
                        ClickOutsideTalosSelectDirective,
                        TalosTouchEventsListenerDirective,
                        TalosHidingTooltipDirective,
                        OutsideClickedDirective,
                    ],
                    exports: exports,
                    imports: [CommonModule, MatSliderModule, RouterModule],
                    providers: [providers]
                }]
        }] });

class CoreModule {
    constructor(injector) {
        this.injector = injector;
        if (!customElements.get('single-image-upload-element')) {
            const singleImageUploadElementInstance = createCustomElement(SingleImageUploadElement, {
                injector,
            });
            customElements.define('single-image-upload-element', singleImageUploadElementInstance);
        }
        if (!customElements.get('toast-element')) {
            const toastElementInstance = createCustomElement(ToastElement, {
                injector,
            });
            customElements.define('toast-element', toastElementInstance);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: CoreModule, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.0.6", ngImport: i0, type: CoreModule, bootstrap: [SingleImageUploadElement, ToastElement], imports: [CoreServerSideModule], exports: [AsPipe, SanitizeHtmlPipe, BackgroundImageUrlPipe, ContentCardButtonComponent, ModalLargeComponent, TalosCarouselComponent, ModalApplicantsElementsComponent, TalosDropdownMenuComponent, TalosExpandedButtonComponent, TalosInputCheckboxComponent, TalosInputEmailComponent, TalosInputSearchComponent, TalosInputTextComponent, TalosInputToggleComponent, TalosSelectComponent, TalosSearchSelectComponent, TalosRadioComponent, TalosTagComponent, TalosInfoMessageComponent, SkipToContentButtonComponent, TalosDrawerComponent, TalosDrawerMenuComponent, PaginationComponent, PaginatePipe, SliderComponent, ProgressBarComponent, GoogleAutocompleteComponent, CustomTooltipComponent, TalosUserDetailTooltipComponent, TalosLoadingComponent, TalosNotificationTagComponent, NotificationBannerComponent, SingleImageUploadElement, ToastElement, IfEmptyThenPipe, IsErrorStatePipe, ErrorStateTextPipe, IsEmailErrorStatePipe, HasPermissionPipe, HasPermissionsPipe, HasEitherPermissionsPipe, ShouldTruncateNamePipe, HighlightSearchPipe, IsObjectEmptyPipe, GetTagTextColourPipe, GetTagBackgroundPipe, HasFeatureFlagPipe, HasFeatureFlagsPipe, HasModulesPipe, HasModulePipe, HasModuleSettingPipe, HasModuleSettingsPipe, ClickOutsideTalosSelectDirective, TalosTouchEventsListenerDirective, TalosHidingTooltipDirective, OutsideClickedDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: CoreModule, providers: providers, imports: [CoreServerSideModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.6", ngImport: i0, type: CoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CoreServerSideModule],
                    exports: exports,
                    providers: providers,
                    bootstrap: [SingleImageUploadElement, ToastElement],
                }]
        }], ctorParameters: () => [{ type: i0.Injector }] });

var FeatureFlag;
(function (FeatureFlag) {
    FeatureFlag[FeatureFlag["SystemBranding"] = 1] = "SystemBranding";
    FeatureFlag[FeatureFlag["CandidateSearch"] = 2] = "CandidateSearch";
    FeatureFlag[FeatureFlag["Vacancies"] = 3] = "Vacancies";
    FeatureFlag[FeatureFlag["Notifications"] = 4] = "Notifications";
    FeatureFlag[FeatureFlag["InterviewScheduler"] = 5] = "InterviewScheduler";
    FeatureFlag[FeatureFlag["NotificationEmails"] = 6] = "NotificationEmails";
})(FeatureFlag || (FeatureFlag = {}));

const AtsModule = {
    Administration: 'Administration',
    AdonisHR: 'AdonisHR',
    AgencyPortal: 'AgencyPortal',
    AtlanticData: 'AtlanticData',
    ATR: 'ATR',
    AutoAssessments: 'AutoAssessments',
    CandidateActions: 'CandidateActions',
    CareersPageBuilder: 'CareersPageBuilder',
    Cascade: 'Cascade',
    CezanneHR: 'CezanneHR',
    ClientAdministration: 'ClientAdministration',
    Credence: 'Credence',
    Dashboard: 'Dashboard',
    Docusign: 'Docusign',
    Forms: 'Forms',
    Fourth: 'Fourth',
    IFS: 'IFS',
    Interview: 'Interview',
    InterviewScheduler: 'InterviewScheduler',
    Kornferry: 'Kornferry',
    Onboarding: 'Onboarding',
    PeopleHR: 'PeopleHR',
    Posting: 'Posting',
    PostingInFuture: 'PostingInFuture',
    ProcureWizard: 'ProcureWizard',
    Referencing: 'Referencing',
    Reporting: 'Reporting',
    ReportingOLD: 'ReportingOLD',
    Requisitions: 'Requisitions',
    SearchPlus: 'SearchPlus',
    SignOff: 'SignOff',
    SMI: 'SMI',
    SMS: 'SMS',
    SocialNetworkJobBoards: 'SocialNetworkJobBoards',
    SourceTracking: 'SourceTracking',
    SpeculativeCVs: 'SpeculativeCVs',
    SSO: 'SSO',
    Talent: 'Talent',
    TalentStrengths: 'TalentStrengths',
    TalosApply: 'TalosApply',
    TalosMarketplace: 'TalosMarketplace',
    TrustId: 'TrustId',
    TwoFactorAuthentication: 'TwoFactorAuthentication',
    VideoScreening: 'VideoScreening',
    Worklife: 'Worklife',
    ZendeskChat: 'ZendeskChat',
    TalentAnalytics: 'TalentAnalytics',
};

/*
 * Public API Surface of core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { animations as Animations, AsPipe, AtsModule, AtsModuleSetting, BackgroundImageUrlPipe, ClickOutsideTalosSelectDirective, ContentCardButtonComponent, CoreModule, CoreServerSideModule, CustomTooltipComponent, ErrorStateTextPipe, ExpandedButtonBorderStylePipe, FeatureFlag, FileReaderService, GetTagBackgroundPipe, GetTagTextColourPipe, GoogleAutocompleteComponent, HasEitherPermissionsPipe, HasFeatureFlagPipe, HasFeatureFlagsPipe, HasModulePipe, HasModuleSettingPipe, HasModuleSettingsPipe, HasModulesPipe, HasPermissionPipe, HasPermissionsPipe, HighlightSearchPipe, IfEmptyThenPipe, IsEmailErrorStatePipe, IsErrorStatePipe, IsNextButtonDisabledPipe, IsObjectEmptyPipe, IsPreviousButtonDisabledPipe, ModalApplicantsElementsComponent, ModalLargeComponent, NotificationBannerComponent, OutsideClickedDirective, PaginatePipe, PaginationComponent, ProgressBarComponent, SanitizeHtmlPipe, ShouldTruncateNamePipe, SingleImageUploadElement, SkipToContentButtonComponent, SliderComponent, TalosCarouselComponent, TalosDrawerComponent, TalosDrawerMenuComponent, TalosDropdownMenuComponent, TalosExpandedButtonComponent, TalosHidingTooltipDirective, TalosInfoMessageComponent, TalosInputCheckboxComponent, TalosInputEmailComponent, TalosInputSearchComponent, TalosInputTextComponent, TalosInputToggleComponent, TalosLoadingComponent, TalosNotificationTagComponent, TalosRadioComponent, TalosSearchSelectComponent, TalosSelectComponent, TalosTagComponent, TalosTouchEventsListenerDirective, TalosUserDetailTooltipComponent, ToastElement, ToastEvents, ToastTypes, exports, providers };
